import { CheckOutlined, CloseOutlined, DownloadOutlined, DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, LeftOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ReloadOutlined, RightOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal, Popover, Select, Slider, Spin, Switch, Table, theme, Result, Tag } from "antd";
import {
  doc,
  getDoc,
  Timestamp,
  updateDoc,
  deleteDoc,
  collection,
  setDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore"; import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { SketchPicker } from "react-color";
import "./App.css";
import { auth, db } from "./FirebaseConfig";
import { createImageJZip } from "./utils/exportImageJ";
import { fetchEditLogs, buildEditLogsCsv } from "./utils/exportEditLogs";
import { usePosts } from "./usePosts";
import { logBoundingBoxAction } from "./utils/logging";
import { useTranslation } from "react-i18next";
import StomataLoader from "./StomataLoader";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import FloatingPhotoComment from "./components/FloatingPhotoComment";
import CanvasHelpPanel from "./components/CanvasHelpPanel";
import CanvasPointerToolBadge from "./components/CanvasPointerToolBadge";

const { confirm } = Modal;

// No automatic log feed. Export reads logs only after an explicit button click.
const markLogsDirty = () => {};
const refetchLogs = async () => {};

const POLYGON_CANVAS_SIZE = 960;
const POLYGON_LOGICAL_SIZE = 160;
const POLYGON_SCALE_FACTOR = POLYGON_CANVAS_SIZE / POLYGON_LOGICAL_SIZE;
const DEFAULT_AUTO_CONTOUR_POINTS = 24;
const MIN_AUTO_CONTOUR_POINTS = 24;
const MAX_AUTO_CONTOUR_POINTS = 80;
const AUTO_CONTOUR_EDGE_MARGIN_PX = 6;
const AUTO_CONTOUR_MAX_COMPONENT_RATIO = 0.22;
const AUTO_CONTOUR_MAX_RADIUS_RATIO = 0.38;
const AUTO_CONTOUR_CENTER_REFINE_RADIUS_RATIO = 0.18;
const AUTO_CONTOUR_RAY_STEP_PX = 2;
const AUTO_CONTOUR_MIN_RAY_RADIUS_PX = 12;
const AUTO_CONTOUR_MAX_RADIUS_MULTIPLIER = 1.75;
const AUTO_CONTOUR_ELONGATED_RAY_ASPECT_THRESHOLD = 2.2;
const AUTO_CONTOUR_ELONGATED_RAY_RADIUS_MULTIPLIER = 4.25;
const AUTO_CONTOUR_ELONGATED_RAY_MIN_VALID_RATIO = 0.28;
const POLYGON_IMAGE_LOAD_TIMEOUT_MS = 30000;
const BINARY_MASK_OVERLAY_ALPHA = 96;
const POLYGON_POINT_DELETE_HIT_RADIUS = 4.5;
const POLYGON_POINT_DRAG_HIT_RADIUS = 1.5;
const POLYGON_EDGE_INSERT_HIT_RADIUS = 1.5;
const POLYGON_EDGE_INSERT_CLICK_RADIUS = 10;
const POLYGON_SMOOTHING_ALPHA = 0.35;
const MIN_POLYGON_IMAGE_ZOOM = 1;
const MAX_POLYGON_IMAGE_ZOOM = 8;
const POLYGON_IMAGE_ZOOM_STEP = 0.1;

// regionprops discrete moments on integer pixel centers; boundary included.
// Input: original-resolution pixel coordinates in an integer-origin crop.
function measurePoreRegion(points) {
  if (!Array.isArray(points) || points.length < 3 ||
      points.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
  const eps = 1e-7;
  const xmin = Math.ceil(Math.min(...points.map(p => p.x)) - eps);
  const xmax = Math.floor(Math.max(...points.map(p => p.x)) + eps);
  const ymin = Math.ceil(Math.min(...points.map(p => p.y)) - eps);
  const ymax = Math.floor(Math.max(...points.map(p => p.y)) + eps);
  if ((xmax-xmin+1)*(ymax-ymin+1) > 10000000) return null;
  let n = 0, cx = 0, cy = 0, xx = 0, yy = 0, xy = 0;
  for (let y = ymin; y <= ymax; y++) {
    const crossings = [], spans = [];
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i+1)%points.length];
      if (Math.abs(a.y-b.y) < eps) {
        if (Math.abs(y-a.y) < eps) spans.push([Math.min(a.x,b.x), Math.max(a.x,b.x)]);
      } else {
        const x = a.x + (y-a.y)*(b.x-a.x)/(b.y-a.y);
        if (y >= Math.min(a.y,b.y)-eps && y <= Math.max(a.y,b.y)+eps) spans.push([x,x]);
        if ((a.y > y) !== (b.y > y)) crossings.push(x);
      }
    }
    crossings.sort((a,b) => a-b);
    for (let i = 0; i+1 < crossings.length; i+=2) spans.push([crossings[i],crossings[i+1]]);
    const pixels = new Set();
    for (const [lo,hi] of spans) {
      for (let x=Math.max(xmin,Math.ceil(lo-eps)); x<=Math.min(xmax,Math.floor(hi+eps)); x++) pixels.add(x);
    }
    for (const x of [...pixels].sort((a,b)=>a-b)) {
      n++;
      const dx=x-cx, dy=y-cy;
      cx+=dx/n; cy+=dy/n;
      xx+=dx*(x-cx); yy+=dy*(y-cy); xy+=dx*(y-cy);
    }
  }
  if (!n) return null;
  const cxx=xx/n, cyy=yy/n, cxy=xy/n;
  const delta=Math.hypot(cxx-cyy,2*cxy), trace=cxx+cyy;
  return {
    center: {x:cx,y:cy},
    majorAxis:4*Math.sqrt(Math.max(0,(trace+delta)/2)),
    minorAxis:4*Math.sqrt(Math.max(0,(trace-delta)/2)),
    angle:delta<1e-12?0:0.5*Math.atan2(2*cxy,cxx-cyy),
    area:n,
  };
}

function toFiniteNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function calcPhotoSummaryFromBoxes(boxes) {
  const safeBoxes = Array.isArray(boxes) ? boxes : [];
  const openBoxes = safeBoxes.filter((box) => String(box?.title || "").toLowerCase() === "open");

  const apertureValues = openBoxes
    .map((box) => toFiniteNumber(box?.aperture, null))
    .filter((value) => value !== null && Number.isFinite(value));

  const aperture =
    apertureValues.length > 0
      ? apertureValues.reduce((sum, value) => sum + value, 0) / apertureValues.length
      : 0;

  return {
    aperture: Number(aperture),
    size: safeBoxes.length,
  };
}

// Build a persisted box action without changing the original array or contours.
function getPolygonBoxAction(boxes, key, action) {
  const before = boxes.find(box => box.key === key);
  if (!before || !["delete", "close"].includes(action)) return null;
  const after = action === "close" ? {
    ...before, title: "close", aperture: 0, area: 0, long: 0, short: 0,
    status: "DetectClose",
    idClass: before.title !== "close" ? (before.idClass ?? 0) + 1 : before.idClass,
  } : null;
  const updatedBoxes = action === "delete" ? boxes.filter(box => box.key !== key)
    : boxes.map(box => box.key === key ? after : box);
  return { before, after, updatedBoxes };
}

function serializeLinesForFirestore(lines) {
  return (Array.isArray(lines) ? lines : []).map((line) => ({
    id: line?.id != null ? line.id.toString() : "",
    start: {
      x: toFiniteNumber(line?.start?.x, 0),
      y: toFiniteNumber(line?.start?.y, 0),
    },
    end: {
      x: toFiniteNumber(line?.end?.x, 0),
      y: toFiniteNumber(line?.end?.y, 0),
    },
    distance: line?.distance ?? "0.00",
    isScale: line?.isScale ?? false,
  }));
}

const MICRON_PER_PIXEL = 0.45;

function recalculateLoadedPore(box) {
  if (String(box.title).toLowerCase() !== "open") return box;
  const manual = Array.isArray(box.manualPoreContour) && box.manualPoreContour.length >= 3 && box.manualPoreCrop;
  const points = manual ? box.manualPoreContour : box.poreContour;
  const crop = manual ? box.manualPoreCrop : box.poreCrop;
  if (!Array.isArray(points) || points.length < 3 || !crop || !(crop.size > 0)) return box;
  const region = measurePoreRegion(points.map(p => ({
    x: p.x * crop.size + crop.x - Math.floor(crop.x),
    y: p.y * crop.size + crop.y - Math.floor(crop.y),
  })));
  if (!region) return box;
  const legacyMeasurement = box.legacyMeasurement || (box.measurementMethod === "regionprops-pixel-centers-v1" ? null : {
    axisLong: toFiniteNumber(box.long), axisShort: toFiniteNumber(box.short),
    aperture: toFiniteNumber(box.aperture), area: toFiniteNumber(box.area),
    measurementMethod: box.measurementMethod || "legacy-unknown",
  });
  return { ...box, long: region.majorAxis, short: region.minorAxis,
    aperture: region.minorAxis * MICRON_PER_PIXEL, area: region.area * MICRON_PER_PIXEL ** 2,
    measurementMethod: "regionprops-pixel-centers-v1", legacyMeasurement };
}

function serializeMeasureRectsForFirestore(rects) {
  return (Array.isArray(rects) ? rects : []).map((rect) => {
    const x = toFiniteNumber(rect?.x, 0);
    const y = toFiniteNumber(rect?.y, 0);
    const width = toFiniteNumber(rect?.width, 0);
    const height = toFiniteNumber(rect?.height, 0);

    return {
      id: rect?.id != null ? rect.id.toString() : "",
      x,
      y,
      width,
      height,
      horizontal: width.toFixed(2),
      vertical: height.toFixed(2),
    };
  });
}

function normalizeMeasureRect(rect) {
  if (!rect) return null;

  const x = toFiniteNumber(rect.x, 0);
  const y = toFiniteNumber(rect.y, 0);
  const width = toFiniteNumber(rect.width, 0);
  const height = toFiniteNumber(rect.height, 0);

  if (width <= 0 || height <= 0) return null;

  return {
    id: rect.id ?? Date.now(),
    x,
    y,
    width,
    height,
    horizontal: width.toFixed(2),
    vertical: height.toFixed(2),
  };
}

function buildMeasureRectFromPoints(start, end) {
  if (!start || !end) return null;

  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);
  const width = Math.abs(end.x - start.x);
  const height = Math.abs(end.y - start.y);

  return {
    id: Date.now(),
    x,
    y,
    width,
    height,
    horizontal: width.toFixed(2),
    vertical: height.toFixed(2),
  };
}

// 編集用の元画像だけを、この画面のメモリ内で共有する。
// 表示用 <img>・DB・計測ロジックには触れない。追加パッケージも不要。
// キャッシュ保持中の Image は src / onload / onerror を呼び出し側で変更しない。
function createPhotoImageCache({
  translate = (key) => key,
  maxImages = 2,
  maxPixels = 3840 * 2160 * 2,
  timeoutMs = POLYGON_IMAGE_LOAD_TIMEOUT_MS,
} = {}) {
  const entries = new Map();

  const trimReadyImages = () => {
    const ready = Array.from(entries.entries()).filter(([, entry]) => entry.ready);
    let count = ready.length;
    let pixels = ready.reduce((sum, [, entry]) => sum + entry.pixels, 0);
    for (const [url, entry] of ready) {
      if (count <= maxImages && pixels <= maxPixels) break;
      // 参照を外すだけ。編集中・出力中の呼び出し側が持つ画像は壊さない。
      entries.delete(url);
      count -= 1;
      pixels -= entry.pixels;
    }
  };

  const load = (url) => {
    if (typeof url !== "string" || !url.trim()) {
      return Promise.reject(new Error(translate("bbox.editor.missingSource")));
    }
    const existing = entries.get(url);
    if (existing) {
      // 読み込み中も同じ Promise を返す。同時クリックで通信を増やさない。
      entries.delete(url);
      entries.set(url, existing);
      return existing.promise;
    }

    let resolveLoad;
    let rejectLoad;
    const promise = new Promise((resolve, reject) => {
      resolveLoad = resolve;
      rejectLoad = reject;
    });
    const entry = { promise, ready: false, pixels: 0, cancel: null };
    entries.set(url, entry);

    let image = null;
    let timer = null;
    let settled = false;

    const cleanup = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      if (image) {
        image.onload = null;
        image.onerror = null;
      }
      entry.cancel = null;
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (entries.get(url) === entry) entries.delete(url);
      // 未完了の通信のみ停止を試みる。成功済み画像の src は変更しない。
      if (image) {
        try { image.removeAttribute("src"); } catch (_) { /* best effort */ }
      }
      rejectLoad(error instanceof Error ? error : new Error(translate("bbox.editor.sourceLoadFailed")));
    };

    entry.cancel = () => {
      const error = new Error(translate("bbox.editor.loadCancelled"));
      error.code = "IMAGE_CACHE_CLEARED";
      fail(error);
    };

    try {
      image = new Image();
      // Canvas 計測・画像出力のため、従来と同じ CORS 設定を先に指定する。
      image.crossOrigin = "anonymous";
      image.decoding = "async";
      image.onload = () => {
        if (settled) return;
        if (!(image.naturalWidth > 0 && image.naturalHeight > 0)) {
          fail(new Error(translate("bbox.editor.sourceSizeMissing")));
          return;
        }
        settled = true;
        cleanup();
        entry.ready = true;
        entry.pixels = image.naturalWidth * image.naturalHeight;
        trimReadyImages();
        resolveLoad(image);
      };
      image.onerror = () => fail(new Error(translate("bbox.editor.sourceLoadFailed")));
      timer = setTimeout(() => {
        fail(new Error(translate("bbox.editor.loadTimeout", { seconds: Math.round(timeoutMs / 1000) })));
      }, timeoutMs);
      // 毎回変わるクエリ文字列は付けない。画像そのものも加工・縮小しない。
      image.src = url;
    } catch (error) {
      fail(error);
    }
    return promise;
  };

  const clear = () => {
    const oldEntries = Array.from(entries.values());
    entries.clear();
    oldEntries.forEach((entry) => entry.cancel?.());
  };

  return { load, clear };
}

function BoundingBox() {
  // Performance Safe v4: 既存Polygon state/functionを削除せず、重いUI/読込だけ停止。
  const { posts, loading, loadingMore, hasMore, error: postsError, user, refetch, loadMore, removePost } = usePosts();
  const photoListRef = useRef(null);
  const photoRequestRef = useRef(0);
  const deletingPhotoRef = useRef(false);
  // Open only the newest photo once when the lightweight list first arrives.
  // This restores the familiar initial view without preloading every photo detail.
  const latestAutoOpenedRef = useRef("");
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [downloadingEditLogs, setDownloadingEditLogs] = useState(false);
  const editLogsDownloadRef = useRef(false);
  const [boundingBoxes, setBoundingBoxes] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // 初期ズームレベル
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 }); // ドラッグの位置
  const [isDragging, setIsDragging] = useState(false); // ドラッグ状態
  const [startDragPosition, setStartDragPosition] = useState({ x: 0, y: 0 });
  const [openColor, setOpenColor] = useState("#ff0000"); // 初期色: Open (赤)
  const [closeColor, setCloseColor] = useState("#0000ff"); // 初期色: Close (青)
  const actualWidth = 3840; // 画像の実サイズ
  const actualHeight = 2160;
  const displayWidth = 1000; // 表示サイズ
  const displayHeight = (actualHeight / actualWidth) * displayWidth;
  const scaleX = actualWidth / displayWidth; // 横方向スケール
  const scaleY = actualHeight / displayHeight; // 縦方向スケール
  const [hoveredBoxKey, setHoveredBoxKey] = useState(null); // ホバー中のボックス
  const [drawBoundingBox, setDrawBoundingBox] = useState(true);
  const [drawText, setDrawText] = useState(true);
  const [drawId, setDrawId] = useState(true);
  const [drawScaleBar, setDrawScaleBar] = useState(true);
  const [drawPoreMask, setDrawPoreMask] = useState(true);
  const [boxShadowWidth, setBoxShadowWidth] = useState(1.0);
  const [mode, setMode] = useState("edit"); // "edit", "setting", "view"
  const [newBoxStart, setNewBoxStart] = useState(null);
  const [newBoxEnd, setNewBoxEnd] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedBoxKey, setSelectedBoxKey] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [shiftPressed, setShiftPressed] = useState(false);
  const [lines, setLines] = useState([]); // 直線の一覧
  const [newLineStart, setNewLineStart] = useState(null);
  const [newLineEnd, setNewLineEnd] = useState(null);
  const [measureRects, setMeasureRects] = useState([]); // 計測用四角形の一覧
  const [newRectStart, setNewRectStart] = useState(null);
  const [newRectEnd, setNewRectEnd] = useState(null);
  const [originalMeasureRects, setOriginalMeasureRects] = useState([]);
  const [zoomOrigin, setZoomOrigin] = useState({ x: displayWidth / 2, y: displayHeight / 2 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showCrosshair, setShowCrosshair] = useState(false);
  const [originalBoundingBoxes, setOriginalBoundingBoxes] = useState([]);
  const [originalLines, setOriginalLines] = useState([]);
  const [lineColor, setLineColor] = useState("white");
  const [lineWidth, setLineWidth] = useState(1);
  const [shiftZPressed, setShiftZPressed] = useState(false);
  const [showPhotoList, setShowPhotoList] = useState(false);
  const currentIndex = posts.findIndex(p => p.photopath === selectedPhoto?.photopath);
  const [showBBoxTable, setShowBBoxTable] = useState(false);
  const [count, setCount] = useState({ value: 0 });
  const [drawingMode, setDrawingMode] = useState("none");
  const [mouseScreenPos, setMouseScreenPos] = useState({ x: 0, y: 0 }); // fixed表示用
  const [aPressed, setAPressed] = useState(false);
  const [showCanvasHelp, setShowCanvasHelp] = useState(false);
  const [showPointerToolBadge, setShowPointerToolBadge] = useState(false);

  const canvasRef = useRef(null);
  const [mousePosCanvas, setMousePosCanvas] = useState({ x: 0, y: 0 });
  const [lineMode, setLineMode] = useState("free");
  const [polygonModalVisible, setPolygonModalVisible] = useState(false);
  const [polygonPoints, setPolygonPointsState] = useState([]);
  const currentPolygonRef = useRef([]);
  const polygonHistoryRef = useRef({ entries: [[]], index: 0 });
  const [polygonHistoryVersion, setPolygonHistoryVersion] = useState(0);
  const [beforePolygonPoints, setBeforePolygonPoints] = useState([]);
  const [polygonCompareMode, setPolygonCompareMode] = useState("after");
  const [showPointNumbers, setShowPointNumbers] = useState(false);
  const [showAxisLabels, setShowAxisLabels] = useState(false);
  const [showMeasurementAxes, setShowMeasurementAxes] = useState(true);
  const copyPolygon = (points) => points.map((p) => ({ ...p }));
  const commitPolygonHistory = () => {
    const history = polygonHistoryRef.current;
    const points = currentPolygonRef.current;
    if (JSON.stringify(history.entries[history.index]) === JSON.stringify(points)) return;
    history.entries = [...history.entries.slice(0, history.index + 1), copyPolygon(points)];
    history.index = history.entries.length - 1;
    setPolygonHistoryVersion((v) => v + 1);
  };
  // Keep refs synchronous; a drag is committed once on mouseup, not on every move.
  const setPolygonPoints = (value) => {
    setPolygonCompareMode("after");
    const points = typeof value === "function" ? value(currentPolygonRef.current) : value;
    currentPolygonRef.current = copyPolygon(points);
    setPolygonPointsState(currentPolygonRef.current);
    if (!polygonDragStartRef.current) commitPolygonHistory();
  };
  const initializePolygonHistory = (points, beforePoints = points) => {
    currentPolygonRef.current = copyPolygon(points);
    polygonHistoryRef.current = { entries: [copyPolygon(points)], index: 0 };
    setBeforePolygonPoints(copyPolygon(beforePoints));
    setPolygonCompareMode("after");
    setPolygonHistoryVersion((v) => v + 1);
  };
  const travelPolygonHistory = (direction) => {
    if (polygonImageLoading || polygonImageError || polygonDragStartRef.current || polygonImagePanStartRef.current) return;
    const history = polygonHistoryRef.current;
    const index = history.index + direction;
    if (index < 0 || index >= history.entries.length) return;
    history.index = index;
    const points = copyPolygon(history.entries[index]);
    currentPolygonRef.current = points;
    setPolygonPointsState(points);
    // Recalculate using the same geometry path, without adding a history entry.
    refreshPolygonGeometry(points);
    setHoveredPointIndex(null);
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;
    setPolygonCompareMode("after");
    setPolygonHistoryVersion((v) => v + 1);
  };

  const [showPolygonOverlay, setShowPolygonOverlay] = useState(true);
  // 外接する計測用ボックスはデフォルト非表示。必要なときだけ表示する。
  const [showMeasurementBox, setShowMeasurementBox] = useState(false);
  const [polygonArea, setPolygonArea] = useState(null);
  const polygonCanvasRef = useRef(null);
  const polygonCanvasFrameRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 400 });
  const [polygonCanvasDisplaySize, setPolygonCanvasDisplaySize] = useState(720);
  const [polygonImageZoom, setPolygonImageZoom] = useState(1);
  const [polygonImagePan, setPolygonImagePan] = useState({ x: 0, y: 0 });
  const [polygonImagePanMode, setPolygonImagePanMode] = useState(false);
  const [polygonImagePanning, setPolygonImagePanning] = useState(false);
  const polygonImagePanStartRef = useRef(null);
  const [mouseOnPolygonCanvas, setMouseOnPolygonCanvas] = useState({ x: null, y: null });
  const [fittedEllipse, setFittedEllipse] = useState(null); // 楕円の結果（中心・短径・長径）
  const [hoveredPointIndex, setHoveredPointIndex] = useState(null);
  const [pointDeleteMode, setPointDeleteMode] = useState(false);
  const [pointInsertMode, setPointInsertMode] = useState(false);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const [hoveredSegmentInfo, setHoveredSegmentInfo] = useState(null);
  const hoveredSegmentInfoRef = useRef(null);
  const suppressNextPolygonClickRef = useRef(false);
  const polygonDragStartRef = useRef(null);
  const [cachedImage, setCachedImage] = useState(null);
  const [polygonImageLoading, setPolygonImageLoading] = useState(false);
  const [polygonImageError, setPolygonImageError] = useState(false);
  const [polygonImageStatus, setPolygonImageStatus] = useState("");
  const [polygonImageRetryBox, setPolygonImageRetryBox] = useState(null);
  const [polygonSavingCount, setPolygonSavingCount] = useState(0);
  const polygonImageRequestIdRef = useRef(0);
  const polygonEditorSessionRef = useRef(0);
  const photoSaveQueueRef = useRef(Promise.resolve());
  const [boxMeasurement, setBoxMeasurement] = useState({ long: 0, short: 0 });
  const [binaryPreviewEnabled, setBinaryPreviewEnabled] = useState(false);
  const [binaryThreshold, setBinaryThreshold] = useState(175);
  const [binaryInvert, setBinaryInvert] = useState(false);
  const [binaryForegroundPixels, setBinaryForegroundPixels] = useState(0);
  const [polygonBoxActionPending, setPolygonBoxActionPending] = useState("");
  const polygonBoxActionRef = useRef(false);
  const polygonBoxActionDialogRef = useRef(false);
  const imageJExportLock = useRef(false);
  const [imageJExporting, setImageJExporting] = useState(false);
  const [savingBoxKey, setSavingBoxKey] = useState(null); // 保存中のkey
  // Edit Logsは一旦UI/読込を停止。操作ログの保存(logBoundingBoxAction)だけ継続する。
  // 全ユーザー分の履歴をGallery起動時に取得しないことで、初期表示の負荷を抑える。

  const [autoContourMode, setAutoContourMode] = useState(false);
  const [autoContourPointCount, setAutoContourPointCount] = useState(DEFAULT_AUTO_CONTOUR_POINTS);
  const [longThinContourAssistEnabled, setLongThinContourAssistEnabled] = useState(false);
  const [hoverContourPoints, setHoverContourPoints] = useState([]);
  const [hoverContourStatus, setHoverContourStatus] = useState("");
  const [hoverContourArea, setHoverContourArea] = useState(null);

  const [polygonToolMode, setPolygonToolMode] = useState("manualPolygon");

  const [showPhotoComment, setShowPhotoComment] = useState(true);
  const [isEditingPhotoComment, setIsEditingPhotoComment] = useState(false);
  const [photoCommentDraft, setPhotoCommentDraft] = useState("");
  const [photoCommentSaving, setPhotoCommentSaving] = useState(false);

  const isAutoExtracting = polygonToolMode === "autoAssist";
  const isManualPolygon = polygonToolMode === "manualPolygon";

  const POLYGON_POINT_SIZE = 3;
  const POLYGON_POINT_HOVER_SIZE = 4;
  const POLYGON_POINT_DRAG_SIZE = 4;

  // ログ保存は維持。表示・再取得は行わない。
  const logAction = async (payload) => {
    try {
      await logBoundingBoxAction(payload);
    } catch (e) {
      console.warn("logBoundingBoxAction failed:", e);
    }
  };

  useEffect(() => {
    hoveredSegmentInfoRef.current = hoveredSegmentInfo;
  }, [hoveredSegmentInfo]);
  const getSelectedPhotoComment = () => {
    if (!selectedPhoto) return "";

    return (
      selectedPhoto.freeText ||
      selectedPhoto.comment ||
      selectedPhoto.comments ||
      selectedPhoto.memo ||
      selectedPhoto.note ||
      selectedPhoto.description ||
      ""
    );
  };
  useEffect(() => {
    if (!selectedPhoto) {
      setPhotoCommentDraft("");
      setIsEditingPhotoComment(false);
      return;
    }

    const currentComment =
      selectedPhoto.freeText ||
      selectedPhoto.comment ||
      selectedPhoto.comments ||
      selectedPhoto.memo ||
      selectedPhoto.note ||
      selectedPhoto.description ||
      "";

    setPhotoCommentDraft(currentComment);
    setIsEditingPhotoComment(false);
  }, [selectedPhoto?.photopath]);
  const isCtrlOrCmdPressed = (e) => e.ctrlKey || e.metaKey;
  const [imageError, setImageError] = useState(false);
  const [imgReloadKey, setImgReloadKey] = useState(0); // 再読み込み用キー
  const { t, i18n } = useTranslation();
  useEffect(() => { setHoverContourStatus(""); setPolygonImageStatus(""); }, [t]);
  const handleDownloadEditLogs = async () => {
    if (!user?.email || editLogsDownloadRef.current) return;
    const email = user.email;
    const isCurrent = () => auth.currentUser?.email === email;
    const progressKey = "edit-logs-export";
    editLogsDownloadRef.current = true;
    setDownloadingEditLogs(true);
    try {
      message.loading({ content: t("bbox.logs.loading"), key: progressKey, duration: 0 });
      const logs = await fetchEditLogs({ db, email, isCurrent });
      if (!isCurrent()) throw new Error("edit-logs-account-changed");
      if (!logs.length) {
        message.info({ content: t("bbox.logs.empty"), key: progressKey });
        return;
      }
      const blob = new Blob([buildEditLogsCsv(logs)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      try {
        anchor.href = url;
        anchor.download = `stomatascope_edit_logs_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
      } finally {
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      message.success({ content: t("bbox.logs.downloaded", { count: logs.length }), key: progressKey });
    } catch (error) {
      console.error("Edit log export failed:", error);
      message.error({ content: t(error.message === "edit-logs-account-changed" ? "bbox.logs.accountChanged" : "bbox.logs.failed"), key: progressKey });
    } finally {
      editLogsDownloadRef.current = false;
      setDownloadingEditLogs(false);
    }
  };

  // 画像表示調整
  const [brightness, setBrightness] = useState(1.0); // 0.5〜2.0
  const [contrast, setContrast] = useState(1.0); // 0.5〜2.0
  const [sharpness, setSharpness] = useState(0.0); // 0.0〜1.0
  // 上部で
  const viewportRef = useRef(null);
  const photoImageCacheRef = useRef(null);
  if (photoImageCacheRef.current === null) {
    photoImageCacheRef.current = createPhotoImageCache({ translate: (key, options) => i18n.t(key, options) });
  }
  const hoverImageRequestRef = useRef(0);
  const activePhotoImageUrlRef = useRef("");
  const polygonImageOpenRef = useRef(false);

  // 画像をHTMLImageElementとしてロード
  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // 必要なら
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  // ★ 追加：素の画像（背景だけ）をオフスクリーンCanvasで描いてBlob化
  const makeCleanCanvasBlob = async ({ baseImageEl, baseImageUrl, width, height, mime = "image/jpeg", quality = 0.95 }) => {
    let img = baseImageEl;
    if (!img && baseImageUrl) img = await loadImage(baseImageUrl);
    if (!img) throw new Error(t("bbox.errors.noBaseImage"));

    const off = document.createElement("canvas");
    off.width = width;
    off.height = height;
    const ctx = off.getContext("2d");
    ctx.drawImage(img, 0, 0, off.width, off.height); // 背景だけ描く（オーバーレイなし）

    const blob = await new Promise((res) => off.toBlob(res, mime, quality));
    if (!blob) throw new Error(t("bbox.errors.blobFailed")); return blob;
  };

  // ★ 追加：BlobをStorageに上げてURL取得
  const uploadBlobToStorage = async (blob, filename) => {
    const storage = getStorage();
    const storageRef = ref(storage, `labelbox_crops/${filename}`);
    const snap = await uploadBytes(storageRef, blob);
    return await getDownloadURL(snap.ref);
  };

  const buildPhotoImageUrl = (photo) => {
    if (!photo?.userName || !photo?.cultiverName || !photo?.photopath) return "";

    return `https://firebasestorage.googleapis.com/v0/b/stomatascope-fdf90.appspot.com/o/${encodeURIComponent(
      photo.userName
    )}%2F${encodeURIComponent(photo.cultiverName)}%2F${encodeURIComponent(
      photo.photopath
    )}?alt=media&token=bb06e309-ca5b-4b6b-9bdd-7ea80efa4ced`;
  };

  const clearPolygonCanvas = () => {
    const canvas = polygonCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const resetPolygonMeasurementState = () => {
    setPolygonPoints([]);
    setPolygonArea(null);
    setBoxMeasurement({ long: 0, short: 0 });
    setFittedEllipse(null);
    setHoverContourPoints([]);
    setHoverContourArea(null);
    setHoverContourStatus("");
    setAutoContourMode(false);
    setBinaryForegroundPixels(0);
    setMouseOnPolygonCanvas({ x: null, y: null });
    setHoveredPointIndex(null);
    setPointDeleteMode(false);
    setPointInsertMode(false);
    setDraggingPointIndex(null);
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;
    setPolygonImagePanning(false);
    suppressNextPolygonClickRef.current = false;
    polygonDragStartRef.current = null;
    polygonImagePanStartRef.current = null;
    setPolygonToolMode("manualPolygon");
    setBinaryPreviewEnabled(false);
    setAutoContourMode(false);
  };

  const resetPolygonImageView = () => {
    setPolygonImageZoom(1);
    setPolygonImagePan({ x: 0, y: 0 });
    setPolygonImagePanMode(false);
    setPolygonImagePanning(false);
    polygonImagePanStartRef.current = null;
  };

  const clampPolygonImageZoom = (value) => {
    const n = toFiniteNumber(value, 1);
    return Math.max(MIN_POLYGON_IMAGE_ZOOM, Math.min(MAX_POLYGON_IMAGE_ZOOM, n));
  };

  const setPolygonImageZoomWithAnchor = (nextZoomValue, anchorClientPoint = null) => {
    const nextZoom = clampPolygonImageZoom(nextZoomValue);
    const frame = polygonCanvasFrameRef.current;

    if (!frame) {
      setPolygonImageZoom(nextZoom);
      if (nextZoom <= 1) {
        setPolygonImagePan({ x: 0, y: 0 });
        setPolygonImagePanMode(false);
      }
      return nextZoom;
    }

    const rect = frame.getBoundingClientRect();
    const anchorX = anchorClientPoint ? anchorClientPoint.clientX - rect.left : rect.width / 2;
    const anchorY = anchorClientPoint ? anchorClientPoint.clientY - rect.top : rect.height / 2;
    const baseLeft = (rect.width - polygonCanvasDisplaySize) / 2;
    const baseTop = (rect.height - polygonCanvasDisplaySize) / 2;
    const safeCurrentZoom = Math.max(MIN_POLYGON_IMAGE_ZOOM, polygonImageZoom || 1);

    const contentX = (anchorX - baseLeft - polygonImagePan.x) / safeCurrentZoom;
    const contentY = (anchorY - baseTop - polygonImagePan.y) / safeCurrentZoom;

    const nextPan = nextZoom <= 1
      ? { x: 0, y: 0 }
      : {
        x: anchorX - baseLeft - contentX * nextZoom,
        y: anchorY - baseTop - contentY * nextZoom,
      };

    setPolygonImageZoom(nextZoom);
    setPolygonImagePan(nextPan);

    if (nextZoom <= 1) {
      setPolygonImagePanMode(false);
      setPolygonImagePanning(false);
      polygonImagePanStartRef.current = null;
    }

    return nextZoom;
  };

  const startPolygonImagePan = (e) => {
    if (polygonImageLoading || polygonImageError || !cachedImage) return false;
    // Right/middle dragging also works at the initial zoom.

    e.preventDefault();
    e.stopPropagation();

    suppressNextPolygonClickRef.current = true;
    polygonImagePanStartRef.current = {
      button: e.button,
      clientX: e.clientX,
      clientY: e.clientY,
      pan: { ...polygonImagePan },
    };
    setPolygonImagePanning(true);
    setHoverContourStatus(t("bbox.status.dragImageToPan"));
    return true;
  };

  const updatePolygonImagePanFromPointer = (e) => {
    const start = polygonImagePanStartRef.current;
    if (!start) return;

    setPolygonImagePan({
      x: start.pan.x + e.clientX - start.clientX,
      y: start.pan.y + e.clientY - start.clientY,
    });
  };

  const finishPolygonImagePan = () => {
    if (!polygonImagePanning) return;

    if (polygonImagePanStartRef.current?.button !== 0) suppressNextPolygonClickRef.current = false;
    setPolygonImagePanning(false);
    polygonImagePanStartRef.current = null;
    setHoverContourStatus(t("bbox.status.imagePanDone"));
  };

  const handlePolygonImageWheel = (e) => {
    if (polygonImageLoading || polygonImageError || !cachedImage) return;

    e.preventDefault();
    e.stopPropagation();

    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const nextZoom = setPolygonImageZoomWithAnchor(polygonImageZoom * factor, {
      clientX: e.clientX,
      clientY: e.clientY,
    });
    setHoverContourStatus(t("bbox.status.imageZoom", { percent: Math.round(nextZoom * 100) }));
  };

  // Polygon Editor のホイールズームは React の onWheel ではなく
  // passive:false のネイティブイベントで受ける。
  // これにより preventDefault が確実に効き、ページスクロールを止めたままズームできる。
  useEffect(() => {
    if (!polygonModalVisible) return;

    const frame = polygonCanvasFrameRef.current;
    if (!frame) return;

    const wheelHandler = (event) => {
      handlePolygonImageWheel(event);
    };

    frame.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      frame.removeEventListener("wheel", wheelHandler);
    };
  }, [
    polygonModalVisible,
    polygonImageLoading,
    polygonImageError,
    cachedImage,
    polygonImageZoom,
    polygonImagePan.x,
    polygonImagePan.y,
    polygonCanvasDisplaySize,
  ]);

  const clearPolygonCropImage = () => {
    polygonImageRequestIdRef.current += 1;
    setPreviewImage(null);
    setCachedImage(null);
    setPolygonImageLoading(false);
    setPolygonImageError(false);
    setPolygonImageStatus("");
    setPolygonImageRetryBox(null);
    resetPolygonImageView();
    clearPolygonCanvas();
  };

  const resetPolygonEditorState = ({ clearImage = false, clearSelection = false } = {}) => {
    resetPolygonMeasurementState();

    if (clearImage) {
      clearPolygonCropImage();
    }

    if (clearSelection) {
      setSelectedBoxKey(null);
    }
  };

  const closePolygonModal = () => {
    polygonImageOpenRef.current = false;
    hoverImageRequestRef.current += 1;
    polygonEditorSessionRef.current += 1;
    setPolygonModalVisible(false);
    resetPolygonEditorState({ clearImage: true, clearSelection: true });
  };

  const handleReset = () => {
    if (polygonImageLoading || polygonImageError || !cachedImage) return;
    if (beforePolygonPoints.length === 0) {
      setHoverContourStatus(t("bbox.editor.noBefore"));
      return;
    }
    // Finish any pending drag before recording Reset as an undoable edit.
    commitPolygonHistory();
    polygonDragStartRef.current = null;
    setDraggingPointIndex(null);
    setPolygonCompareMode("after");
    setPolygonToolMode("manualPolygon");
    setAutoContourMode(false);
    setBinaryPreviewEnabled(false);
    setShowPolygonOverlay(true);
    setPointDeleteMode(false);
    setPointInsertMode(false);
    setPolygonImagePanMode(false);
    setHoveredPointIndex(null);
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;
    suppressNextPolygonClickRef.current = false;
    refreshPolygonGeometry(copyPolygon(beforePolygonPoints), t("bbox.editor.resetDone"), { forceEllipse: true });
  };

  const clearAutoContourPreview = () => {
    setHoverContourPoints([]);
    setHoverContourArea(null);
    setHoverContourStatus("");
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;
  };

  const applyPolygonToolMode = (mode) => {
    setPolygonToolMode(mode);

    setPointDeleteMode(false);
    setPointInsertMode(false);
    setPolygonImagePanMode(false);
    setShowPolygonOverlay(true);
    clearAutoContourPreview();

    if (mode === "manualAssist") {
      setAutoContourMode(false);
      setBinaryPreviewEnabled(true);
      setLongThinContourAssistEnabled(false);

      setHoverContourStatus(t("bbox.status.resetDone"));

      setTimeout(() => {
        updateAutoPolygonFromCenter(t("bbox.status.resetPolygon"), 175);
      }, 0);
      return;
    }

    if (mode === "manualPolygon") {
      setAutoContourMode(false);
      setBinaryPreviewEnabled(false);
      setPolygonPoints([]);
      setPolygonArea(null);
      setFittedEllipse(null);
      setBoxMeasurement({ long: 0, short: 0 });
      setHoverContourStatus(t("bbox.status.manualPolygonClickToAdd")); return;
    }

    if (mode === "autoAssist") {
      setAutoContourMode(false);
      setBinaryPreviewEnabled(true);
      setLongThinContourAssistEnabled(false);

      const nextThreshold = computeAdaptiveThresholdFromCenter(
        POLYGON_CANVAS_SIZE,
        POLYGON_CANVAS_SIZE
      );

      setBinaryThreshold(nextThreshold);

      updateAutoPolygonFromCenter(
        t("bbox.status.autoExtractThreshold", { threshold: Math.round(nextThreshold) }),
        nextThreshold,
        false
      );

      return;
    }
  };

  const getPolygonCanvasPointer = (e) => {
    const canvas = polygonCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const canvasX = ((e.clientX - rect.left) * canvas.width) / rect.width;
    const canvasY = ((e.clientY - rect.top) * canvas.height) / rect.height;

    const logicalX = canvasX / POLYGON_SCALE_FACTOR;
    const logicalY = canvasY / POLYGON_SCALE_FACTOR;

    return {
      canvasX: Math.max(0, Math.min(canvas.width, canvasX)),
      canvasY: Math.max(0, Math.min(canvas.height, canvasY)),
      x: Math.max(0, Math.min(POLYGON_LOGICAL_SIZE, logicalX)),
      y: Math.max(0, Math.min(POLYGON_LOGICAL_SIZE, logicalY)),
    };
  };

  useEffect(() => {
    if (!polygonModalVisible) return;

    const frame = polygonCanvasFrameRef.current;
    if (!frame) return;

    let animationFrameId = null;

    const updateDisplaySize = () => {
      const rect = frame.getBoundingClientRect();
      const style = window.getComputedStyle(frame);
      const paddingX =
        parseFloat(style.paddingLeft || "0") + parseFloat(style.paddingRight || "0");
      const paddingY =
        parseFloat(style.paddingTop || "0") + parseFloat(style.paddingBottom || "0");

      const availableWidth = Math.max(0, rect.width - paddingX);
      const availableHeight = Math.max(0, rect.height - paddingY);
      const nextSize = Math.max(96, Math.floor(Math.min(availableWidth, availableHeight, 820)));

      setPolygonCanvasDisplaySize((prev) => (prev === nextSize ? prev : nextSize));
    };

    const scheduleUpdate = () => {
      if (animationFrameId != null) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updateDisplaySize);
    };

    scheduleUpdate();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleUpdate) : null;

    if (resizeObserver) {
      resizeObserver.observe(frame);
    } else {
      window.addEventListener("resize", scheduleUpdate);
    }

    return () => {
      if (animationFrameId != null) cancelAnimationFrame(animationFrameId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", scheduleUpdate);
      }
    };
  }, [polygonModalVisible]);

  useEffect(() => {
    if (user) {
      const settings = {
        drawBoundingBox,
        drawText,
        drawId,
        drawScaleBar,
        drawPoreMask,
        openColor,
        closeColor,
        boxShadowWidth,
      };
      localStorage.setItem(`settings-${user.email}`, JSON.stringify(settings));
    }
  }, [drawBoundingBox, drawText, drawId, drawScaleBar, drawPoreMask, openColor, closeColor, boxShadowWidth]);
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem(`settings-${user.email}`);
      if (saved) {
        try {
          const s = JSON.parse(saved);
          setDrawBoundingBox(s.drawBoundingBox ?? true);
          setDrawText(s.drawText ?? true);
          setDrawId(s.drawId ?? true);
          setDrawScaleBar(s.drawScaleBar ?? true);
          setDrawPoreMask(s.drawPoreMask ?? true);
          setOpenColor(s.openColor ?? "#ff0000");
          setCloseColor(s.closeColor ?? "#0000ff");
          setBoxShadowWidth(s.boxShadowWidth ?? 1.0);
        } catch (err) {
          console.warn("設定の読み込み失敗:", err);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "1") setShiftPressed(true);
      if (e.key === "2") setCtrlPressed(true);
      if (e.key.toLowerCase() === "a") setAPressed(true);
    };

    const handleKeyUp = (e) => {
      if (e.key === "1") setShiftPressed(false);
      if (e.key === "2") setCtrlPressed(false);
      if (e.key.toLowerCase() === "a") setAPressed(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);
  // Performance: Gallery起動時は軽量一覧だけ先に取得し、
  // 最新1枚だけ full document を自動取得して初期表示する。
  // それ以外の results / pore contours は、ユーザーが選択した時だけ取得する。

  // Performance: older photos are loaded only when the user presses the explicit Load button.
  // Do not auto-fetch on selection/scroll; this keeps Gallery memory bounded.

  const goToPreviousPhoto = () => {
    if (currentIndex > 0) {
      handlePhotoClick(posts[currentIndex - 1]);
    }
  };

  const goToNextPhoto = () => {
    if (currentIndex < posts.length - 1) {
      handlePhotoClick(posts[currentIndex + 1]);
    }
  };


  const getMousePositionOnImage = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    // キャンバス上の表示座標 → 論理座標（元画像の座標）へ変換
    const displayX = (offsetX - zoomOrigin.x - currentPosition.x) / zoomLevel;
    const displayY = (offsetY - zoomOrigin.y - currentPosition.y) / zoomLevel;

    const logicalX = displayX * scaleX + actualWidth / 2;
    const logicalY = displayY * scaleY + actualHeight / 2;

    return { x: logicalX, y: logicalY };
  };



  // 編集モードでのボックス作成（mousedown）
  const handleCanvasMouseDown = (e) => {

    // 👇 Hover中のやつを一旦解除！
    setHoveredBoxKey(null);
    if (mode !== "edit") return;

    // 🛑 右クリックメニューを防止（必須！）
    e.preventDefault();

    const isLeftClick = e.button === 0;
    const isRightClick = e.button === 2;

    // 左クリック・右クリック以外は無視
    if (!isLeftClick && !isRightClick) return;

    const boxType = isRightClick ? "close" : "open";

    setNewBoxStart({ ...getMousePositionOnImage(e), type: boxType });
    setNewBoxEnd(null);
  };

  const handleCanvasMouseMove = (e) => {
    if (mode !== "edit" || !newBoxStart) return;
    setNewBoxEnd(getMousePositionOnImage(e));
  };


  // mouseup
  const handleCanvasMouseUp = async () => {
    if (!newBoxStart) return;
    setNewBoxStart(null);
    const finalEnd = newBoxEnd || newBoxStart;
    const width = Math.abs(finalEnd.x - newBoxStart.x);
    const height = Math.abs(finalEnd.y - newBoxStart.y);

    if (width < 5 || height < 5) {
      setNewBoxStart(null);
      setNewBoxEnd(null);
      message.warning(t("bbox.boxTooSmall")); // ← 追加（任意）
      return;
    }

    const left = Math.min(newBoxStart.x, finalEnd.x);
    const top = Math.min(newBoxStart.y, finalEnd.y);
    const newId = getNextId();
    const isClose = newBoxStart.type === "close";

    const newBox = {
      key: newId,
      id: newId.toString(),
      x: left,
      y: top,
      width,
      height,
      title: isClose ? "close" : "open",
      aperture: 0.0,
      long: 0.0,
      short: 0.0,
      area: 0,
      idClass: 1, // openなら1で開始してもOK
    };

    const updatedBoxes = [...boundingBoxes, newBox];
    setBoundingBoxes(updatedBoxes);
    setUndoStack(prev => [...prev, { boxes: boundingBoxes, lines, measureRects }]);

    try {
      await savePhotoResults(updatedBoxes);
      message.success(t("bbox.addedAndSaved", { id: newBox.id }));
      await logAction({
        email: user.email,
        photopath: selectedPhoto.photopath,
        cultiverName: selectedPhoto.cultiverName,
        boxId: newBox.id,
        action: "added(" + newBox.title + ")",
      });

    } catch (e) {
      console.error("追加保存エラー:", e);
      message.error(t("bbox.addSaveFailed"));
    }
    setNewBoxStart(null);
    setNewBoxEnd(null);
    // 🔥 すぐ対象のBoxを開いて画像抽出・モーダル表示
    if (newBox.title === "open") {
      void openPolygonEditorForBox(newBox);
    }
  };


  const handleUndoBoundingBox = () => {
    if (undoStack.length === 0) return;

    const last = undoStack[undoStack.length - 1];

    setUndoStack((prev) => prev.slice(0, -1));
    setRedoStack((prev) => [
      ...prev,
      { boxes: boundingBoxes, lines, measureRects },
    ]);

    setBoundingBoxes(last.boxes);
    setLines(last.lines);
    setMeasureRects(last.measureRects ?? measureRects);
  };

  const handleRedoBoundingBox = () => {
    if (redoStack.length === 0) return;

    const last = redoStack[redoStack.length - 1];
    if (!last?.boxes || !last?.lines) return; // 🔥 safety check！

    setRedoStack((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [
      ...prev,
      { boxes: boundingBoxes, lines, measureRects },
    ]);

    setBoundingBoxes(last.boxes);
    setLines(last.lines);
    setMeasureRects(last.measureRects ?? measureRects);
  };


  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((isCtrlOrCmdPressed(e)) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndoBoundingBox();
      }

      if ((isCtrlOrCmdPressed(e)) && e.key === "y") {
        e.preventDefault();
        handleRedoBoundingBox();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undoStack, redoStack, boundingBoxes, lines, measureRects]);



  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const selectedPhotoImageKey = selectedPhoto
    ? `${selectedPhoto.userName || ""}/${selectedPhoto.cultiverName || ""}/${selectedPhoto.photopath || ""}`
    : "";

  useEffect(() => {
    activePhotoImageUrlRef.current = buildPhotoImageUrl(selectedPhoto);
    hoverImageRequestRef.current += 1;
    // 写真が本当に変わったときだけ、前の写真の遅延コールバックを無効にする。
    // 同じ写真の計測結果保存ではこの Effect は走らない。
    if (polygonImageOpenRef.current) closePolygonModal();
    if (selectedPhotoImageKey) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [selectedPhotoImageKey]);

  // 通常表示用の開口輪郭はBBデータが変わった時だけ計算する。
  // zoom/panではこの配列を再生成しないため、大量BBでも軽くなる。
  const poreMaskPolygons = useMemo(() => {
    return boundingBoxes
      .filter((box) => box?.title === "open")
      .map((box) => {
        const geometry = getPreferredPoreGeometryForDisplay(box);
        if (!geometry) return null;
        const cropX = toFiniteNumber(geometry.crop?.x, 0);
        const cropY = toFiniteNumber(geometry.crop?.y, 0);
        const cropSize = toFiniteNumber(geometry.crop?.size, 0);
        if (cropSize <= 0) return null;
        const points = geometry.points.map((p) => {
          const originalX = cropX + toFiniteNumber(p?.x, 0) * cropSize;
          const originalY = cropY + toFiniteNumber(p?.y, 0) * cropSize;
          return `${originalX / scaleX},${originalY / scaleY}`;
        }).join(" ");
        return points ? { key: box.key, points, source: geometry.source } : null;
      })
      .filter(Boolean);
  }, [boundingBoxes, scaleX, scaleY]);

  function getPreferredPoreGeometryForDisplay(box) {
    if (Array.isArray(box?.manualPoreContour) && box.manualPoreContour.length >= 3 && box?.manualPoreCrop) {
      return { points: box.manualPoreContour, crop: box.manualPoreCrop, source: "manual" };
    }
    if (Array.isArray(box?.poreContour) && box.poreContour.length >= 3 && box?.poreCrop) {
      return { points: box.poreContour, crop: box.poreCrop, source: "ai" };
    }
    return null;
  }

  const convertToFirestoreFormat = (box) => {
    const x = toFiniteNumber(box.x, 0);
    const y = toFiniteNumber(box.y, 0);
    const width = toFiniteNumber(box.width, 0);
    const height = toFiniteNumber(box.height, 0);

    return {
      title: box.title || "open",
      aperture: toFiniteNumber(box.aperture, 0),
      axisLong: toFiniteNumber(box.long, 0),
      axisShort: toFiniteNumber(box.short, 0),
      confidence: toFiniteNumber(box.confidence, 1),
      area: toFiniteNumber(box.area, 0),
      ...(box.legacyMeasurement ? { legacyMeasurement: box.legacyMeasurement } : {}),
      ...(box.measurementMethod ? { measurementMethod: box.measurementMethod, pixelSize: MICRON_PER_PIXEL } : {}),
      id: box.id != null ? box.id.toString() : box.key.toString(),
      idClass: box.idClass != null ? Number(box.idClass) : 0,
      stability: toFiniteNumber(box.stability, 0),
      scoreThreshold: toFiniteNumber(box.scoreThreshold, 0),
      status: box.status || "CalcAperture",
      location: {
        left: x,
        top: y,
        right: x + width,
        bottom: y + height,
        empty: false,
      },
      // Android AI原本。Web編集では上書きしない。
      poreContour: Array.isArray(box.poreContour)
        ? box.poreContour.map((p) => ({ x: toFiniteNumber(p?.x, 0), y: toFiniteNumber(p?.y, 0) }))
        : [],
      poreCrop: box.poreCrop
        ? {
            x: toFiniteNumber(box.poreCrop.x, 0),
            y: toFiniteNumber(box.poreCrop.y, 0),
            size: toFiniteNumber(box.poreCrop.size, 0),
          }
        : null,
      // Webで人が修正した輪郭。AI原本とは分離して保存する。
      manualPoreContour: Array.isArray(box.manualPoreContour)
        ? box.manualPoreContour.map((p) => ({ x: toFiniteNumber(p?.x, 0), y: toFiniteNumber(p?.y, 0) }))
        : [],
      manualPoreCrop: box.manualPoreCrop
        ? {
            x: toFiniteNumber(box.manualPoreCrop.x, 0),
            y: toFiniteNumber(box.manualPoreCrop.y, 0),
            size: toFiniteNumber(box.manualPoreCrop.size, 0),
          }
        : null,
      contourSource: box.contourSource ||
        (Array.isArray(box.manualPoreContour) && box.manualPoreContour.length >= 3 ? "manual" : "ai"),
      ...(box.contourEditedAt ? { contourEditedAt: box.contourEditedAt } : {}),
    };
  };


  /**
   * BB編集後の保存口をここに統一する。
   *
   * 重要:
   * - aperture は open のみ平均
   * - size は全BB数
   * - aperture は Firestore に number として保存する
   * - 保存はキュー化して、連続保存時に古い保存完了が新しい編集内容を上書きしないようにする
   */

  const savePhotoResults = async (updatedBoxes, options = {}) => {
    const targetPhoto = options.photo || selectedPhoto;
    const targetEmail = options.email || user?.email || targetPhoto?.userName;
    const boxesToSave = Array.isArray(updatedBoxes) ? updatedBoxes.map((box) => ({ ...box })) : [];
    const linesToSave = options.lines;
    const measureRectsToSave = options.measureRects;
    const shouldUpdateLocalState = options.updateLocalState !== false;
    const shouldUpdateSelectedPhoto = shouldUpdateLocalState && options.updateSelectedPhoto !== false;

    if (!targetPhoto?.photopath || !targetEmail) {
      throw new Error(t("bbox.noSaveTarget"));
    }

    const runSave = async () => {
      const converted = boxesToSave.map(convertToFirestoreFormat);
      const summary = calcPhotoSummaryFromBoxes(boxesToSave);
      const updatedAt = Timestamp.now();

      const payload = {
        results: converted,
        aperture: summary.aperture,
        size: summary.size,
        updatedAt,
      };

      if (linesToSave !== undefined) {
        payload.lines = serializeLinesForFirestore(linesToSave);
      }

      if (measureRectsToSave !== undefined) {
        payload.measureRects = serializeMeasureRectsForFirestore(measureRectsToSave);
      }

      const photoRef = doc(db, targetEmail, targetPhoto.photopath);
      await updateDoc(photoRef, payload);

      if (shouldUpdateLocalState) {
        setOriginalBoundingBoxes(boxesToSave);
        if (linesToSave !== undefined) {
          setOriginalLines(linesToSave);
        }
        if (measureRectsToSave !== undefined) {
          setOriginalMeasureRects(measureRectsToSave);
        }
      }

      if (shouldUpdateSelectedPhoto) {
        setSelectedPhoto((prev) => {
          if (!prev || prev.photopath !== targetPhoto.photopath) return prev;
          return {
            ...prev,
            results: converted,
            aperture: summary.aperture,
            size: summary.size,
            updatedAt,
            ...(linesToSave !== undefined ? { lines: payload.lines } : {}),
            ...(measureRectsToSave !== undefined ? { measureRects: payload.measureRects } : {}),
          };
        });
      }

      return {
        converted,
        aperture: summary.aperture,
        size: summary.size,
        updatedAt,
      };
    };

    const queuedSave = photoSaveQueueRef.current
      .catch(() => undefined)
      .then(runSave);

    photoSaveQueueRef.current = queuedSave.catch(() => undefined);
    return queuedSave;
  };
  const persistPolygonBoxAction = async ({ action, photo, email, key, session, boxes }) => {
    if (polygonBoxActionRef.current) return;
    if (!photo?.photopath || !email || auth.currentUser?.email !== email) {
      throw new Error(t("bbox.noSaveTarget"));
    }
    if (polygonEditorSessionRef.current !== session) throw new Error(t("bbox.editor.boxActionMissing"));
    const change = getPolygonBoxAction(boxes, key, action);
    if (!change) throw new Error(t("bbox.editor.boxActionMissing"));
    polygonBoxActionRef.current = true;
    setPolygonBoxActionPending(action);
    try {
      // Persist before updating the editor, so a failed save leaves the edit intact.
      const saved = await savePhotoResults(change.updatedBoxes, { photo, email, updateLocalState: false });
      if (polygonEditorSessionRef.current === session && auth.currentUser?.email === email) {
        setBoundingBoxes(change.updatedBoxes);
        setOriginalBoundingBoxes(change.updatedBoxes);
        setSelectedPhoto(prev => prev?.photopath === photo.photopath ? {
          ...prev, results: saved.converted, aperture: saved.aperture, size: saved.size, updatedAt: saved.updatedAt,
        } : prev);
        closePolygonModal();
      }
      message.success(t(action === "delete" ? "bbox.editor.boxDeleted" : "bbox.editor.boxClosed"));
      // Logging failure must not turn a successful result save into an apparent failure.
      await logAction({ email, photopath: photo.photopath, cultiverName: photo.cultiverName,
        boxId: change.before.id ?? key, action: action === "delete" ? "deleted" : "changed(close)",
        details: { source: "polygon-editor", before: convertToFirestoreFormat(change.before),
          after: change.after ? convertToFirestoreFormat(change.after) : null },
      });
    } catch (error) {
      message.error(t("bbox.editor.boxActionFailed"));
      throw error;
    } finally {
      polygonBoxActionRef.current = false;
      setPolygonBoxActionPending("");
    }
  };

  const requestPolygonBoxAction = (action) => {
    if (polygonBoxActionRef.current || polygonBoxActionDialogRef.current || polygonSavingCount > 0 || savingBoxKey != null) return;
    const box = boundingBoxes.find(item => item.key === selectedBoxKey);
    if (!box || !selectedPhoto?.photopath || !user?.email) return;
    const context = { action, photo: { ...selectedPhoto }, email: user.email, key: box.key,
      session: polygonEditorSessionRef.current, boxes: boundingBoxes.map(item => ({ ...item })) };
    polygonBoxActionDialogRef.current = true;
    const dialog = confirm({
      afterClose: () => { polygonBoxActionDialogRef.current = false; },
      title: t(action === "delete" ? "bbox.editor.deleteBoxConfirm" : "bbox.editor.closeBoxConfirm"),
      content: t("bbox.editor.boxActionConfirmDetail", { photo: selectedPhoto.photopath, id: box.id ?? box.key }),
      okText: t(action === "delete" ? "bbox.editor.deleteBox" : "bbox.editor.closeBox"),
      cancelText: t("common.cancel"), okButtonProps: { danger: action === "delete" }, maskClosable: false,
      onOk: async () => {
        dialog.update({ cancelButtonProps: { disabled: true }, closable: false, keyboard: false });
        try { await persistPolygonBoxAction(context); }
        finally { dialog.update({ cancelButtonProps: { disabled: false }, keyboard: true }); }
      },
    });
  };

  const handleSavePhotoComment = async () => {
    if (!selectedPhoto?.photopath) {
      message.error(t("bbox.photoComment.noPhoto"));
      return;
    }

    const targetEmail = user?.email || selectedPhoto.userName;

    if (!targetEmail) {
      message.error(t("bbox.photoComment.noUser"));
      return;
    }

    setPhotoCommentSaving(true);

    try {
      const updatedAt = Timestamp.now();
      const commentToSave = photoCommentDraft ?? "";

      const photoRef = doc(db, targetEmail, selectedPhoto.photopath);

      await updateDoc(photoRef, {
        freeText: commentToSave,
        updatedAt,
      });

      setSelectedPhoto((prev) => {
        if (!prev || prev.photopath !== selectedPhoto.photopath) return prev;

        return {
          ...prev,
          freeText: commentToSave,
          updatedAt,
        };
      });

      setIsEditingPhotoComment(false);
      message.success(t("bbox.photoComment.saved"));

      try {
        refetch?.();
      } catch (e) {
        console.warn("一覧リフレッシュ失敗:", e);
      }
    } catch (e) {
      console.error("コメント保存エラー:", e);
      message.error(t("bbox.photoComment.saveFailed"));
    } finally {
      setPhotoCommentSaving(false);
    }
  };


  const handlePhotoClick = async (photo, options = {}) => {
    if (!photo || (deletingPhotoRef.current && !options.allowDuringDelete)) return;
    const requestId = ++photoRequestRef.current;

    try {
      const photoRef = doc(db, user.email, photo.photopath);

      const snap = await getDoc(photoRef);
      if (requestId !== photoRequestRef.current) return;
      console.log("photo:", photo);

      if (!snap.exists()) {
        console.warn("データが存在しません");
        return;
      }

      const photoData = snap.data();

      setSelectedPhoto(photoData); // ← ステート更新！

      const boxes = (photoData.results || []).filter(item => item.location && item.location.left != null)
        .map((item, index) => {
          const id = item.id !== undefined && item.id !== "" ? Number(item.id) : index;
          return {
            key: id,
            id: id.toString(),
            x: item.location.left,
            y: item.location.top,
            width: item.location.right - item.location.left,
            height: item.location.bottom - item.location.top,
            title: item.title || "Unknown",
            aperture: item.aperture,
            long: item.axisLong,
            short: item.axisShort,
            area: item.area || 0, // 🔥 これを追加
            idClass: item.idClass != null ? Number(item.idClass) : 0,
            stability: item.stability ?? 0, // ←これが必要！
            scoreThreshold: item.scoreThreshold ?? 0,
            poreContour: Array.isArray(item.poreContour) ? item.poreContour : [],
            poreCrop: item.poreCrop ?? null,
            manualPoreContour: Array.isArray(item.manualPoreContour) ? item.manualPoreContour : [],
            manualPoreCrop: item.manualPoreCrop ?? null,
            measurementMethod: item.measurementMethod ?? null,
            legacyMeasurement: item.legacyMeasurement ?? null,
            contourSource: item.contourSource ||
              (Array.isArray(item.manualPoreContour) && item.manualPoreContour.length >= 3 ? "manual" : "ai"),
            contourEditedAt: item.contourEditedAt ?? null,
          };
        }).map(recalculateLoadedPore);

      const loadedMeasureRects = (photoData.measureRects || [])
        .map(normalizeMeasureRect)
        .filter(Boolean);

      setBoundingBoxes(boxes);
      setLines(photoData.lines ?? []);
      setMeasureRects(loadedMeasureRects);
      setOriginalBoundingBoxes(boxes);
      setOriginalLines(photoData.lines ?? []);
      setOriginalMeasureRects(loadedMeasureRects);


    } catch (error) {
      if (requestId === photoRequestRef.current) {
        console.error("データ取得エラー:", error);
        message.error(t("bbox.photoLoadFailed"));
      }
    }
  };

  // Restore the familiar initial UX: show the newest photo automatically.
  // usePosts is already ordered by timestamp desc, so posts[0] is the newest loaded record.
  // Only that one document is fetched; this does not undo the Gallery memory optimization.
  useEffect(() => {
    if (loading || !user?.email || posts.length === 0) return;
    if (selectedPhoto?.photopath) return;

    const latest = posts[0];
    if (!latest?.photopath) return;

    const openKey = `${user.email}/${latest.photopath}`;
    if (latestAutoOpenedRef.current === openKey) return;

    latestAutoOpenedRef.current = openKey;
    void handlePhotoClick(latest);
  }, [loading, user?.email, posts, selectedPhoto?.photopath]);

  const handleMouseDown = (e) => {
    if (mode === "edit" && ctrlPressed) {
      setDrawingMode("bbox");
      handleCanvasMouseDown(e);
    } else if (mode === "edit" && aPressed) {
      e.preventDefault();
      setNewRectStart(getMousePositionOnImage(e));
      setNewRectEnd(null);
      setDrawingMode("rect");
    } else if (mode === "edit" && shiftPressed) {
      setNewLineStart(getMousePositionOnImage(e));
      setNewLineEnd(null);
      setDrawingMode("line");
    } else {
      setIsDragging(true);
      setStartDragPosition({ x: e.clientX - currentPosition.x, y: e.clientY - currentPosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (mode === "edit" && ctrlPressed) {
      handleCanvasMouseMove(e);
    } else if (mode === "edit" && newRectStart) {
      setNewRectEnd(getMousePositionOnImage(e));
    } else if (mode === "edit" && shiftPressed && newLineStart) {
      let pos = getMousePositionOnImage(e);

      if (shiftZPressed) {
        const dx = pos.x - newLineStart.x;
        const dy = pos.y - newLineStart.y;

        // 角度を判断してスナップ
        if (Math.abs(dx) > Math.abs(dy)) {
          pos.y = newLineStart.y; // 水平に固定
        } else {
          pos.x = newLineStart.x; // 垂直に固定
        }
      }

      setNewLineEnd(pos);
    } else if (isDragging) {
      const x = e.clientX - startDragPosition.x;
      const y = e.clientY - startDragPosition.y;
      setCurrentPosition({ x, y });
    }


    // 🎯 クロスヘア用マウス位置を記録（display座標系）
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setMousePos({ x: offsetX, y: offsetY });
    setMouseScreenPos({ x: e.clientX, y: e.clientY });

  };


  const handleMouseUp = async () => {
    if (mode === "edit" && ctrlPressed) {
      await handleCanvasMouseUp();
      setDrawingMode("none");
    } else if (mode === "edit" && newRectStart) {
      const finalEnd = newRectEnd || newRectStart;
      const newRect = buildMeasureRectFromPoints(newRectStart, finalEnd);

      setNewRectStart(null);
      setNewRectEnd(null);
      setDrawingMode("none");

      if (!newRect || newRect.width < 5 || newRect.height < 5) {
        message.warning(t("bbox.rectTooSmall"));
        return;
      }

      const updatedRects = [...measureRects, newRect];
      setMeasureRects(updatedRects);
      setUndoStack(prev => [...prev, { boxes: boundingBoxes, lines, measureRects }]);

      try {
        await savePhotoResults(boundingBoxes, {
          lines,
          measureRects: updatedRects,
        });
        message.success(t("bbox.rectSaved"));
      } catch (e) {
        console.error("bbox.rectSaveFailed", e);
        message.error(t("bbox.rectangleSaveFailed"));
      }
    } else if (mode === "edit" && shiftPressed && newLineStart) {
      const finalEnd = newLineEnd || newLineStart;
      const distance = Math.sqrt(
        Math.pow(finalEnd.x - newLineStart.x, 2) +
        Math.pow(finalEnd.y - newLineStart.y, 2)
      );

      const newLine = {
        id: Date.now(),
        start: newLineStart,
        end: finalEnd,
        distance: distance.toFixed(2),
      };

      const updatedLines = [...lines, newLine];
      setLines(updatedLines);
      setUndoStack(prev => [...prev, { boxes: boundingBoxes, lines, measureRects }]);
      setNewLineStart(null);
      setNewLineEnd(null);
      setDrawingMode("none");

      try {
        await savePhotoResults(boundingBoxes, { lines: updatedLines, measureRects });
        message.success(t("bbox.lineSaved"));
      } catch (e) {
        console.error(t("bbox.lineSaveError"), e);
        message.error(t("bbox.lineSaveFailed"));
      }

    } else {
      setIsDragging(false);
      setDrawingMode("none");
    }
  };


  // ホイールでズーム
  // カーソル位置を基準にズーム
  const handleWheel = (e) => {
    e.preventDefault();

    if (!viewportRef.current) return;

    // 1) 拡大率の決定（ホイール上：拡大、下：縮小）
    const scaleStep = 1.2;                 // お好みで 1.05～1.5
    const factor = e.deltaY < 0 ? scaleStep : 1 / scaleStep;

    const min = 1;                          // 最小倍率
    const max = 50;                         // 最大倍率
    const newZoom = Math.min(max, Math.max(min, zoomLevel * factor));
    if (newZoom === zoomLevel) return;

    // 2) ビューポート内のカーソル位置（スクリーン → 要素ローカル）
    const rect = viewportRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left - 500;
    const sy = e.clientY - rect.top - 500;

    // 3) ズーム前の画像座標（ワールド座標）を計算
    //    画面座標 = 画像座標 * zoom + translate
    const ix = (sx - currentPosition.x) / zoomLevel;
    const iy = (sy - currentPosition.y) / zoomLevel;

    // 4) ズーム後も (ix,iy) が同じ画面位置 (sx,sy) に来るように平行移動を再計算
    const newPosX = sx - ix * newZoom;
    const newPosY = sy - iy * newZoom;

    setZoomLevel(newZoom);
    setCurrentPosition({ x: newPosX, y: newPosY });
  };


  const handleOpenColorChange = (color) => {
    setOpenColor(color.hex);
  };

  const handleCloseColorChange = (color) => {
    setCloseColor(color.hex);
  };

  const exportForImageJ = async () => {
    if (!selectedPhoto || imageJExportLock.current || polygonSavingCount > 0 || savingBoxKey != null) return;
    imageJExportLock.current = true;
    setImageJExporting(true);
    const photo = { ...selectedPhoto };
    const boxes = JSON.parse(JSON.stringify(boundingBoxes));
    try {
      const image = await photoImageCacheRef.current.load(buildPhotoImageUrl(photo));
      const blob = await createImageJZip({ image, photo, boxes, measure: measurePoreRegion });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${String(photo.photopath || "stomata").replace(/[^a-zA-Z0-9_.-]/g, "_")}_ImageJ.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      message.success(t("bbox.imageJ.success"));
    } catch (error) {
      console.error("ImageJ export failed", error);
      message.error(t("bbox.imageJ.error"));
    } finally {
      imageJExportLock.current = false;
      setImageJExporting(false);
    }
  };

  const downloadWithBoundingBox = async () => {
    if (!selectedPhoto) return;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = actualWidth;
    canvas.height = actualHeight;

    try {
      const image = await photoImageCacheRef.current.load(buildPhotoImageUrl(selectedPhoto));
      ctx.drawImage(image, 0, 0, actualWidth, actualHeight);

      if (drawBoundingBox) {
        boundingBoxes.forEach((box) => {
          const color = box.title === "open" ? openColor : closeColor;
          ctx.strokeStyle = color;
          ctx.lineWidth = boxShadowWidth * 5;
          ctx.strokeRect(box.x, box.y, box.width, box.height);

          if (drawText) {
            ctx.fillStyle = color;
            ctx.font = "16px Arial";

            if (drawId) {
              const idLabel = box.id && box.id !== "" ? box.id : box.key + 1;
              ctx.fillText(idLabel, box.x + box.width - 15, box.y + box.height - 5);
            }

            if (drawText) {
              ctx.fillText((box.aperture ?? 0).toFixed(2) + "µm", box.x, box.y - 5);
            }
          }
        });
      }
      if (drawScaleBar) {
        if (lines.length > 0) {
          ctx.strokeStyle = lineColor;
          ctx.fillStyle = lineColor;
          ctx.lineWidth = lineWidth;

          const micronPerPixel = MICRON_PER_PIXEL; // ✅ ← 固定値に！

          lines.forEach((line) => {
            const startX = line.start.x;
            const startY = line.start.y;
            const endX = line.end.x;
            const endY = line.end.y;

            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();

            const labelX = (startX + endX) / 2;
            const labelY = (startY + endY) / 2 - 10;

            const labelText = `${line.distance} px / ${(line.distance * micronPerPixel).toFixed(2)} µm`;
            ctx.font = "20px Arial";
            ctx.fillText(labelText, labelX, labelY);
          });
        }

        if (measureRects.length > 0) {
          ctx.strokeStyle = lineColor;
          ctx.fillStyle = lineColor;
          ctx.lineWidth = lineWidth;
          ctx.font = "20px Arial";

          measureRects.forEach((rect) => {
            const x = toFiniteNumber(rect.x, 0);
            const y = toFiniteNumber(rect.y, 0);
            const width = toFiniteNumber(rect.width, 0);
            const height = toFiniteNumber(rect.height, 0);

            ctx.strokeRect(x, y, width, height);
            ctx.fillText(
              `${t("bbox.horizontal")} ${width.toFixed(2)} px / ${(width * MICRON_PER_PIXEL).toFixed(2)} µm`,
              x + width / 2 - 80,
              y - 10
            );
            ctx.fillText(
              `${t("bbox.vertical")} ${height.toFixed(2)} px / ${(height * MICRON_PER_PIXEL).toFixed(2)} µm`,
              x + 8,
              y + height / 2
            );
          });
        }


      }


      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/jpeg");
      link.download = selectedPhoto.photopath;
      link.click();
    } catch (error) {
      if (error?.code === "IMAGE_CACHE_CLEARED") return;
      console.error("画像出力エラー:", error);
      message.error(t("bbox.imageLoadFailed"));
    }
  };

  // Legacy hover preview helper. 現在はhoverから呼ばず、互換用に保持。
  const extractBoxImage = async (box) => {
    if (!selectedPhoto || !box || polygonImageOpenRef.current) return;
    const url = buildPhotoImageUrl(selectedPhoto);
    if (!url) return;
    const requestId = ++hoverImageRequestRef.current;
    try {
      const img = await photoImageCacheRef.current.load(url);
      if (
        requestId !== hoverImageRequestRef.current ||
        activePhotoImageUrlRef.current !== url ||
        polygonImageOpenRef.current
      ) return;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(box.width));
      canvas.height = Math.max(1, Math.round(box.height));
      canvas.getContext("2d").drawImage(img, box.x, box.y, box.width, box.height, 0, 0, canvas.width, canvas.height);
      setPreviewImage(canvas.toDataURL("image/png"));
    } catch (error) {
      if (requestId === hoverImageRequestRef.current && error?.code !== "IMAGE_CACHE_CLEARED") {
        console.warn("Hover crop unavailable", error);
      }
    }
  };

  const getNextId = () => {
    const ids = boundingBoxes.map(b => Number(b.id ?? b.key)).filter(n => !isNaN(n));
    return ids.length > 0 ? Math.max(...ids) + 1 : 0;
  };

  useEffect(() => {
    const box = boundingBoxes.find(b => b.key === selectedBoxKey);
    if (box && selectedPhoto?.lines) {
      setLines(selectedPhoto.lines);
    }
  }, [selectedBoxKey, selectedPhoto]);

  useEffect(() => {
    if (!selectedPhoto) return;

    const results = selectedPhoto.results || [];
    const linesFromPhoto = selectedPhoto.lines || [];
    const rectsFromPhoto = (selectedPhoto.measureRects || [])
      .map(normalizeMeasureRect)
      .filter(Boolean);

    const boxes = results
      .filter(item => item.location && item.location.left != null)
      .map((item, index) => {
        const id = item.id !== undefined && item.id !== "" ? Number(item.id) : index;
        return {
          key: id,
          id: id.toString(),
          x: item.location.left,
          y: item.location.top,
          width: item.location.right - item.location.left,
          height: item.location.bottom - item.location.top,
          title: item.title || "Unknown",
          aperture: item.aperture,
          long: item.axisLong,
          short: item.axisShort,
          area: item.area ?? 0,
          idClass: item.idClass ?? 0,
          stability: item.stability ?? 0, // ←これが必要！
          scoreThreshold: item.scoreThreshold ?? 0,
          poreContour: Array.isArray(item.poreContour) ? item.poreContour : [],
          poreCrop: item.poreCrop ?? null,
          manualPoreContour: Array.isArray(item.manualPoreContour) ? item.manualPoreContour : [],
          manualPoreCrop: item.manualPoreCrop ?? null,
            measurementMethod: item.measurementMethod ?? null,
            legacyMeasurement: item.legacyMeasurement ?? null,
          contourSource: item.contourSource ||
            (Array.isArray(item.manualPoreContour) && item.manualPoreContour.length >= 3 ? "manual" : "ai"),
          contourEditedAt: item.contourEditedAt ?? null,
        };
      }).map(recalculateLoadedPore);

    setBoundingBoxes(boxes);
    setMeasureRects(rectsFromPhoto);
    setOriginalMeasureRects(rectsFromPhoto);


    // 🎯 ここがポイント！
    if (!linesFromPhoto || linesFromPhoto.length === 0) {
      setLines([
        {
          id: Date.now(),
          start: { x: 20, y: 2122 },
          end: { x: 42.22, y: 2122 },
          distance: "22.22",
          isScale: true,
        },
      ]);
    } else {
      setLines(linesFromPhoto);
    }
  }, [selectedPhoto]);

  useEffect(() => {
    const loadUserSettings = async () => {
      if (!user) return;
      const settingsRef = doc(db, user.email, "_settings");
      const snap = await getDoc(settingsRef);
      if (snap.exists()) {
        const s = snap.data();
        setDrawBoundingBox(s.drawBoundingBox ?? true);
        setDrawText(s.drawText ?? true);
        setDrawId(s.drawId ?? true);
        setDrawScaleBar(s.drawScaleBar ?? true);
        setOpenColor(s.openColor ?? "#ff0000");
        setCloseColor(s.closeColor ?? "#0000ff");
        setBoxShadowWidth(s.boxShadowWidth ?? 1.0);
      }
    };
    loadUserSettings();
  }, [user]);

  const handleSaveAllChanges = async () => {
    if (!selectedPhoto) {
      alert(t("bbox.noPhotoSelected"));
      return;
    }

    try {
      await savePhotoResults(boundingBoxes, { lines, measureRects });

      alert(t("bbox.changesSaved"));

      await logBoundingBoxAction({
        email: user.email,
        photopath: selectedPhoto.photopath,
        cultiverName: selectedPhoto.cultiverName,
        boxId: "-", // 特定のIDでない場合
        action: "manual save (Ctrl+S)",
      });
      // 🔽 これを try-catch でラップする！
      try {
        refetch();
      } catch (e) {
        console.warn("一覧リフレッシュ失敗:", e);
      }

    } catch (e) {
      console.error("保存エラー:", e);
      alert(t("bbox.changesSaveFailed"));
    }
  };

  useEffect(() => {
    const preventContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", preventContextMenu);
    return () => {
      window.removeEventListener("contextmenu", preventContextMenu);
    };
  }, []);

  const exportCroppedBoxesAsOneImage = async () => {
    if (!selectedPhoto || boundingBoxes.length === 0) return;

    try {
      const image = await photoImageCacheRef.current.load(buildPhotoImageUrl(selectedPhoto));
      const margin = 10; // 各画像間の余白
      const boxSize = 96 * 8; // 切り出し画像の表示サイズ（縮小）

      const columns = 5; // 横に並べる個数（自由に調整）
      const rows = Math.ceil(boundingBoxes.length / columns);
      const canvasWidth = columns * (boxSize + margin) + margin;
      const canvasHeight = rows * (boxSize + margin) + margin;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      boundingBoxes.forEach((box, i) => {
        const row = Math.floor(i / columns);
        const col = i % columns;
        const x = col * (boxSize + margin) + margin;
        const y = row * (boxSize + margin) + margin;

        ctx.drawImage(
          image,
          box.x,
          box.y,
          box.width,
          box.height,
          x,
          y,
          boxSize,
          boxSize
        );

        // IDラベルを描く（任意）
        if (drawId) {
          ctx.fillStyle = "black";
          ctx.font = "100px Arial";
          ctx.fillText(`ID:${box.id}`, x, y + 100);
        }

        // タイトルラベルを描く（任意）   
        ctx.fillStyle = "black";
        ctx.font = "100px Arial";
        ctx.fillText(`${box.title}`, x, y + 200);
      });

      // ダウンロード処理
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${selectedPhoto.photopath}_BB集.png`;
      link.click();
    } catch (error) {
      if (error?.code === "IMAGE_CACHE_CLEARED") return;
      console.error("切り出し画像出力エラー:", error);
      message.error(t("bbox.imageLoadFailed"));
    }
  };

  const centerOnBox = (box) => {
    if (!box) return;

    const boxCenterX = box.x + box.width / 2;
    const boxCenterY = box.y + box.height / 2;

    // ⭐ 強制的にズームを10倍（1000%）に変更
    const targetZoom = 8;
    setZoomLevel(targetZoom);

    // 表示サイズの中心
    const displayCenterX = displayWidth / 2.08;
    const displayCenterY = displayHeight / 1.9;

    // 新しいパン位置を計算（ズームレベル込みで）
    const dx = ((boxCenterX * displayWidth / actualWidth
    ) - displayCenterX) * targetZoom;
    const dy = ((boxCenterY * displayHeight / actualHeight) - displayCenterY) * targetZoom;

    setCurrentPosition({
      x: -dx,
      y: -dy,
    });
  };
  const resetZoomAndPosition = () => {
    setZoomLevel(1);
    setCurrentPosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "v") {
        setMode("view");
      } else if (e.key === "e") {
        setMode("edit");
      } else if (e.key === "s" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault(); // ← optional
        setMode("setting");
      } else if (e.key === "Escape") {
        resetZoomAndPosition();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const calculatePolygonArea = (points) => measurePoreRegion(points)?.area ?? 0;

  const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

  const getAutoContourTargetPointCount = () => Math.max(
    MIN_AUTO_CONTOUR_POINTS,
    Math.min(MAX_AUTO_CONTOUR_POINTS, Number(autoContourPointCount) || DEFAULT_AUTO_CONTOUR_POINTS)
  );

  const isNearCanvasEdge = (x, y, width, height, margin = AUTO_CONTOUR_EDGE_MARGIN_PX) => (
    x <= margin ||
    y <= margin ||
    x >= width - 1 - margin ||
    y >= height - 1 - margin
  );

  const getMedianNumber = (values, fallback = 0) => {
    const finiteValues = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    if (finiteValues.length === 0) return fallback;

    const mid = Math.floor(finiteValues.length / 2);
    return finiteValues.length % 2 === 0
      ? (finiteValues[mid - 1] + finiteValues[mid]) / 2
      : finiteValues[mid];
  };

  const getPercentileNumber = (values, percentile, fallback = 0) => {
    const finiteValues = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    if (finiteValues.length === 0) return fallback;
    if (finiteValues.length === 1) return finiteValues[0];

    const p = clampNumber(percentile, 0, 1);
    const rawIndex = (finiteValues.length - 1) * p;
    const lower = Math.floor(rawIndex);
    const upper = Math.ceil(rawIndex);
    const weight = rawIndex - lower;

    return finiteValues[lower] * (1 - weight) + finiteValues[upper] * weight;
  };

  const getBinaryMaskFromCanvas = (overrideThreshold = null) => {
    const canvas = polygonCanvasRef.current;
    if (!canvas || !cachedImage) return null;

    const off = document.createElement("canvas");
    off.width = canvas.width;
    off.height = canvas.height;

    const ctx = off.getContext("2d");
    ctx.drawImage(cachedImage, 0, 0, off.width, off.height);

    const imageData = ctx.getImageData(0, 0, off.width, off.height);
    const { data, width, height } = imageData;
    const mask = new Uint8Array(width * height);

    const threshold = clampNumber(
      overrideThreshold != null ? Number(overrideThreshold) : Number(binaryThreshold),
      0,
      255
    );

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a === 0) continue;

      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      const isForeground = binaryInvert ? gray < threshold : gray >= threshold;
      mask[i / 4] = isForeground ? 1 : 0;
    }

    return { mask, width, height };
  };

  const findNearestForegroundPixel = (
    mask,
    width,
    height,
    startX,
    startY,
    maxRadius = 80
  ) => {
    if (
      startX >= 0 &&
      startX < width &&
      startY >= 0 &&
      startY < height &&
      mask[startY * width + startX] === 1
    ) {
      return { x: startX, y: startY };
    }

    for (let r = 1; r <= maxRadius; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const x = startX + dx;
          const y = startY + dy;
          if (x < 0 || x >= width || y < 0 || y >= height) continue;
          if (mask[y * width + x] === 1) {
            return { x, y };
          }
        }
      }
    }

    return null;
  };

  const getExpectedAutoContourMaxRadiusPx = (width, height) => {
    const canvasMaxRadius = Math.min(width, height) * AUTO_CONTOUR_MAX_RADIUS_RATIO;
    const box = boundingBoxes.find((b) => b.key === selectedBoxKey);
    const logicalCandidates = [box?.long, box?.width, box?.height]
      .map((value) => toFiniteNumber(value, 0))
      .filter((value) => value > 0);

    if (logicalCandidates.length === 0) {
      return canvasMaxRadius;
    }

    const expectedLogicalLong = Math.max(...logicalCandidates);
    const expectedRadius = expectedLogicalLong * POLYGON_SCALE_FACTOR * 0.75;

    return clampNumber(expectedRadius, 90, canvasMaxRadius);
  };

  const refineAutoContourCenter = (mask, width, height, seed, maxRayRadius) => {
    const radius = clampNumber(
      Math.min(width, height) * AUTO_CONTOUR_CENTER_REFINE_RADIUS_RATIO,
      70,
      Math.max(90, maxRayRadius * 0.7)
    );
    const radiusSq = radius ** 2;
    const minX = Math.max(AUTO_CONTOUR_EDGE_MARGIN_PX, Math.floor(seed.x - radius));
    const maxX = Math.min(width - 1 - AUTO_CONTOUR_EDGE_MARGIN_PX, Math.ceil(seed.x + radius));
    const minY = Math.max(AUTO_CONTOUR_EDGE_MARGIN_PX, Math.floor(seed.y - radius));
    const maxY = Math.min(height - 1 - AUTO_CONTOUR_EDGE_MARGIN_PX, Math.ceil(seed.y + radius));

    let sumX = 0;
    let sumY = 0;
    let weightSum = 0;
    let count = 0;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (mask[y * width + x] !== 1) continue;

        const dx = x - seed.x;
        const dy = y - seed.y;
        const distSq = dx ** 2 + dy ** 2;
        if (distSq > radiusSq) continue;

        // 近い画素をやや強く見る。背景側へ連結している場合でも中心が飛びにくい。
        const dist = Math.sqrt(distSq);
        const weight = 1 / (1 + dist / radius);
        sumX += x * weight;
        sumY += y * weight;
        weightSum += weight;
        count += 1;
      }
    }

    if (count < 20 || weightSum === 0) return seed;

    const centroid = { x: sumX / weightSum, y: sumY / weightSum };
    const dx = centroid.x - seed.x;
    const dy = centroid.y - seed.y;
    const dist = Math.sqrt(dx ** 2 + dy ** 2);
    const maxShift = radius * 0.45;

    if (dist > maxShift && dist > 0) {
      return {
        x: seed.x + (dx / dist) * maxShift,
        y: seed.y + (dy / dist) * maxShift,
      };
    }

    return centroid;
  };

  const buildEllipseClosedContourFromSeed = (mask, width, height, seed, targetPointCount, maxRayRadius, options = {}) => {
    const { allowElongated = false } = options;
    const center = refineAutoContourCenter(mask, width, height, seed, maxRayRadius);
    const rayCount = Math.max(targetPointCount * 2, 96);
    const radii = new Array(rayCount).fill(null);
    const validRadii = [];
    let clippedRayCount = 0;

    for (let i = 0; i < rayCount; i++) {
      const angle = (2 * Math.PI * i) / rayCount;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      let seenForeground = false;
      let lastForegroundRadius = 0;
      let backgroundRun = 0;
      let closedByBackground = false;
      let clippedByLimit = false;

      for (let r = 0; r <= maxRayRadius; r += AUTO_CONTOUR_RAY_STEP_PX) {
        const x = Math.round(center.x + cos * r);
        const y = Math.round(center.y + sin * r);

        if (
          x < 0 ||
          x >= width ||
          y < 0 ||
          y >= height ||
          isNearCanvasEdge(x, y, width, height)
        ) {
          clippedByLimit = true;
          break;
        }

        const foreground = mask[y * width + x] === 1;
        if (foreground) {
          seenForeground = true;
          lastForegroundRadius = r;
          backgroundRun = 0;
        } else if (seenForeground && r >= AUTO_CONTOUR_MIN_RAY_RADIUS_PX) {
          backgroundRun += 1;
          if (backgroundRun >= 3) {
            closedByBackground = true;
            break;
          }
        }
      }

      if (
        seenForeground &&
        closedByBackground &&
        !clippedByLimit &&
        lastForegroundRadius >= AUTO_CONTOUR_MIN_RAY_RADIUS_PX
      ) {
        radii[i] = lastForegroundRadius;
        validRadii.push(lastForegroundRadius);
      } else {
        clippedRayCount += 1;
      }
    }

    if (validRadii.length < Math.max(10, Math.floor(rayCount * 0.18))) {
      return null;
    }

    const medianRadius = getMedianNumber(validRadii, maxRayRadius * 0.4);
    const p25Radius = getPercentileNumber(validRadii, 0.25, medianRadius);
    const p90Radius = getPercentileNumber(validRadii, 0.90, medianRadius);
    const p95Radius = getPercentileNumber(validRadii, 0.95, p90Radius);
    const elongatedScore = p90Radius / Math.max(1, p25Radius);
    const validRayRatio = validRadii.length / rayCount;

    // 長細い補正は、通常の自動抽出では使わない。
    // ON時でも、レイで実際に観測できた半径だけを伸ばし、背景マスク全体へ広がらないようにする。
    const elongatedLike =
      allowElongated &&
      validRayRatio >= AUTO_CONTOUR_ELONGATED_RAY_MIN_VALID_RATIO &&
      elongatedScore >= AUTO_CONTOUR_ELONGATED_RAY_ASPECT_THRESHOLD &&
      p90Radius >= medianRadius * 1.45;

    const minAllowedRadius = Math.max(
      AUTO_CONTOUR_MIN_RAY_RADIUS_PX,
      medianRadius * (elongatedLike ? 0.18 : 0.35)
    );
    const maxAllowedRadius = Math.min(
      maxRayRadius,
      elongatedLike
        ? Math.max(p95Radius * 1.08, medianRadius * AUTO_CONTOUR_ELONGATED_RAY_RADIUS_MULTIPLIER)
        : medianRadius * AUTO_CONTOUR_MAX_RADIUS_MULTIPLIER
    );

    let filledRadii = radii.map((radius, i) => {
      if (Number.isFinite(radius)) {
        return clampNumber(radius, minAllowedRadius, maxAllowedRadius);
      }

      const opposite = radii[(i + Math.floor(rayCount / 2)) % rayCount];
      if (Number.isFinite(opposite)) {
        return clampNumber(opposite, minAllowedRadius, maxAllowedRadius);
      }

      for (let offset = 1; offset < rayCount / 2; offset++) {
        const prev = radii[(i - offset + rayCount) % rayCount];
        const next = radii[(i + offset) % rayCount];
        if (Number.isFinite(prev) && Number.isFinite(next)) {
          return clampNumber((prev + next) / 2, minAllowedRadius, maxAllowedRadius);
        }
        if (Number.isFinite(prev)) return clampNumber(prev, minAllowedRadius, maxAllowedRadius);
        if (Number.isFinite(next)) return clampNumber(next, minAllowedRadius, maxAllowedRadius);
      }

      return medianRadius;
    });

    // 円形に近いがギザつく境界を少しだけ平滑化する。
    for (let round = 0; round < 2; round++) {
      filledRadii = filledRadii.map((radius, i) => {
        const prev = filledRadii[(i - 1 + rayCount) % rayCount];
        const next = filledRadii[(i + 1) % rayCount];
        return (prev + radius * 2 + next) / 4;
      });
    }

    const points = Array.from({ length: targetPointCount }, (_, i) => {
      const sourceIndex = Math.floor((i * rayCount) / targetPointCount) % rayCount;
      const angle = (2 * Math.PI * sourceIndex) / rayCount;
      const radius = filledRadii[sourceIndex];

      return {
        x: clampNumber((center.x + Math.cos(angle) * radius) / POLYGON_SCALE_FACTOR, 0, POLYGON_LOGICAL_SIZE),
        y: clampNumber((center.y + Math.sin(angle) * radius) / POLYGON_SCALE_FACTOR, 0, POLYGON_LOGICAL_SIZE),
      };
    });

    return {
      points,
      center,
      clippedRayCount,
      method: elongatedLike ? "elongated-ray-close" : "ellipse-close",
      aspect: elongatedLike ? elongatedScore : undefined,
    };
  };

  const extractAutoContourPoints = (
    seedX,
    seedY,
    overrideThreshold = null,
    overrideLongThinAssist = null
  ) => {
    const binary = getBinaryMaskFromCanvas(overrideThreshold);
    if (!binary) return null;

    const { mask, width, height } = binary;
    const targetPointCount = getAutoContourTargetPointCount();
    const maxRayRadius = getExpectedAutoContourMaxRadiusPx(width, height);

    const seed = findNearestForegroundPixel(mask, width, height, seedX, seedY, 80);
    if (!seed) return null;

    const visited = new Uint8Array(width * height);
    const queue = [seed];
    let head = 0;
    const pixels = [];
    const dirs = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];

    const maxComponentPixels = Math.floor(width * height * AUTO_CONTOUR_MAX_COMPONENT_RATIO);
    let exceededComponentLimit = false;
    let touchesCanvasEdge = false;
    let minX = seed.x;
    let maxX = seed.x;
    let minY = seed.y;
    let maxY = seed.y;

    while (head < queue.length) {
      const { x, y } = queue[head++];
      const idx = y * width + x;

      if (visited[idx]) continue;
      visited[idx] = 1;

      if (mask[idx] !== 1) continue;

      pixels.push({ x, y });
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      if (isNearCanvasEdge(x, y, width, height)) {
        touchesCanvasEdge = true;
      }

      if (pixels.length > maxComponentPixels) {
        exceededComponentLimit = true;
        break;
      }

      for (const [dx, dy] of dirs) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const nIdx = ny * width + nx;
        if (!visited[nIdx] && mask[nIdx] === 1) {
          queue.push({ x: nx, y: ny });
        }
      }
    }

    if (pixels.length < 20) return null;

    const bboxWidth = maxX - minX + 1;
    const bboxHeight = maxY - minY + 1;
    const componentTooLarge =
      exceededComponentLimit ||
      bboxWidth > maxRayRadius * 2.25 ||
      bboxHeight > maxRayRadius * 2.25;
    const allowElongated =
      overrideLongThinAssist != null
        ? overrideLongThinAssist
        : longThinContourAssistEnabled;
    // 二値化が背景とつながると、輪郭が画面端まで流れて発散する。
    // その場合は、気孔開口部がほぼ楕円である前提で、レイを飛ばして欠けた方向を補完する。
    if (touchesCanvasEdge || componentTooLarge) {
      const ellipseClosed = buildEllipseClosedContourFromSeed(
        mask,
        width,
        height,
        seed,
        targetPointCount,
        maxRayRadius,
        { allowElongated }
      );

      if (ellipseClosed?.points?.length >= 3) {
        return {
          points: ellipseClosed.points,
          pixelCount: pixels.length,
          method: ellipseClosed.method,
          clippedRayCount: ellipseClosed.clippedRayCount,
          aspect: ellipseClosed.aspect,
          edgeClosed: true,
        };
      }
    }

    // 外周候補だけ拾う
    const boundary = pixels.filter(({ x, y }) => {
      return dirs.some(([dx, dy]) => {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) return true;
        return mask[ny * width + nx] === 0;
      });
    });

    if (boundary.length < 8) return null;

    // 重心
    const cx = boundary.reduce((s, p) => s + p.x, 0) / boundary.length;
    const cy = boundary.reduce((s, p) => s + p.y, 0) / boundary.length;

    // 角度順に並べる
    boundary.sort((a, b) => {
      const aa = Math.atan2(a.y - cy, a.x - cx);
      const bb = Math.atan2(b.y - cy, b.x - cx);
      return aa - bb;
    });

    const sampleCount = Math.min(targetPointCount, boundary.length);
    const sampled = Array.from({ length: sampleCount }, (_, i) => {
      const index = Math.floor((i * boundary.length) / sampleCount);
      return boundary[index];
    });

    const points = sampled.map((p) => ({
      x: p.x / POLYGON_SCALE_FACTOR,
      y: p.y / POLYGON_SCALE_FACTOR,
    }));

    return {
      points,
      pixelCount: pixels.length,
      method: "component",
      edgeClosed: false,
    };
  };

  const updateAutoPolygonFromCenter = (
    statusText = "",
    overrideThreshold = null,
    overrideLongThinAssist = null
  ) => {
    if (!cachedImage) return;

    const contour = extractAutoContourPoints(
      Math.round(POLYGON_CANVAS_SIZE / 2),
      Math.round(POLYGON_CANVAS_SIZE / 2),
      overrideThreshold
    );

    if (contour?.points?.length >= 3) {
      const points = contour.points.map(clampPolygonPoint);
      const area = calculatePolygonArea(points);
      const ellipse = points.length >= 3 ? fitEllipsePCA(points) : null;

      setPolygonPoints(points);
      setPolygonArea(area);
      setFittedEllipse(ellipse);
      setHoverContourPoints([]);
      setHoverContourArea(null);
      setAutoContourMode(false);
      setHoverContourStatus(
        statusText || t("bbox.status.autoUpdatedFromCenter", { count: points.length })
      );
    } else {
      setPolygonPoints([]);
      setPolygonArea(null);
      setFittedEllipse(null);
      setHoverContourPoints([]);
      setHoverContourArea(null);
      setHoverContourStatus(t("bbox.status.autoPolygonFailedFromCenter"));
    }
  };

  useEffect(() => {
    if (!cachedImage) {
      clearPolygonCanvas();
    }
  }, [cachedImage, polygonModalVisible]);

  const extractCenteredBoxImage = (box, size = POLYGON_LOGICAL_SIZE) => {
    const photo = selectedPhoto;
    const requestId = polygonImageRequestIdRef.current + 1;
    polygonImageRequestIdRef.current = requestId;

    setPolygonImageLoading(true);
    setPolygonImageError(false);
    setPolygonImageStatus(t("bbox.editor.loadingSource"));
    setPreviewImage(null);
    setCachedImage(null);
    clearPolygonCanvas();

    return new Promise((resolve, reject) => {
      const imageUrl = buildPhotoImageUrl(photo);
      let croppedImg = null;
      let timeoutId = null;
      let finished = false;

      const isCurrentRequest = () =>
        polygonImageRequestIdRef.current === requestId &&
        activePhotoImageUrlRef.current === imageUrl;

      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (croppedImg) {
          croppedImg.onload = null;
          croppedImg.onerror = null;
        }
      };

      const resolveOnce = (value) => {
        if (finished) return;
        finished = true;
        cleanup();
        resolve(value);
      };

      const fail = (error) => {
        if (finished) return;

        if (!isCurrentRequest()) {
          resolveOnce(null);
          return;
        }

        finished = true;
        cleanup();

        const detail = error?.message || t("bbox.editor.loadFailed");
        setPreviewImage(null);
        setCachedImage(null);
        setPolygonImageLoading(false);
        setPolygonImageError(true);
        setPolygonImageStatus(detail);
        clearPolygonCanvas();
        reject(error instanceof Error ? error : new Error(detail));
      };

      if (!box || !imageUrl) {
        fail(new Error(t("bbox.editor.missingSource")));
        return;
      }

      timeoutId = window.setTimeout(() => {
        fail(new Error(t("bbox.editor.loadTimeout", { seconds: Math.round(POLYGON_IMAGE_LOAD_TIMEOUT_MS / 1000) })));
      }, POLYGON_IMAGE_LOAD_TIMEOUT_MS);

      const prepareCrop = (img) => {
        if (finished) return;
        if (!isCurrentRequest()) {
          resolveOnce(null);
          return;
        }

        try {
          setPolygonImageStatus(t("bbox.editor.creatingCrop"));

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          canvas.width = size * POLYGON_SCALE_FACTOR;
          canvas.height = size * POLYGON_SCALE_FACTOR;
          canvas.style.width = `${size}px`;
          canvas.style.height = `${size}px`;

          const centerX = box.x + box.width / 2;
          const centerY = box.y + box.height / 2;
          const maxCropX = Math.max(0, img.naturalWidth - size);
          const maxCropY = Math.max(0, img.naturalHeight - size);
          const cropX = Math.max(0, Math.min(maxCropX, Math.floor(centerX - size / 2)));
          const cropY = Math.max(0, Math.min(maxCropY, Math.floor(centerY - size / 2)));

          ctx.drawImage(
            img,
            cropX,
            cropY,
            size,
            size,
            0,
            0,
            canvas.width,
            canvas.height
          );

          const dataURL = canvas.toDataURL("image/png");
          croppedImg = new Image();
          croppedImg.decoding = "async";

          croppedImg.onload = () => {
            if (finished) return;
            if (!isCurrentRequest()) {
              resolveOnce(null);
              return;
            }

            setPolygonImageStatus(t("bbox.editor.updatingView"));
            setPreviewImage(dataURL);
            setCachedImage(croppedImg);
            setPolygonImageLoading(false);
            setPolygonImageError(false);
            setPolygonImageStatus("");
            resolveOnce(dataURL);
          };

          croppedImg.onerror = () => fail(new Error(t("bbox.editor.cropFailed")));
          setPolygonImageStatus(t("bbox.editor.preparingCrop"));
          croppedImg.src = dataURL;
        } catch (error) {
          fail(error);
        }
      };

      // ホバー・別の気孔・画像出力と、同じ読み込み済み元画像を共有する。
      // prepareCrop は共有 Image のイベントハンドラーを書き換えない。
      photoImageCacheRef.current.load(imageUrl).then(prepareCrop).catch(fail);
    });
  };

  // Polygon Editorは元画像上の160x160 cropを論理座標0..160で編集する。
  const getPolygonEditorCrop = (box, size = POLYGON_LOGICAL_SIZE) => {
    const centerX = toFiniteNumber(box?.x, 0) + toFiniteNumber(box?.width, 0) / 2;
    const centerY = toFiniteNumber(box?.y, 0) + toFiniteNumber(box?.height, 0) / 2;
    const maxCropX = Math.max(0, actualWidth - size);
    const maxCropY = Math.max(0, actualHeight - size);
    return {
      x: Math.max(0, Math.min(maxCropX, Math.floor(centerX - size / 2))),
      y: Math.max(0, Math.min(maxCropY, Math.floor(centerY - size / 2))),
      size,
    };
  };

  const getPreferredPoreGeometry = (box) => {
    if (Array.isArray(box?.manualPoreContour) && box.manualPoreContour.length >= 3 && box?.manualPoreCrop) {
      return { points: box.manualPoreContour, crop: box.manualPoreCrop, source: "manual" };
    }
    if (Array.isArray(box?.poreContour) && box.poreContour.length >= 3 && box?.poreCrop) {
      return { points: box.poreContour, crop: box.poreCrop, source: "ai" };
    }
    return null;
  };

  const poreGeometryToEditorPoints = (box, suppliedGeometry = null) => {
    const geometry = suppliedGeometry || getPreferredPoreGeometry(box);
    if (!geometry) return [];
    const editorCrop = getPolygonEditorCrop(box);
    const sourceCropX = toFiniteNumber(geometry.crop?.x, 0);
    const sourceCropY = toFiniteNumber(geometry.crop?.y, 0);
    const sourceCropSize = toFiniteNumber(geometry.crop?.size, 0);
    if (sourceCropSize <= 0) return [];

    return geometry.points.map((p) => {
      const originalX = sourceCropX + toFiniteNumber(p?.x, 0) * sourceCropSize;
      const originalY = sourceCropY + toFiniteNumber(p?.y, 0) * sourceCropSize;
      return {
        x: originalX - editorCrop.x,
        y: originalY - editorCrop.y,
      };
    });
  };

  const openPolygonEditorForBox = async (box) => {
    if (!box) return;
    polygonImageOpenRef.current = true;
    hoverImageRequestRef.current += 1;

    const sessionId = polygonEditorSessionRef.current + 1;
    polygonEditorSessionRef.current = sessionId;

    resetPolygonEditorState({ clearImage: true, clearSelection: false });
    setPolygonImageRetryBox(box);
    setSelectedBoxKey(box.key);
    setPolygonModalVisible(true);

    try {
      await extractCenteredBoxImage(box);
      if (sessionId !== polygonEditorSessionRef.current || !polygonImageOpenRef.current) return;

      // Android AI輪郭（または過去に人が修正した輪郭）をManual Polygonへ引き継ぐ。
      const inheritedPoints = poreGeometryToEditorPoints(box);
      if (inheritedPoints.length >= 3) {
        refreshPolygonGeometry(
          inheritedPoints,
          getPreferredPoreGeometry(box)?.source === "manual"
            ? t("bbox.editor.manualLoaded")
            : t("bbox.editor.aiLoaded"),
          { forceEllipse: true }
        );
      } else {
        setPolygonPoints([]);
        setPolygonArea(null);
        setFittedEllipse(null);
        setBoxMeasurement({ long: 0, short: 0 });
        setHoverContourStatus(t("bbox.editor.noContour"));
      }
      const aiBeforePoints = Array.isArray(box.poreContour) && box.poreContour.length >= 3 && box.poreCrop
        ? poreGeometryToEditorPoints(box, { points: box.poreContour, crop: box.poreCrop }) : inheritedPoints;
      initializePolygonHistory(inheritedPoints.length >= 3 ? inheritedPoints : [], aiBeforePoints);
      setShowPointNumbers(false);
      setShowAxisLabels(false);
      setShowMeasurementAxes(true);
      setShowMeasurementBox(false);
      setPolygonToolMode("manualPolygon");
      setBinaryPreviewEnabled(false);
      setShowPolygonOverlay(true);
      setPointInsertMode(false);
      setPointDeleteMode(false);
      setAutoContourMode(false);
    } catch (error) {
      if (sessionId !== polygonEditorSessionRef.current) return;
      console.error("ポリゴン編集用画像の読み込み失敗:", error);
      message.error(t("bbox.editor.loadFailed"));
    }
  };

  useEffect(() => {
    const imageCache = photoImageCacheRef.current;
    // アカウント変更の cleanup 後も、このレンダーの画像識別子を復元する。
    activePhotoImageUrlRef.current = buildPhotoImageUrl(selectedPhoto);
    return () => {
      hoverImageRequestRef.current += 1;
      polygonImageRequestIdRef.current += 1;
      polygonEditorSessionRef.current += 1;
      polygonImageOpenRef.current = false;
      activePhotoImageUrlRef.current = "";
      // 画面を離れたとき・アカウント変更時に、メモリ上の画像参照だけを解放。
      // Firestore / Storage のデータや画像は削除しない。
      imageCache.clear();
    };
  }, [user?.uid, user?.email]);

  const clampPolygonPoint = (point) => ({
    x: Math.max(0, Math.min(POLYGON_LOGICAL_SIZE, toFiniteNumber(point?.x, 0))),
    y: Math.max(0, Math.min(POLYGON_LOGICAL_SIZE, toFiniteNumber(point?.y, 0))),
  });

  const refreshPolygonGeometry = (nextPoints, statusText = "", options = {}) => {
    const { forceEllipse = true } = options;
    const safePoints = (Array.isArray(nextPoints) ? nextPoints : []).map(p => ({ x: p.x, y: p.y }));
    const nextArea = safePoints.length >= 3 ? calculatePolygonArea(safePoints) : null;

    setPolygonPoints(safePoints);
    setPolygonArea(nextArea);
    setHoverContourPoints([]);
    setHoverContourArea(null);

    if (safePoints.length >= 3 && forceEllipse) {
      const ellipse = fitEllipsePCA(safePoints);
      setFittedEllipse(ellipse);
      if (!ellipse) {
        setBoxMeasurement({ long: 0, short: 0 });
      }
    } else if (safePoints.length < 3) {
      setFittedEllipse(null);
      setBoxMeasurement({ long: 0, short: 0 });
    }

    if (statusText != null) {
      setHoverContourStatus(statusText);
    }

    return safePoints;
  };

  const getNearestPolygonPointIndex = (x, y, maxDistance = POLYGON_POINT_DELETE_HIT_RADIUS) => {
    let nearestIndex = null;
    let nearestDistance = Infinity;

    polygonPoints.forEach((pt, idx) => {
      const dist = Math.sqrt((pt.x - x) ** 2 + (pt.y - y) ** 2);
      if (dist <= maxDistance && dist < nearestDistance) {
        nearestIndex = idx;
        nearestDistance = dist;
      }
    });

    return nearestIndex;
  };

  const getDistanceToSegment = (point, start, end) => {
    const vx = end.x - start.x;
    const vy = end.y - start.y;
    const wx = point.x - start.x;
    const wy = point.y - start.y;
    const lenSq = vx * vx + vy * vy;

    if (lenSq <= 0.0001) {
      return {
        distance: Math.sqrt((point.x - start.x) ** 2 + (point.y - start.y) ** 2),
        projection: { ...start },
        t: 0,
      };
    }

    const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / lenSq));
    const projection = {
      x: start.x + t * vx,
      y: start.y + t * vy,
    };

    return {
      distance: Math.sqrt((point.x - projection.x) ** 2 + (point.y - projection.y) ** 2),
      projection,
      t,
    };
  };

  const getNearestPolygonSegmentInfo = (x, y, maxDistance = POLYGON_EDGE_INSERT_HIT_RADIUS) => {
    if (polygonPoints.length < 2) return null;

    const target = { x, y };
    const closeLoop = polygonPoints.length >= 3;
    const segmentCount = closeLoop ? polygonPoints.length : polygonPoints.length - 1;
    let best = null;

    for (let i = 0; i < segmentCount; i += 1) {
      const j = (i + 1) % polygonPoints.length;
      const result = getDistanceToSegment(target, polygonPoints[i], polygonPoints[j]);

      if (result.distance <= maxDistance && (!best || result.distance < best.distance)) {
        best = {
          startIndex: i,
          endIndex: j,
          insertIndex: i + 1,
          distance: result.distance,
          point: clampPolygonPoint(result.projection),
        };
      }
    }

    return best;
  };

  // Shared hit testing: an existing vertex always wins over adjacent edges.
  const getPolygonPointerTarget = (x, y, explicitInsert = false) => {
    const pointIndex = getNearestPolygonPointIndex(x, y, POLYGON_POINT_DRAG_HIT_RADIUS);
    return {
      pointIndex,
      segment: pointIndex == null ? getNearestPolygonSegmentInfo(
        x, y, explicitInsert ? POLYGON_EDGE_INSERT_CLICK_RADIUS : POLYGON_EDGE_INSERT_HIT_RADIUS
      ) : null,
    };
  };

  const insertPolygonPointAtSegment = (segment) => {
    if (!segment || !Array.isArray(polygonPoints) || polygonPoints.length < 2) {
      setHoverContourStatus(t("bbox.editor.noEdge"));
      return false;
    }

    const insertIndex = Math.max(0, Math.min(polygonPoints.length, segment.insertIndex));
    const insertPoint = clampPolygonPoint(segment.point);
    const nextPoints = [...polygonPoints];
    nextPoints.splice(insertIndex, 0, insertPoint);

    refreshPolygonGeometry(
      nextPoints,
      t("bbox.editor.edgeAdded", { start: segment.startIndex + 1, end: segment.endIndex + 1, count: nextPoints.length }),
      { forceEllipse: true }
    );
    setHoveredPointIndex(insertIndex);
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;

    // 追加後は「辺に点追加」モードを解除
    setPointInsertMode(false);

    setHoverContourStatus(
      t("bbox.editor.added", { index: insertIndex + 1 })
    );

    return true;
  };

  const smoothPolygonPoints = (iterations = 1) => {
    if (polygonPoints.length < 3) {
      setHoverContourStatus(t("bbox.editor.needThreeSmooth"));
      return;
    }

    let nextPoints = polygonPoints.map(clampPolygonPoint);
    const count = Math.max(1, iterations);

    for (let loop = 0; loop < count; loop += 1) {
      nextPoints = nextPoints.map((pt, idx) => {
        const prev = nextPoints[(idx - 1 + nextPoints.length) % nextPoints.length];
        const next = nextPoints[(idx + 1) % nextPoints.length];
        return clampPolygonPoint({
          x: pt.x * (1 - POLYGON_SMOOTHING_ALPHA) + ((prev.x + next.x) / 2) * POLYGON_SMOOTHING_ALPHA,
          y: pt.y * (1 - POLYGON_SMOOTHING_ALPHA) + ((prev.y + next.y) / 2) * POLYGON_SMOOTHING_ALPHA,
        });
      });
    }

    refreshPolygonGeometry(
      nextPoints,
      t("bbox.editor.smoothed", { count: nextPoints.length }),
      { recalcEllipse: fittedEllipse != null }
    );
    setHoveredPointIndex(null);
    setHoveredSegmentInfo(null);
  };

  const removePolygonPointAtIndex = (pointIndex) => {
    if (pointIndex == null || pointIndex < 0 || pointIndex >= polygonPoints.length) {
      setHoverContourStatus(t("bbox.editor.noPoint"));
      return;
    }

    const nextPoints = polygonPoints.filter((_, idx) => idx !== pointIndex);
    refreshPolygonGeometry(
      nextPoints,
      t("bbox.editor.deleted", { index: pointIndex + 1, count: nextPoints.length }),
      { recalcEllipse: fittedEllipse != null }
    );
    setHoveredPointIndex(null);
    setHoveredSegmentInfo(null);
  };

  const handlePolygonCanvasMouseDown = (e) => {
    if (polygonImageLoading || polygonImageError || !cachedImage) return;

    if (e.button === 2 || e.button === 1 || (e.button === 0 && polygonImagePanMode)) {
      startPolygonImagePan(e);
      return;
    }

    if (polygonCompareMode === "before" || e.button !== 0 || autoContourMode || pointDeleteMode) return;

    const pointer = getPolygonCanvasPointer(e);
    if (!pointer) return;

    if (e.altKey) return; // Leave Alt-click deletion to the click handler.
    const target = getPolygonPointerTarget(pointer.x, pointer.y, pointInsertMode || e.shiftKey);
    const targetIndex = target.pointIndex;
    if (targetIndex == null && target.segment) {
      e.preventDefault();
      e.stopPropagation();
      if (insertPolygonPointAtSegment(target.segment)) suppressNextPolygonClickRef.current = true;
      return;
    }

    if (targetIndex == null) return;

    e.preventDefault();
    suppressNextPolygonClickRef.current = true;
    polygonDragStartRef.current = {
      index: targetIndex,
      x: pointer.x,
      y: pointer.y,
      moved: false,
    };

    setDraggingPointIndex(targetIndex);
    setHoveredPointIndex(targetIndex);
    setHoveredSegmentInfo(null);
    setShowPolygonOverlay(true);
    setHoverContourStatus(t("bbox.editor.dragHint", { index: targetIndex + 1 }));
  };

  const handlePolygonCanvasMouseMove = (e) => {
    if (polygonImageLoading || polygonImageError || !cachedImage) return;

    if (polygonImagePanning) {
      updatePolygonImagePanFromPointer(e);
      return;
    }

    const pointer = getPolygonCanvasPointer(e);
    if (!pointer) return;

    const { x, y } = pointer;

    setMouseOnPolygonCanvas({ x: Math.floor(x), y: Math.floor(y) });

    if (draggingPointIndex != null) {
      const nextPoints = polygonPoints.map((pt, idx) =>
        idx === draggingPointIndex ? clampPolygonPoint({ x, y }) : pt
      );

      const dragStart = polygonDragStartRef.current;
      if (dragStart && !dragStart.moved) {
        const movedDistance = Math.sqrt((x - dragStart.x) ** 2 + (y - dragStart.y) ** 2);
        if (movedDistance > 0.15) {
          polygonDragStartRef.current = { ...dragStart, moved: true };
        }
      }

      refreshPolygonGeometry(
        nextPoints,
        t("bbox.editor.dragging", { index: draggingPointIndex + 1 }),
        { recalcEllipse: fittedEllipse != null }
      );
      setHoveredPointIndex(draggingPointIndex);
      setHoveredSegmentInfo(null);
      return;
    }

    const target = getPolygonPointerTarget(x, y, pointInsertMode || e.shiftKey);
    const found = pointDeleteMode
      ? getNearestPolygonPointIndex(x, y) : target.pointIndex;
    setHoveredPointIndex(found);
    const segment = !autoContourMode && !pointDeleteMode && !e.altKey && found == null
      ? target.segment : null;
    hoveredSegmentInfoRef.current = segment;
    setHoveredSegmentInfo(segment);

  };

  const handlePolygonCanvasMouseUp = (e) => {
    if (polygonImagePanning) {
      e?.preventDefault?.();
      finishPolygonImagePan();
      return;
    }

    if (draggingPointIndex == null) return;

    e?.preventDefault?.();
    const moved = polygonDragStartRef.current?.moved;
    const fixedIndex = draggingPointIndex;

    setDraggingPointIndex(null);
    polygonDragStartRef.current = null;
    commitPolygonHistory();
    setHoveredSegmentInfo(null);
    setHoverContourStatus(
      moved
        ? t("bbox.editor.moved", { index: fixedIndex + 1 })
        : t("bbox.editor.selected", { index: fixedIndex + 1 })
    );
  };

  const handlePolygonCanvasLeave = () => {
    if (draggingPointIndex != null || polygonImagePanning) return;

    setHoveredPointIndex(null);
    setHoveredSegmentInfo(null);
    hoveredSegmentInfoRef.current = null;
    setMouseOnPolygonCanvas({ x: null, y: null });
  };

  const handlePolygonCanvasClick = (e) => {
    if (e.button !== 0 || polygonCompareMode === "before") return;
    if (polygonImageLoading || polygonImageError || !cachedImage) return;

    if (suppressNextPolygonClickRef.current) {
      suppressNextPolygonClickRef.current = false;
      return;
    }

    const pointer = getPolygonCanvasPointer(e);
    if (!pointer) return;

    const { x, y } = pointer;

    // 点削除モード、または Alt/Option + クリックで近くの点を削除
    if (!autoContourMode && (pointDeleteMode || e.altKey)) {
      const targetIndex = getNearestPolygonPointIndex(x, y);
      if (targetIndex == null) {
        setHoverContourStatus(t("bbox.editor.noPoint"));
        return;
      }
      removePolygonPointAtIndex(targetIndex);
      return;
    }

    if (!autoContourMode) {
      const target = getPolygonPointerTarget(x, y, pointInsertMode || e.shiftKey);
      if (target.pointIndex != null) return;
      if (target.segment) {
        insertPolygonPointAtSegment(target.segment);
        return;
      }
      if (pointInsertMode || e.shiftKey) return;
    }

    // 自動抽出モード
    if (autoContourMode) {
      if (hoverContourPoints.length >= 3) {
        const confirmedPoints = [...hoverContourPoints].map(clampPolygonPoint);
        setPolygonPoints(confirmedPoints);
        setAutoContourMode(false);
        setPointDeleteMode(false);
        setPointInsertMode(false);
        setHoveredPointIndex(null);
        setHoveredSegmentInfo(null);
        setHoverContourPoints([]);
        setHoverContourArea(null);

        const rawArea = calculatePolygonArea(confirmedPoints);
        setPolygonArea(rawArea);

        const ellipse = fitEllipsePCA(confirmedPoints);
        setFittedEllipse(ellipse);

        setHoverContourStatus(t("bbox.editor.confirmed"));
      } else {
        setHoverContourStatus(t("bbox.editor.noCandidate"));
      }
      return;
    }

    // 手動点打ちモード
    const newPoints = [...polygonPoints, clampPolygonPoint({ x, y })];
    refreshPolygonGeometry(
      newPoints,
      "",
      { recalcEllipse: fittedEllipse != null }
    );
  };

  const handlePolygonCanvasDoubleClick = (e) => {
    if (e.button !== 0 || polygonCompareMode === "before") return;
    if (polygonImageLoading || polygonImageError || !cachedImage || autoContourMode) return;

    const pointer = getPolygonCanvasPointer(e);
    if (!pointer) return;

    const targetIndex = getNearestPolygonPointIndex(pointer.x, pointer.y);
    if (targetIndex == null) return;

    e.preventDefault();
    removePolygonPointAtIndex(targetIndex);
  };

  const handlePolygonCanvasContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const toggleOverlay = (event) => {
      if (!selectedPhoto || polygonModalVisible || event.defaultPrevented || event.repeat || event.isComposing || event.keyCode === 229 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (event.target?.closest?.("input, textarea, select, button, [role='textbox'], [contenteditable]:not([contenteditable='false'])") || document.querySelector(".ant-modal-wrap:not([style*='display: none']), .ant-select-dropdown:not(.ant-select-dropdown-hidden)")) return;
      const key = event.key.toLowerCase();
      if (key !== "b" && key !== "p") return;
      event.preventDefault();
      if (key === "b") setDrawBoundingBox(value => !value);
      else setDrawPoreMask(value => !value);
    };
    window.addEventListener("keydown", toggleOverlay);
    return () => window.removeEventListener("keydown", toggleOverlay);
  }, [selectedPhoto, polygonModalVisible]);

  useEffect(() => {
    if (!polygonModalVisible) return;
    const onKey = (event) => {
      if (event.target?.closest?.("input, textarea, select, [contenteditable]:not([contenteditable='false'])")) return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key !== "z" && key !== "y") return;
      event.preventDefault();
      travelPolygonHistory(key === "y" || event.shiftKey ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [polygonModalVisible, polygonImageLoading, polygonImageError, polygonHistoryVersion]);

  useEffect(() => {
    if (!polygonModalVisible || autoContourMode) return;

    const handlePointDeleteKey = (e) => {
      if (polygonCompareMode === "before") return;
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (polygonImageLoading || polygonImageError || !cachedImage) return;
      if (hoveredPointIndex == null) return;

      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (["input", "textarea", "select"].includes(activeTag)) return;

      e.preventDefault();
      removePolygonPointAtIndex(hoveredPointIndex);
    };

    window.addEventListener("keydown", handlePointDeleteKey);
    return () => window.removeEventListener("keydown", handlePointDeleteKey);
  }, [
    polygonModalVisible,
    autoContourMode,
    polygonImageLoading,
    polygonImageError,
    cachedImage,
    hoveredPointIndex,
    polygonCompareMode,
    polygonPoints,
    fittedEllipse,
  ]);

  useEffect(() => {
    if (draggingPointIndex == null) return;

    const handleWindowMouseUp = () => {
      const fixedIndex = draggingPointIndex;
      const moved = polygonDragStartRef.current?.moved;
      setDraggingPointIndex(null);
      polygonDragStartRef.current = null;
      commitPolygonHistory();
      setHoveredSegmentInfo(null);
      setHoverContourStatus(
        moved
          ? t("bbox.editor.moved", { index: fixedIndex + 1 })
          : t("bbox.editor.selected", { index: fixedIndex + 1 })
      );
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => window.removeEventListener("mouseup", handleWindowMouseUp);
  }, [draggingPointIndex]);

  useEffect(() => {
    if (!polygonImagePanning) return;

    const handleWindowMouseMove = (e) => {
      e.preventDefault();
      updatePolygonImagePanFromPointer(e);
    };

    const handleWindowMouseUp = () => {
      finishPolygonImagePan();
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [polygonImagePanning]);

  useEffect(() => {
    const canvas = document.querySelector(".canvas-container");
    if (!canvas) return;

    const wheelHandler = (e) => {
      e.preventDefault();
      handleWheel(e); // ← これは今ある関数をそのまま使える！
    };

    canvas.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      canvas.removeEventListener("wheel", wheelHandler);
    };
  }, [handleWheel]);

  useEffect(() => {
    if (!polygonModalVisible || !fittedEllipse || polygonPoints.length < 3) {
      setBoxMeasurement((prev) =>
        prev.long === 0 && prev.short === 0 ? prev : { long: 0, short: 0 }
      );
      return;
    }

    setBoxMeasurement({ long: fittedEllipse.majorAxis, short: fittedEllipse.minorAxis });
  }, [fittedEllipse, polygonModalVisible, polygonPoints]);

  const selectedBox = boundingBoxes.find(b => b.key === selectedBoxKey);
  const beforeRegionMeasurement = useMemo(() => measurePoreRegion(beforePolygonPoints), [beforePolygonPoints]);

  const fitEllipsePCA = measurePoreRegion;
  const getRotatedBoundingBox = (points, angleRad) => {
    const cos = Math.cos(-angleRad);
    const sin = Math.sin(-angleRad);

    const rotated = points.map(({ x, y }) => ({
      x: x * cos - y * sin,
      y: x * sin + y * cos,
    }));

    const minX = Math.min(...rotated.map(p => p.x));
    const maxX = Math.max(...rotated.map(p => p.x));
    const minY = Math.min(...rotated.map(p => p.y));
    const maxY = Math.max(...rotated.map(p => p.y));

    const rotateBack = (x, y) => ({
      x: x * Math.cos(angleRad) - y * Math.sin(angleRad),
      y: x * Math.sin(angleRad) + y * Math.cos(angleRad),
    });

    const box = [
      rotateBack(minX, minY),
      rotateBack(maxX, minY),
      rotateBack(maxX, maxY),
      rotateBack(minX, maxY),
    ];

    return box;
  };

  const getBoxSideLengths = (box) => {
    if (!Array.isArray(box) || box.length < 4) {
      return { length01: 0, length03: 0 };
    }

    const dx01 = box[1].x - box[0].x;
    const dy01 = box[1].y - box[0].y;
    const dx03 = box[3].x - box[0].x;
    const dy03 = box[3].y - box[0].y;

    return {
      length01: Math.sqrt(dx01 ** 2 + dy01 ** 2),
      length03: Math.sqrt(dx03 ** 2 + dy03 ** 2),
    };
  };

  const getMeasurementBoundingBox = (points, ellipse) => {
    if (!ellipse || !Array.isArray(points) || points.length < 3) return [];

    // Display-only box: never used for measurement. No angle snapping.
    return getRotatedBoundingBox(points, ellipse.angle);
  };

  const drawBinaryMaskOverlay = (ctx, width, height, options = {}) => {
    if (!cachedImage) return 0;

    const { threshold = 175, invert = false } = options;
    const source = document.createElement("canvas");
    source.width = width;
    source.height = height;
    const sourceCtx = source.getContext("2d");
    sourceCtx.drawImage(cachedImage, 0, 0, width, height);

    const imageData = sourceCtx.getImageData(0, 0, width, height);
    const { data } = imageData;

    const overlay = document.createElement("canvas");
    overlay.width = width;
    overlay.height = height;
    const overlayCtx = overlay.getContext("2d");
    const overlayData = overlayCtx.createImageData(width, height);
    const out = overlayData.data;

    let foregroundCount = 0;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a === 0) continue;

      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      const isForeground = invert ? gray < threshold : gray >= threshold;

      if (isForeground) {
        foregroundCount += 1;
        out[i] = 0;
        out[i + 1] = 150;
        out[i + 2] = 255;
        out[i + 3] = BINARY_MASK_OVERLAY_ALPHA;
      }
    }

    overlayCtx.putImageData(overlayData, 0, 0);
    ctx.drawImage(overlay, 0, 0, width, height);
    return foregroundCount;
  };

  const computeAdaptiveThresholdFromCenter = (width, height) => {
    if (!cachedImage) return 150;

    const off = document.createElement("canvas");
    off.width = width;
    off.height = height;

    const ctx = off.getContext("2d");
    ctx.drawImage(cachedImage, 0, 0, width, height);

    const { data } = ctx.getImageData(0, 0, width, height);

    const cx = Math.floor(width / 2);
    const cy = Math.floor(height / 2);

    const radius = Math.min(width, height) * 0.15;

    const values = [];

    for (let y = cy - radius; y <= cy + radius; y++) {
      for (let x = cx - radius; x <= cx + radius; x++) {
        if (x < 0 || x >= width || y < 0 || y >= height) continue;

        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy > radius * radius) continue;

        const i = (y * width + x) * 4;
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

        values.push(gray);
      }
    }

    if (values.length === 0) return 150;

    // 中央値
    values.sort((a, b) => a - b);
    const median = values[Math.floor(values.length / 2)];

    // 明るい側を取る（開口は明るい）
    const threshold = median + 10;

    return Math.min(255, Math.max(0, threshold));
  };

  const drawCanvas = (compareMode = polygonCompareMode) => {
    const polygonPoints = compareMode === "before" ? beforePolygonPoints : currentPolygonRef.current;
    const fittedEllipse = polygonPoints.length >= 3 ? fitEllipsePCA(polygonPoints) : null;
    const canvas = polygonCanvasRef.current;
    if (!canvas || !cachedImage) return;

    const ctx = canvas.getContext("2d");
    const scaleFactor = POLYGON_SCALE_FACTOR;

    // 1. クリア
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 2. 背景画像は常に残す。二値化は抽出対象を示す半透明マスクとして重ねる。
    ctx.drawImage(cachedImage, 0, 0, canvas.width, canvas.height);

    if (binaryPreviewEnabled) {
      const count = drawBinaryMaskOverlay(ctx, canvas.width, canvas.height, {
        threshold: binaryThreshold,
        invert: binaryInvert,
      });
      setBinaryForegroundPixels((prev) => (prev === count ? prev : count));
    } else {
      setBinaryForegroundPixels((prev) => (prev === 0 ? prev : 0));
    }

    if (compareMode === "overlay" && showPolygonOverlay && beforePolygonPoints.length >= 2) {
      ctx.save();
      ctx.beginPath();
      beforePolygonPoints.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x * scaleFactor, p.y * scaleFactor);
        else ctx.lineTo(p.x * scaleFactor, p.y * scaleFactor);
      });
      ctx.closePath();
      ctx.setLineDash([10, 7]);
      ctx.strokeStyle = "#52c41a";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }
    // 3. ポリゴンと点の描画（非表示にしても点データ・計算結果は保持）
    if (showPolygonOverlay) {
      polygonPoints.forEach((pt, i) => {
        const sx = pt.x * scaleFactor;
        const sy = pt.y * scaleFactor;
        const isHovered = compareMode !== "before" && i === hoveredPointIndex;
        const isDraggingPoint = compareMode !== "before" && i === draggingPointIndex;
        const isDeleteTarget = pointDeleteMode && isHovered;

        // 点の描画
        ctx.beginPath();
        ctx.arc(
          sx,
          sy,
          isDraggingPoint
            ? POLYGON_POINT_DRAG_SIZE
            : isHovered
              ? POLYGON_POINT_HOVER_SIZE
              : POLYGON_POINT_SIZE,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = isDraggingPoint ? "#faad14" : isDeleteTarget ? "#ff4d4f" : "red";
        ctx.fill();

        if (isHovered || isDraggingPoint) {
          ctx.strokeStyle = isDeleteTarget ? "white" : isDraggingPoint ? "#fff1b8" : "yellow";
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        // 点番号
        ctx.fillStyle = "white";
        ctx.font = "12px Arial";
        if (showPointNumbers || (compareMode !== "before" && (isHovered || isDraggingPoint))) ctx.fillText(i + 1, sx + 6, sy - 6);
      });

      if (polygonPoints.length >= 2) {
        ctx.beginPath();
        polygonPoints.forEach((pt, i) => {
          const sx = pt.x * scaleFactor;
          const sy = pt.y * scaleFactor;
          if (i === 0) ctx.moveTo(sx, sy);
          else ctx.lineTo(sx, sy);
        });
        ctx.closePath();
        ctx.strokeStyle = "red";
        ctx.lineWidth = 2;
        ctx.stroke();
        // Keep the aperture unobscured by a polygon fill.
      }

      // 辺追加モード中は、追加先の辺と挿入予定位置を水色で表示
      if (compareMode !== "before" && hoveredSegmentInfo && polygonPoints.length >= 2) {
        const start = polygonPoints[hoveredSegmentInfo.startIndex];
        const end = polygonPoints[hoveredSegmentInfo.endIndex];
        const insertPoint = hoveredSegmentInfo.point;

        if (start && end && insertPoint) {
          ctx.beginPath();
          ctx.moveTo(start.x * scaleFactor, start.y * scaleFactor);
          ctx.lineTo(end.x * scaleFactor, end.y * scaleFactor);
          ctx.strokeStyle = "#13c2c2";
          ctx.lineWidth = 4;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(insertPoint.x * scaleFactor, insertPoint.y * scaleFactor, 6, 0, 2 * Math.PI);
          ctx.fillStyle = "#13c2c2";
          ctx.fill();
          ctx.strokeStyle = "white";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    // ホバー中の自動抽出候補（黄色）
    if (autoContourMode && hoverContourPoints.length >= 3) {
      ctx.beginPath();
      hoverContourPoints.forEach((pt, i) => {
        const sx = pt.x * scaleFactor;
        const sy = pt.y * scaleFactor;
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.closePath();
      ctx.strokeStyle = "yellow";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "rgba(255, 255, 0, 0.15)";
      ctx.fill();
    }

    // 計測軸をポリゴン内部へ表示する。外接ボックスは必要時のみ表示。
    if (showPolygonOverlay && showMeasurementAxes && fittedEllipse && polygonPoints.length >= 3) {
      const box = getMeasurementBoundingBox(polygonPoints, fittedEllipse);
      if (Array.isArray(box) && box.length >= 4) {

        // 外接ボックス（黄色）はデフォルト非表示。
        if (showMeasurementBox) {
          ctx.beginPath();
          box.forEach((pt, i) => {
            const sx = pt.x * scaleFactor;
            const sy = pt.y * scaleFactor;
            if (i === 0) ctx.moveTo(sx, sy);
            else ctx.lineTo(sx, sy);
          });
          ctx.closePath();
          ctx.strokeStyle = "#ffd666";
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        const center = fittedEllipse.center;
        const longAxis = {
          x: Math.cos(fittedEllipse.angle), y: Math.sin(fittedEllipse.angle),
          length: fittedEllipse.majorAxis,
        };
        const shortAxis = {
          x: -Math.sin(fittedEllipse.angle), y: Math.cos(fittedEllipse.angle),
          length: fittedEllipse.minorAxis,
        };

        const drawDoubleArrowAxis = (axis, color, label, labelOffset = 0) => {
          const centerX = center.x * scaleFactor;
          const centerY = center.y * scaleFactor;
          const halfLength = (axis.length * scaleFactor) / 2;
          const startX = centerX - axis.x * halfLength;
          const startY = centerY - axis.y * halfLength;
          const endX = centerX + axis.x * halfLength;
          const endY = centerY + axis.y * halfLength;

          ctx.save();
          ctx.strokeStyle = color;
          ctx.fillStyle = color;
          ctx.lineWidth = 2;
          ctx.lineCap = "round";
          ctx.shadowColor = "rgba(0,0,0,0.65)";
          ctx.shadowBlur = 2;

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();

          const arrowSize = 8;
          const drawArrowHead = (x, y, directionX, directionY) => {
            const angle = Math.atan2(directionY, directionX);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(
              x - arrowSize * Math.cos(angle - Math.PI / 6),
              y - arrowSize * Math.sin(angle - Math.PI / 6)
            );
            ctx.moveTo(x, y);
            ctx.lineTo(
              x - arrowSize * Math.cos(angle + Math.PI / 6),
              y - arrowSize * Math.sin(angle + Math.PI / 6)
            );
            ctx.stroke();
          };

          drawArrowHead(endX, endY, axis.x, axis.y);
          drawArrowHead(startX, startY, -axis.x, -axis.y);

          if (!showAxisLabels) { ctx.restore(); return; }
          const normalX = -axis.y;
          const normalY = axis.x;
          const labelX = centerX + normalX * labelOffset;
          const labelY = centerY + normalY * labelOffset;

          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const metrics = ctx.measureText(label);
          const paddingX = 4;
          const paddingY = 2;
          const labelHeight = 15;
          ctx.shadowBlur = 0;
          ctx.fillStyle = "rgba(0,0,0,0.58)";
          ctx.fillRect(
            labelX - metrics.width / 2 - paddingX,
            labelY - labelHeight / 2 - paddingY,
            metrics.width + paddingX * 2,
            labelHeight + paddingY * 2
          );
          ctx.fillStyle = color;
          ctx.fillText(label, labelX, labelY);
          ctx.restore();
        };

        // 軸長は外側ではなく、開口部の中に双方向矢印で表示する。
        drawDoubleArrowAxis(longAxis, "#ffffff", `↔ L ${longAxis.length.toFixed(2)} px`, -14);
        drawDoubleArrowAxis(shortAxis, "#69c0ff", `↔ S ${shortAxis.length.toFixed(2)} px`, 14);
      }
    }
  };

  useEffect(() => {
    drawCanvas();
  }, [
    polygonCompareMode, beforePolygonPoints, showPointNumbers, showAxisLabels, showMeasurementAxes,
    polygonPoints,
    cachedImage,
    binaryPreviewEnabled,
    binaryThreshold,
    binaryInvert,
    hoverContourPoints,
    autoContourMode,
    fittedEllipse,
    showPolygonOverlay,
    showMeasurementBox,
    hoveredPointIndex,
    pointDeleteMode,
    pointInsertMode,
    draggingPointIndex,
    hoveredSegmentInfo
  ]);

  useEffect(() => {
    if (!polygonModalVisible) return;
    if (!cachedImage) return;
    if (polygonToolMode !== "manualAssist") return;
    if (!binaryPreviewEnabled) return;

    updateAutoPolygonFromCenter();
  }, [
    polygonModalVisible,
    cachedImage,
    polygonToolMode,
    binaryPreviewEnabled,
    binaryThreshold,
    binaryInvert,
    autoContourPointCount,
    longThinContourAssistEnabled,
  ]);
  const clearSelectedPhotoState = () => {
    setSelectedPhoto(null);
    setBoundingBoxes([]);
    setLines([]);
    setMeasureRects([]);
    setOriginalBoundingBoxes([]);
    setOriginalLines([]);
    setOriginalMeasureRects([]);
    setImageLoaded(false);
    setImageError(false);
    resetPolygonEditorState({ clearImage: true, clearSelection: true });
  };

  const selectNeighborAfterDelete = async (deletedPath, options = {}) => {
    const { allowDuringDelete = true } = options;
    const deletedIndex = posts.findIndex((p) => p.photopath === deletedPath);
    const remainingPosts = posts.filter((p) => p.photopath !== deletedPath);

    removePost?.(deletedPath);

    if (remainingPosts.length === 0) {
      clearSelectedPhotoState();
      return;
    }

    // 同じ位置に詰めてきた次の行を優先。末尾削除なら1つ前。
    const nextIndex = deletedIndex < 0
      ? 0
      : Math.min(deletedIndex, remainingPosts.length - 1);
    const nextPhoto = remainingPosts[nextIndex] || remainingPosts[nextIndex - 1] || null;

    if (nextPhoto) {
      await handlePhotoClick(nextPhoto, { allowDuringDelete });
    } else {
      clearSelectedPhotoState();
    }
  };

  const deletePhotoRecord = async (photoToDelete, { moveSelection = true } = {}) => {
    if (!photoToDelete?.photopath) return;
    const targetEmail = user?.email || photoToDelete.userName;
    if (!targetEmail) throw new Error(t("bbox.deletePhoto.noUser"));

    const photoRef = doc(db, targetEmail, photoToDelete.photopath);
    await deleteDoc(photoRef);
    photoRequestRef.current += 1;

    const wasSelected = selectedPhoto?.photopath === photoToDelete.photopath;
    if (moveSelection && wasSelected) {
      await selectNeighborAfterDelete(photoToDelete.photopath, { allowDuringDelete: true });
    } else {
      // Firestore再取得をせず、一覧から即時除去。
      removePost?.(photoToDelete.photopath);
    }
  };

  const handleDeletePhotoFromList = (record) => {
    if (!record?.photopath || deletingPhotoRef.current) return;

    Modal.confirm({
      title: t("bbox.deletePhoto.confirmTitle"),
      content: (
        <div>
          <div>{record.photopath}</div>
          <div style={{ marginTop: 8, color: "#ff4d4f" }}>
            {t("bbox.deletePhoto.confirmMessage")}
          </div>
        </div>
      ),
      okText: t("bbox.deletePhoto.delete"),
      okType: "danger",
      cancelText: t("common.cancel"),
      onOk: async () => {
        if (deletingPhotoRef.current) return;
        deletingPhotoRef.current = true;
        setDeletingPhoto(true);
        try {
          await deletePhotoRecord(record, { moveSelection: true });
          message.success(t("bbox.deletePhoto.deleted"));
        } catch (e) {
          console.error("画像削除エラー:", e);
          message.error(t("bbox.deletePhoto.deleteFailed"));
          throw e;
        } finally {
          deletingPhotoRef.current = false;
          setDeletingPhoto(false);
        }
      },
    });
  };

  const handleDeleteCurrentPhoto = async () => {
    if (!selectedPhoto?.photopath) {
      message.error(t("bbox.deletePhoto.noPhoto"));
      return;
    }

    if (deletingPhotoRef.current) return;
    Modal.confirm({
      title: t("bbox.deletePhoto.confirmTitle"),
      content: (
        <div>
          <div>{selectedPhoto.photopath}</div>
          <div style={{ marginTop: 8, color: "#ff4d4f" }}>
            {t("bbox.deletePhoto.confirmMessage")}
          </div>
        </div>
      ),
      okText: t("bbox.deletePhoto.delete"),
      okType: "danger",
      cancelText: t("common.cancel"),
      onOk: async () => {
        if (deletingPhotoRef.current) return;
        deletingPhotoRef.current = true;
        setDeletingPhoto(true);
        const photoToDelete = selectedPhoto;

        try {
          await deletePhotoRecord(photoToDelete, { moveSelection: true });
          message.success(t("bbox.deletePhoto.deleted"));
        } catch (e) {
          console.error("画像削除エラー:", e);
          message.error(t("bbox.deletePhoto.deleteFailed"));
          throw e;
        } finally {
          deletingPhotoRef.current = false;
          setDeletingPhoto(false);
        }
      },
    });
  };

  return (
    <div
      style={{
        padding: 24,
        background: colorBgContainer,
        height: "98%",
      }}
    >
      {loading ? (
        <div className="spin">
          <StomataLoader />
        </div>
      ) : (
        <>
          {!user ? (
            <Navigate to={"/"} />
          ) : (
            <>
              <Modal
                open={polygonModalVisible}
                width="min(1180px, 96vw)"
                footer={null}
                centered={false}
                style={{ top: 16 }}
                styles={{ body: { padding: 0, overflow: "hidden" } }}
                className="polygon-measure-modal"
                closable={!polygonBoxActionPending}
                maskClosable={!polygonBoxActionPending}
                keyboard={!polygonBoxActionPending}
                onCancel={() => { if (!polygonBoxActionRef.current) closePolygonModal(); }}
                title={
                  <div className="polygon-modal-title">
                    <span>{t("bbox.modal.title")}</span>
                    {polygonImageError && <Tag color="error">{t("bbox.imageErrorShort")}</Tag>}
                    {polygonSavingCount > 0 && <Tag color="processing">{t("bbox.savingWithCount", { count: polygonSavingCount })}</Tag>}
                    <Tag>{t("bbox.imageZoomTag", { percent: Math.round(polygonImageZoom * 100) })}</Tag>
                    {polygonImagePanMode && <Tag color="blue">{t("bbox.imageMove")}</Tag>}
                  </div>
                }
              >
                <div className="polygon-modal-shell" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="polygon-modal-statusbar">
                    <div className="polygon-modal-meta">
                      <span className="polygon-modal-photo">📷 {selectedPhoto?.photopath ?? "no photo"}</span>
                      <Tag>Box ID: {selectedBox?.id ?? selectedBoxKey ?? "-"}</Tag>
                      <Tag>{t("bbox.pointsCount", { count: polygonPoints.length })}</Tag>
                      <Tag>{t("bbox.imageZoomTag", { percent: Math.round(polygonImageZoom * 100) })}</Tag>
                      <Tag color={showPolygonOverlay ? "green" : "default"}>
                        {showPolygonOverlay ? t("bbox.polygonVisible") : t("bbox.polygonHidden")}
                      </Tag>
                      {polygonImageLoading && <Tag color="processing">{t("bbox.loadingNow")}</Tag>}
                      {polygonImageError && <Tag color="error">{t("bbox.loadFailedShort")}</Tag>}
                      {polygonSavingCount > 0 && <Tag color="processing">{t("bbox.backgroundSaving")}</Tag>}
                    </div>
                    <div className="polygon-modal-coord">
                      <strong>{t("bbox.coord")}</strong> X {mouseOnPolygonCanvas.x ?? "-"}, Y {mouseOnPolygonCanvas.y ?? "-"}
                    </div>
                  </div>

                  <div className="polygon-modal-main">
                    <aside className="polygon-modal-sidebar">
                      <div className="polygon-info-card">
                        <div className="polygon-info-title">{t("bbox.editor.beforeTitle")}</div>
                        <div className="polygon-info-grid">
                          <span>🔖 {t("bbox.title")}</span><strong>{selectedBox?.title ?? "N/A"}</strong>
                          <span>🌿 {t("bbox.aperture")}</span><strong>{beforeRegionMeasurement ? (beforeRegionMeasurement.minorAxis * MICRON_PER_PIXEL).toFixed(2) : "N/A"} μm</strong>
                          <span>📐 {t("bbox.area")}</span><strong>{beforeRegionMeasurement ? (beforeRegionMeasurement.area * MICRON_PER_PIXEL ** 2).toFixed(2) : "N/A"} µm²</strong>
                          <span>↔️ {t("bbox.axisLong")}</span><strong>{beforeRegionMeasurement?.majorAxis.toFixed(2) ?? "N/A"} px</strong>
                          <span>↕️ {t("bbox.axisShort")}</span><strong>{beforeRegionMeasurement?.minorAxis.toFixed(2) ?? "N/A"} px</strong>
                          <span>🏷️ {t("bbox.idClass")}</span><strong>{selectedBox?.idClass ?? 0}</strong>
                        </div>
                      </div>

                      <div className="polygon-info-card polygon-info-card--after">
                        <div className="polygon-info-title">{t("bbox.afterSave")}</div>
                        <div className="polygon-info-grid">
                          <span>🔖 {t("bbox.title")}</span><strong>open</strong>
                          <span>🌿 {t("bbox.aperture")}</span><strong>{boxMeasurement?.short ? (boxMeasurement.short * MICRON_PER_PIXEL).toFixed(2) : "0.00"} μm</strong>
                          <span>📐 {t("bbox.area")}</span><strong>{polygonArea ? (polygonArea * MICRON_PER_PIXEL ** 2).toFixed(2) : "0.00"} µm²</strong>
                          <span>↔️ {t("bbox.axisLong")}</span><strong>{boxMeasurement?.long?.toFixed(2) ?? "0.00"} px</strong>
                          <span>↕️ {t("bbox.axisShort")}</span><strong>{boxMeasurement?.short?.toFixed(2) ?? "0.00"} px</strong>
                          <span>🏷️ {t("bbox.idClass")}</span><strong>{(selectedBox?.idClass ?? 0) + 1}</strong>
                        </div>
                      </div>
                    </aside>

                    <section className="polygon-modal-workspace">
                      <div className="polygon-modal-tools">
                        <div className="polygon-tool-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Button disabled={polygonHistoryRef.current.index <= 0 || polygonImageLoading || polygonImageError || draggingPointIndex != null || polygonImagePanning} onClick={() => travelPolygonHistory(-1)}>{t("bbox.editor.undo")}</Button>
                          <Button disabled={polygonHistoryRef.current.index >= polygonHistoryRef.current.entries.length - 1 || polygonImageLoading || polygonImageError || draggingPointIndex != null || polygonImagePanning} onClick={() => travelPolygonHistory(1)}>{t("bbox.editor.redo")}</Button>
                          <Select aria-label={t("bbox.editor.compareLabel")} value={polygonCompareMode} style={{ minWidth: 180 }} onChange={(mode) => { setPolygonCompareMode(mode); setHoveredPointIndex(null); setHoveredSegmentInfo(null); }} options={[
                            { value: "after", label: t("bbox.editor.afterOption") },
                            { value: "before", label: t("bbox.editor.beforeOption") },
                            { value: "overlay", label: t("bbox.editor.overlayOption") },
                          ]} />
                        </div>
                        <div className="polygon-tool-row" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <span>{t("bbox.editor.axes")} <Switch checked={showMeasurementAxes} onChange={setShowMeasurementAxes} /></span>
                          <span>{t("bbox.editor.pointNumbers")} <Switch checked={showPointNumbers} onChange={setShowPointNumbers} /></span>
                          <span>{t("bbox.editor.imageValues")} <Switch checked={showAxisLabels} onChange={setShowAxisLabels} /></span>
                          <span>{t("bbox.editor.panHelp")}</span>
                        </div>
                        {/*
                          Manual Extract / Auto Extract / 二値化UIは一旦非表示。
                          内部state・関数は互換性と将来復活のため残している。
                        */}

                        {/* 表示・編集 */}
                        <div className="polygon-tool-row">
                          <span className="polygon-tool-label">{t("bbox.polygonDisplay")}</span>

                          <Switch
                            checked={showPolygonOverlay}
                            onChange={setShowPolygonOverlay}
                            checkedChildren={<EyeOutlined />}
                            unCheckedChildren={<EyeInvisibleOutlined />}
                          />

                          <span className="polygon-tool-label">
                            {showPolygonOverlay
                              ? t("bbox.pointsAndContourVisible")
                              : t("bbox.pointsAndContourHidden")}
                          </span>

                          <span className="polygon-tool-label" style={{ marginLeft: 10 }}>
                            {t("bbox.measurementBox", { defaultValue: "Measurement box" })}
                          </span>
                          <Switch
                            checked={showMeasurementBox}
                            onChange={setShowMeasurementBox}
                            checkedChildren={<EyeOutlined />}
                            unCheckedChildren={<EyeInvisibleOutlined />}
                          />
                          <span className="polygon-tool-label">
                            {showMeasurementBox
                              ? t("bbox.visible", { defaultValue: "Visible" })
                              : t("bbox.hidden", { defaultValue: "Hidden" })}
                          </span>
                        </div>

                        {/* 点編集 */}
                        <div className="polygon-tool-row">
                          <Button
                            size="small"
                            disabled={
                              polygonImageLoading ||
                              polygonImageError ||
                              !cachedImage ||
                              autoContourMode ||
                              polygonPoints.length < 3 ||
                              polygonToolMode === "autoAssist"
                            }
                            onClick={() => smoothPolygonPoints(1)}
                          >
                            {t("bbox.smoothCorners")}
                          </Button>

                          <Button
                            icon={<DeleteOutlined />}
                            type={pointDeleteMode ? "primary" : "default"}
                            danger={pointDeleteMode}
                            disabled={
                              polygonImageLoading ||
                              polygonImageError ||
                              !cachedImage ||
                              autoContourMode ||
                              polygonPoints.length === 0 ||
                              polygonToolMode === "autoAssist"
                            }
                            onClick={() => {
                              const next = !pointDeleteMode;
                              setPointDeleteMode(next);
                              setPointInsertMode(false);
                              setPolygonImagePanMode(false);
                              setShowPolygonOverlay(true);
                              setHoveredSegmentInfo(null);

                              if (next) {
                                clearAutoContourPreview();
                                setHoverContourStatus(t("bbox.status.pointDeleteMode"));
                              } else {
                                setHoverContourStatus("");
                              }
                            }}
                          >
                            {t("bbox.deletePoint")}
                          </Button>

                          <Button
                            size="small"
                            disabled={
                              polygonImageLoading ||
                              polygonImageError ||
                              !cachedImage ||
                              autoContourMode ||
                              polygonPoints.length === 0
                            }
                            onClick={() => removePolygonPointAtIndex(polygonPoints.length - 1)}
                          >
                            {t("bbox.deleteLastPoint")}
                          </Button>
                        </div>

                        <div className="polygon-tool-status">
                          {polygonImageError
                            ? polygonImageStatus || t("bbox.status.imageLoadFailedReload")
                            : polygonImageLoading || !cachedImage
                              ? polygonImageStatus || t("bbox.status.waitImageLoading")
                              : polygonImagePanning
                                ? t("bbox.status.imageDragging")
                                : polygonImagePanMode
                                  ? hoverContourStatus || t("bbox.status.imagePanMode")
                                  : autoContourMode
                                    ? hoverContourStatus || t("bbox.status.clickYellowToConfirm")
                                    : pointDeleteMode
                                      ? hoveredPointIndex != null
                                        ? t("bbox.status.clickToDeletePoint", { index: hoveredPointIndex + 1 })
                                        : hoverContourStatus || t("bbox.status.pointDeleteHover")
                                      : hoveredPointIndex != null && draggingPointIndex == null
                                        ? t("bbox.editor.hoverPoint", { index: hoveredPointIndex + 1 })
                                        : (pointInsertMode || hoveredSegmentInfo)
                                        ? hoveredSegmentInfo
                                          ? t("bbox.status.clickToInsertEdge", {
                                            start: hoveredSegmentInfo.startIndex + 1,
                                            end: hoveredSegmentInfo.endIndex + 1,
                                          })
                                          : hoverContourStatus || t("bbox.status.edgeInsertHover")
                                        : draggingPointIndex != null
                                          ? t("bbox.status.draggingPoint", { index: draggingPointIndex + 1 })
                                          : hoverContourStatus || t("polygon.descManualPolygon")}
                        </div>
                      </div>

                      <div
                        className="polygon-canvas-frame"
                        ref={polygonCanvasFrameRef}
                      >
                        {(polygonImageLoading || polygonImageError || !cachedImage) && (
                          <div className="polygon-canvas-loading-overlay">
                            {polygonImageError ? (
                              <>
                                <div className="polygon-canvas-loading-title">
                                  {t("bbox.imageLoadFailed")}
                                </div>
                                <div className="polygon-canvas-loading-text">
                                  {polygonImageStatus || t("bbox.closeAndClickAgain")}
                                </div>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const retryBox =
                                      polygonImageRetryBox ||
                                      boundingBoxes.find((b) => b.key === selectedBoxKey);
                                    if (retryBox) void openPolygonEditorForBox(retryBox);
                                  }}
                                >
                                  {t("common.reload")}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Spin size="large" />
                                <div className="polygon-canvas-loading-title">
                                  {t("bbox.loadingNow")}
                                </div>
                                <div className="polygon-canvas-loading-text">
                                  {polygonImageStatus || t("bbox.preparingNewCrop")}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        <div
                          className="polygon-canvas-zoom-layer"
                          style={{
                            position: "absolute",
                            left: `calc(50% - ${polygonCanvasDisplaySize / 2}px + ${polygonImagePan.x}px)`,
                            top: `calc(50% - ${polygonCanvasDisplaySize / 2}px + ${polygonImagePan.y}px)`,
                            width: `${polygonCanvasDisplaySize}px`,
                            height: `${polygonCanvasDisplaySize}px`,
                            transform: `scale(${polygonImageZoom})`,
                            transformOrigin: "top left",
                            zIndex: 1,
                          }}
                        >
                          <canvas
                            ref={polygonCanvasRef}
                            width={POLYGON_CANVAS_SIZE}
                            height={POLYGON_CANVAS_SIZE}
                            className="polygon-canvas"
                            style={{
                              cursor:
                                polygonImageLoading || polygonImageError || !cachedImage
                                  ? "progress"
                                  : polygonImagePanning
                                    ? "grabbing"
                                    : polygonImagePanMode
                                      ? "grab"
                                      : draggingPointIndex != null
                                        ? "grabbing"
                                        : pointDeleteMode
                                          ? "pointer"
                                          : autoContourMode
                                            ? "copy"
                                            : hoveredPointIndex != null
                                              ? "grab"
                                              : (hoveredSegmentInfo || pointInsertMode)
                                                ? "cell"
                                                : "crosshair",
                              opacity: polygonImageLoading || polygonImageError || !cachedImage ? 0.2 : 1,
                              width: `${polygonCanvasDisplaySize}px`,
                              height: `${polygonCanvasDisplaySize}px`,
                            }}
                            onMouseDown={handlePolygonCanvasMouseDown}
                            onMouseMove={handlePolygonCanvasMouseMove}
                            onMouseUp={handlePolygonCanvasMouseUp}
                            onClick={handlePolygonCanvasClick}
                            onDoubleClick={handlePolygonCanvasDoubleClick}
                            onContextMenu={handlePolygonCanvasContextMenu}
                            onMouseLeave={handlePolygonCanvasLeave}
                          />
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="polygon-modal-footer" style={{ flexWrap: "wrap" }}>
                    <div className="polygon-modal-help">
                      {polygonImagePanMode
                        ? t("bbox.status.footerPan")
                        : autoContourMode
                          ? (longThinContourAssistEnabled
                            ? t("bbox.status.footerLongThin")
                            : t("bbox.status.footerAutoContour"))
                          : pointDeleteMode
                            ? t("bbox.status.footerDelete")
                            : pointInsertMode
                              ? t("bbox.status.footerInsert")
                              : t("bbox.editor.autoHelp")}
                    </div>
                    <div className="polygon-modal-actions" style={{ flexWrap: "wrap", maxWidth: "100%" }}>
                      <Button danger icon={<DeleteOutlined />} loading={polygonBoxActionPending === "delete"}
                        disabled={Boolean(polygonBoxActionPending) || polygonSavingCount > 0 || savingBoxKey != null || selectedBoxKey == null}
                        onClick={() => requestPolygonBoxAction("delete")}>
                        {t("bbox.editor.deleteBox")}
                      </Button>
                      <Button loading={polygonBoxActionPending === "close"}
                        disabled={Boolean(polygonBoxActionPending) || polygonSavingCount > 0 || savingBoxKey != null || selectedBoxKey == null}
                        onClick={() => requestPolygonBoxAction("close")}>
                        {t("bbox.editor.closeBox")}
                      </Button>
                      <Button type="dashed" onClick={handleReset} disabled={Boolean(polygonBoxActionPending) || polygonImageLoading || polygonImageError || !cachedImage || beforePolygonPoints.length === 0}>
                        {t("bbox.editor.resetBefore")}
                      </Button>
                      <Button
                        type="primary"
                        disabled={Boolean(polygonBoxActionPending) || polygonImageLoading || polygonImageError || !cachedImage || boxMeasurement.long === 0}
                        onClick={async () => {
                          if (polygonBoxActionRef.current) return;
                          if (polygonImageLoading || polygonImageError || !cachedImage) {
                            message.warning(t("bbox.status.waitImageLoading"));
                            return;
                          }

                          const photoAtSave = selectedPhoto;
                          const selectedBoxKeyAtSave = selectedBoxKey;
                          const regionAtSave = measurePoreRegion(currentPolygonRef.current);
                          if (!regionAtSave) { message.warning(t("bbox.editor.invalidContour")); return; }
                          const polygonAreaAtSave = regionAtSave.area;
                          const boxMeasurementAtSave = { long: regionAtSave.majorAxis, short: regionAtSave.minorAxis };
                          const polygonPointsAtSave = (currentPolygonRef.current ?? []).map((p) => ({ ...p }));
                          const cachedImageAtSave = cachedImage;
                          const previewImageAtSave = previewImage;
                          drawCanvas("after"); // Save the edited contour, regardless of comparison view.
                          const canvasAtSave = polygonCanvasRef.current;

                          // ---------- バリデーション ----------
                          if (!photoAtSave?.userName || !photoAtSave?.photopath) {
                            message.error(t("bbox.errors.noPhoto"));
                            return;
                          }
                          if (!Number.isFinite(selectedBoxKeyAtSave) || polygonAreaAtSave == null) {
                            message.error(t("bbox.errors.invalidTarget"));
                            return;
                          }
                          if (!canvasAtSave) {
                            message.error(t("bbox.errors.noCanvas"));
                            return;
                          }

                          // ---------- updatedBoxes 作成＆State更新 ----------
                          const updatedBoxes = boundingBoxes.map((b) =>
                            b.key === selectedBoxKeyAtSave
                              ? {
                                ...b,
                                title: "open",
                                area: polygonAreaAtSave * MICRON_PER_PIXEL ** 2,
                                measurementMethod: "regionprops-pixel-centers-v1",
                                pixelSize: MICRON_PER_PIXEL,
                                long: boxMeasurementAtSave.long,
                                short: boxMeasurementAtSave.short,
                                aperture: boxMeasurementAtSave.short * MICRON_PER_PIXEL,
                                idClass: (b.idClass ?? 0) + 1,
                                // AI原本 poreContour は保持し、Web修正版を別フィールドへ保存。
                                manualPoreContour: polygonPointsAtSave.map((p) => ({
                                  x: toFiniteNumber(p?.x, 0) / POLYGON_LOGICAL_SIZE,
                                  y: toFiniteNumber(p?.y, 0) / POLYGON_LOGICAL_SIZE,
                                })),
                                manualPoreCrop: getPolygonEditorCrop(b),
                                contourSource: "manual",
                                contourEditedAt: Timestamp.now(),
                              }
                              : b
                          );

                          const updatedBoxForSelectedKey = updatedBoxes.find(
                            (b) => b.key === selectedBoxKeyAtSave
                          );
                          if (!updatedBoxForSelectedKey) {
                            message.error(t("bbox.errors.boxNotFound"));
                            return;
                          }

                          setBoundingBoxes(updatedBoxes);
                          setOriginalBoundingBoxes(updatedBoxes);

                          const convertedSnapshot = updatedBoxes.map(convertToFirestoreFormat);
                          const summarySnapshot = calcPhotoSummaryFromBoxes(updatedBoxes);
                          const optimisticUpdatedAt = Timestamp.now();
                          setSelectedPhoto((prev) => {
                            if (!prev || prev.photopath !== photoAtSave.photopath) return prev;
                            return {
                              ...prev,
                              results: convertedSnapshot,
                              aperture: summarySnapshot.aperture,
                              size: summarySnapshot.size,
                              updatedAt: optimisticUpdatedAt,
                            };
                          });

                          const W = canvasAtSave.width;
                          const H = canvasAtSave.height;
                          const boxIdForName = (updatedBoxForSelectedKey.id ?? selectedBoxKeyAtSave) + "";
                          const safePhoto = photoAtSave.photopath.replace(/\//g, "_");
                          const saveMessageKey = `polygon-save-${safePhoto}-${boxIdForName}-${Date.now()}`;

                          // モーダルの後処理は保存完了時ではなく、保存開始時点で終わらせる。
                          // これにより、保存完了が次のBB編集中に返ってきても、新しい切り出し画像を消さない。
                          closePolygonModal();
                          setPolygonSavingCount((prev) => prev + 1);
                          message.loading({ content: t("bbox.savingBox", { id: boxIdForName }), key: saveMessageKey, duration: 0 });

                          const sanitize = (o) => JSON.parse(JSON.stringify(o));

                          try {
                            // ① まずBB結果を保存する。切り出し画像アップロードより先に行い、連続保存の順序を安定させる。
                            await savePhotoResults(updatedBoxes, {
                              photo: photoAtSave,
                              email: user?.email || photoAtSave.userName,
                              updateLocalState: false,
                            });

                            // ② Labelbox 用の切り出し画像とメタ情報は、モーダルを閉じた後も保存時点の画像スナップショットで処理する。
                            let croppedImageUrl = "";
                            try {
                              const cleanBlob = await makeCleanCanvasBlob({
                                baseImageEl: cachedImageAtSave ?? null,
                                baseImageUrl: previewImageAtSave ?? null,
                                width: W,
                                height: H,
                                mime: "image/jpeg",
                                quality: 0.95,
                              });
                              croppedImageUrl = await uploadBlobToStorage(cleanBlob, `${safePhoto}_${boxIdForName}_.jpg`);
                            } catch (e) {
                              croppedImageUrl = "";
                            }

                            const boxConverted = convertToFirestoreFormat(updatedBoxForSelectedKey);
                            const boxLocation = boxConverted.location ?? {
                              left: updatedBoxForSelectedKey.x,
                              top: updatedBoxForSelectedKey.y,
                              right: (updatedBoxForSelectedKey.x ?? 0) + (updatedBoxForSelectedKey.width ?? 0),
                              bottom: (updatedBoxForSelectedKey.y ?? 0) + (updatedBoxForSelectedKey.height ?? 0),
                            };

                            const originalImageUrl = buildPhotoImageUrl(photoAtSave);
                            const resultId = (updatedBoxForSelectedKey.id ?? selectedBoxKeyAtSave) + "";
                            const labelboxData = [
                              {
                                external_id: `${safePhoto}_${boxIdForName}.jpg` ?? "unknown",
                                image_url: croppedImageUrl ?? "",
                                label: {
                                  objects: [
                                    {
                                      title: updatedBoxForSelectedKey?.title ?? "untitled",
                                      polygon: {
                                        path: polygonPointsAtSave.map((p) => ({
                                          x: (p?.x ?? 0) * POLYGON_SCALE_FACTOR,
                                          y: (p?.y ?? 0) * POLYGON_SCALE_FACTOR,
                                        })),
                                      },
                                    },
                                  ],
                                },
                              },
                            ];

                            const labelboxDocRef = doc(
                              db,
                              "labelbox_data",
                              `${safePhoto}_${boxIdForName}.jpg`
                            );

                            await setDoc(
                              labelboxDocRef,
                              sanitize({
                                createdAt: Timestamp.now(),
                                data: labelboxData,
                                meta: {
                                  original_image_url: originalImageUrl,
                                  cropped_image_url: croppedImageUrl,
                                  userName: photoAtSave.userName,
                                  cultiverName: photoAtSave.cultiverName,
                                  photopath: photoAtSave.photopath,
                                  resultId,
                                  location: {
                                    left: boxLocation.left,
                                    top: boxLocation.top,
                                    right: boxLocation.right,
                                    bottom: boxLocation.bottom,
                                  },
                                },
                              })
                            );

                            await logBoundingBoxAction({
                              email: user.email,
                              photopath: photoAtSave.photopath,
                              cultiverName: photoAtSave.cultiverName,
                              boxId: updatedBoxForSelectedKey?.id ?? selectedBoxKeyAtSave,
                              action: "updated (open)",
                            });

                            message.success({ content: t("bbox.saved"), key: saveMessageKey, duration: 1.5 });
                          } catch (err) {
                            console.error("ポリゴン保存エラー:", err);
                            message.error({ content: t("bbox.saveFailed"), key: saveMessageKey, duration: 2 });
                          } finally {
                            setPolygonSavingCount((prev) => Math.max(0, prev - 1));
                          }
                        }}
                      >
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                </div>
              </Modal>

              <div style={{ display: "flex", flexDirection: "row", height: "86vh" }}>
                {/* 左側: Photo List */}
                {showPhotoList && (
                  <div ref={photoListRef} style={{ flex: 2, overflowY: "auto", borderRight: "1px solid #ddd" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      <h3 style={{ margin: 0 }}>{t("bbox.photoList")}</h3>
                      <Button size="small" icon={<DownloadOutlined />} loading={downloadingEditLogs} disabled={!user?.email || downloadingEditLogs} onClick={handleDownloadEditLogs} title={t("bbox.logs.scope")}>
                        {t("bbox.logs.button")}
                      </Button>
                    </div>
                    <Table
                      dataSource={posts}
                      size="small"
                      rowKey={(record) => record.photopath}
                      rowClassName={(record) =>
                        selectedPhoto?.photopath === record.photopath ? "selected-row" : ""
                      }
                      width="100%"
                      pagination={{ pageSize: 30, showSizeChanger: false, hideOnSinglePage: true, size: "small" }}
                      scroll={{ y: 400 }}
                      columns={[
                        {
                          title: t("bbox.filename"),
                          dataIndex: "photopath",
                          key: "photopath",
                          width: 120,
                          fixed: "left",
                          defaultSortOrder: "descend",
                          sorter: (a, b) => (a.photopath || "").localeCompare(b.photopath || ""),
                          sortDirections: ["ascend", "descend", "ascend"],
                          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                            <div style={{ padding: 8 }}>
                              <Input
                                placeholder={t("bbox.searchByFilename")}
                                value={selectedKeys[0]}
                                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                                onPressEnter={() => confirm()}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Button
                                type="primary"
                                onClick={() => confirm()}
                                size="small"
                                style={{ width: 90, marginRight: 8 }}
                              >
                                {t("common.search")}
                              </Button>
                              <Button
                                onClick={() => clearFilters()}
                                size="small"
                                style={{ width: 90 }}
                              >
                                {t("common.reset")}
                              </Button>
                            </div>
                          ),
                          filterIcon: (filtered) => (
                            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
                          ),
                          onFilter: (value, record) =>
                            record.photopath?.toLowerCase().includes(value.toLowerCase()),
                          render: (text, record) => (
                            <span
                              onClick={() => handlePhotoClick(record)}
                              style={{
                                cursor: "pointer",
                                fontWeight: selectedPhoto?.photopath === record.photopath ? "bold" : "normal",
                                backgroundColor: selectedPhoto?.photopath === record.photopath ? "#ffec3d" : "inherit",
                                display: "inline-block",
                                padding: "2px 4px"
                              }}
                            >
                              {text}
                            </span>
                          )
                        },

                        {
                          title: t("bbox.cultivar"),
                          dataIndex: "cultiverName",
                          key: "cultiverName",
                          width: 90,
                          sorter: (a, b) => (a.cultiverName || "").localeCompare(b.cultiverName || ""),
                          sortDirections: ["ascend", "descend", "ascend"],
                          filters: [...new Set(posts.map(post => post.cultiverName || ""))]
                            .filter(name => name !== "")
                            .map(name => ({ text: name, value: name })),
                          onFilter: (value, record) => record.cultiverName === value,
                          render: (text, record) => (
                            <span
                              onClick={() => handlePhotoClick(record)}
                              style={{
                                cursor: "pointer",
                                fontWeight: selectedPhoto?.photopath === record.photopath ? "bold" : "normal",
                                backgroundColor: selectedPhoto?.photopath === record.photopath ? "#ffec3d" : "inherit",
                                display: "inline-block",
                                padding: "2px 4px"
                              }}
                            >
                              {text}
                            </span>
                          )
                        },
                        {
                          title: t("bbox.comment"),
                          dataIndex: "freeText",
                          key: "freeText",
                          width: 120,
                          defaultSortOrder: "descend",
                        },
                        {
                          title: t("bbox.updatedAt"),
                          dataIndex: "updatedAt",
                          key: "updatedAt",
                          width: 100,
                          sorter: (a, b) => {
                            const aDate = a.updatedAt?.toDate?.() ?? new Date(0); // nullなら1970年扱い
                            const bDate = b.updatedAt?.toDate?.() ?? new Date(0);
                            return aDate - bDate;
                          },
                          sortDirections: ["descend", "ascend"],
                          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                            <div style={{ padding: 8 }}>
                              <Input
                                placeholder={t("bbox.searchByDateExample")}
                                value={selectedKeys[0]}
                                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                                onPressEnter={() => confirm()}
                                style={{ marginBottom: 8, display: "block" }}
                              />
                              <Button
                                type="primary"
                                onClick={() => confirm()}
                                size="small"
                                style={{ width: 90, marginRight: 8 }}
                              >
                                {t("common.search")}
                              </Button>
                              <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                                {t("common.reset")}
                              </Button>
                            </div>
                          ),
                          filterIcon: (filtered) => (
                            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
                          ),
                          onFilter: (value, record) => {
                            if (!record.updatedAt || !record.updatedAt.toDate) return false;
                            const d = record.updatedAt.toDate();
                            const formatted = `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}`;
                            return formatted.includes(value);
                          },
                          render: (val) => {
                            if (!val || !val.toDate) return t("bbox.noUpdate");
                            const d = val.toDate();
                            const pad = (n) => String(n).padStart(2, "0");
                            return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                          }
                        },
                        {
                          title: t("common.delete"),
                          key: "delete",
                          width: 30,
                          render: (_, record) => (
                            <Button
                              danger
                              size="small"
                              disabled={mode !== "edit"}
                              icon={<DeleteOutlined />}
                              type='text'
                              onClick={() => handleDeletePhotoFromList(record)}
                            >
                            </Button>
                          ),
                        }


                      ]}
                    />
                    {hasMore && !loadingMore && (
                      <div style={{ textAlign: "center", padding: "8px 0" }}>
                        <Button size="small" onClick={() => void loadMore()}>
                          {t("bbox.loadMorePhotos", { count: 30 })}
                        </Button>
                      </div>
                    )}
                    {loadingMore && <div style={{ textAlign: "center", padding: 8 }}><Spin size="small" /></div>}
                    {postsError && <Button size="small" onClick={() => { if (hasMore) void loadMore(); else void refetch(); }}>{t("bbox.retryConnection")}</Button>}
                    {/* Edit Logs: 一覧の常時取得なし。保存を継続し、CSVボタンでのみ取得。 */}

                  </div>
                )}

                {/* 中央: 画像とバウンディングボックス */}
                <div
                  style={{
                    flex: 4,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#000",
                    cursor: isDragging ? "grabbing" : "grab",
                    height: "80vh"
                  }}
                  ref={viewportRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseEnter={() => {
                    setShowCrosshair(true);
                    setShowPointerToolBadge(true);
                  }}
                  onMouseLeave={() => {
                    setShowCrosshair(false);
                    setShowPointerToolBadge(false);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {selectedPhoto && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          backgroundColor: "rgba(0,0,0,0.6)",
                          color: "white",
                          padding: "10px 20px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        {/* 🔍 ズームレベル表示 */}
                        <div
                          style={{
                            position: "absolute",
                            top: 100,
                            right: 20,
                            padding: "5px 10px",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            fontSize: "12px",
                            borderRadius: "5px",
                            zIndex: 999,
                            pointerEvents: "none",
                          }}
                        >
                          {t("bbox.zoom", { percent: Math.round(zoomLevel * 100) })}
                        </div>

                        <span
                          style={{
                            maxWidth: "70%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedPhoto.photopath}（{selectedPhoto.cultiverName}）
                        </span>
                      </div>

                      {/* 写真移動ボタンは上部バーではなく、画像ビューの左右中央に固定 */}
                      <Button
                        icon={<LeftOutlined />}
                        size="large"
                        aria-label={t("bbox.previousPhoto")}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseMove={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToNextPhoto();
                        }}
                        disabled={currentIndex >= posts.length - 1}
                        style={{
                          position: "absolute",
                          left: 18,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 35,
                          width: 44,
                          height: 44,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(255,255,255,0.92)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        }}
                      />
                      <Button
                        icon={<RightOutlined />}
                        size="large"
                        aria-label={t("bbox.nextPhoto")}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseMove={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToPreviousPhoto();
                        }}
                        disabled={currentIndex <= 0}
                        style={{
                          position: "absolute",
                          right: 18,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 35,
                          width: 44,
                          height: 44,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(255,255,255,0.92)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        }}
                      />
                    </>
                  )}

                  {/* 設定フォーム */}
                  {mode === "setting" && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100px",
                        right: "20px",
                        zIndex: 10,
                        background: "rgba(255, 255, 255, 0.9)", // 半透明の背景
                        padding: "15px",
                        borderRadius: "8px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        width: "300px",
                      }}
                      onMouseDown={(e) => e.stopPropagation()} // フォーム内でイベント伝播を停止

                    >
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("bbox.displayAdjust") || "Display adjustments"}</div>

                      {/* 明るさ */}
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ marginRight: 6 }}>{t("bbox.brightness") || "Brightness"}: {brightness.toFixed(2)}</span>
                        <Slider
                          min={0.5}
                          max={2.0}
                          step={0.05}
                          value={brightness}
                          onChange={setBrightness}
                        />
                      </div>

                      {/* コントラスト */}
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ marginRight: 6 }}>{t("bbox.contrast") || "Contrast"}: {contrast.toFixed(2)}</span>
                        <Slider
                          min={0.5}
                          max={2.0}
                          step={0.05}
                          value={contrast}
                          onChange={setContrast}
                        />
                      </div>

                      <div style={{ marginBottom: "10px" }}>
                        <Switch
                          checked={drawBoundingBox}
                          onChange={(checked) => setDrawBoundingBox(checked)}
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                        />
                        <span>{t("bbox.boundingBox")} <kbd>B</kbd></span>

                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <Switch
                          checked={drawText}
                          onChange={(checked) => setDrawText(checked)}
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                        />
                        <span>{t("bbox.apertureLabel")}</span>

                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <Switch
                          checked={drawId}
                          onChange={(checked) => setDrawId(checked)}
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                        />
                        <span>ID</span>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <Switch
                          checked={drawScaleBar}
                          onChange={(checked) => setDrawScaleBar(checked)}
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                        />
                        <span>{t("bbox.scaleBar")}</span>

                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <Switch
                          checked={drawPoreMask}
                          onChange={setDrawPoreMask}
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                        />
                        <span style={{ marginLeft: 6 }}>{t("bbox.poreMaskLabel")} <kbd>P</kbd></span>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <span>Open: </span>
                        <Popover
                          content={<SketchPicker color={openColor} onChange={handleOpenColorChange} />}
                          title="Select Open Color"
                          trigger="click"
                        >
                          <Button style={{ backgroundColor: openColor, color: "#fff" }} aria-label={t("bbox.pickColor")}>{t("bbox.pickColor")}</Button>
                        </Popover>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <span>Close: </span>
                        <Popover
                          content={<SketchPicker color={closeColor} onChange={handleCloseColorChange} />}
                          title="Select Close Color"
                          trigger="click"
                        >
                          <Button style={{ backgroundColor: closeColor, color: "#fff" }} aria-label={t("bbox.pickColor")}>{t("bbox.pickColor")}</Button>
                        </Popover>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        {t("bbox.lineWidth")}:{boxShadowWidth}
                        <Slider
                          min={0.2}
                          max={2.0}
                          value={boxShadowWidth}
                          onChange={(value) => setBoxShadowWidth(value)}
                          step={0.1}
                        />

                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <span>Line: </span>
                        <Popover
                          content={<SketchPicker color={lineColor} onChange={(c) => setLineColor(c.hex)} />}
                          title={t("bbox.pickColor")}
                          trigger="click"
                        >
                          <Button style={{ backgroundColor: lineColor, color: "#fff" }} aria-label={t("bbox.pickColor")}>{t("bbox.pickColor")}</Button>
                        </Popover>
                      </div>

                      <div style={{ marginBottom: "10px" }}>
                        {t("bbox.lineWidth")}:{lineWidth}
                        <Slider
                          min={0.2}
                          max={5.0}
                          step={0.1}
                          value={lineWidth}
                          onChange={setLineWidth}
                        />
                      </div>

                    </div>)}
                  {!imageLoaded && !imageError && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0,0,0,0.5)", // 半透明にして雰囲気よく
                        zIndex: 20, // バウンディングボックスより前
                        pointerEvents: "none", // マウス操作を透過
                      }}
                    >
                      <Spin size="large" tip={t("bbox.imageLoading")} />
                    </div>
                  )}
                  {(ctrlPressed || aPressed || (shiftPressed && newLineStart)) && mode === "edit" && (
                    <>
                      {/* 縦線 */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: `${mousePos.x}px`,
                          width: "1px",
                          height: "100%",
                          borderLeft: "1px dotted white",
                          zIndex: 999,
                          pointerEvents: "none",
                        }}
                      />
                      {/* 横線 */}
                      <div
                        style={{
                          position: "absolute",
                          top: `${mousePos.y}px`,
                          left: 0,
                          height: "1px",
                          width: "100%",
                          borderTop: "1px dotted white",
                          zIndex: 999,
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}
                  <div className="bbox-toolbar" style={{ marginTop: 40 }}>
                    {/* left */}
                    <div className="bbox-toolbar__left">
                      <Button
                        icon={showPhotoList ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                        onClick={() => setShowPhotoList(prev => !prev)}
                      >
                        {showPhotoList ? t("bbox.hidePhotoList") : t("bbox.showPhotoList")}
                      </Button>
                    </div>

                    {/* center (group + reset below) */}
                    <div className="bbox-toolbar__center">
                      <div className="bbox-toolbar__modeGroup">
                        <Button
                          icon={<EditOutlined />}
                          type={mode === "edit" ? "primary" : "default"}
                          onClick={() => setMode("edit")}
                        />
                        <Button
                          icon={<SettingOutlined />}
                          type={mode === "setting" ? "primary" : "default"}
                          onClick={() => setMode("setting")}
                        />
                        <Button
                          icon={<EyeOutlined />}
                          type={mode === "view" ? "primary" : "default"}
                          onClick={() => setMode("view")}
                        />
                      </div>

                      <Button
                        className="bbox-toolbar__reset"
                        icon={<ReloadOutlined />}
                        onClick={resetZoomAndPosition}
                      >
                        {t("bbox.resetZoom")}
                      </Button>
                    </div>

                    {/* right */}
                    <div className="bbox-toolbar__right">
                      <Button
                        icon={showBBoxTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowBBoxTable(prev => !prev)}
                      >
                        {showBBoxTable ? t("bbox.hideBBoxTable") : t("bbox.showBBoxTable")}
                      </Button>
                    </div>
                  </div>
                  <div
                    style={{
                      position: "fixed",
                      bottom: 60,
                      marginLeft: 10,
                      zIndex: 10,
                    }}
                  >
                    {selectedPhoto && (
                      <FloatingPhotoComment t={t}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            marginBottom: showPhotoComment ? 8 : 0,
                          }}
                        >
                          <button type="button" className="photo-comment-drag" data-comment-drag title={t("bbox.photoComment.moveHint")} aria-label={t("bbox.photoComment.moveHint")}>⠿ {t("bbox.photoComment.title")}</button>

                          <div style={{ display: "flex", gap: 6 }}>
                            {showPhotoComment && !isEditingPhotoComment && (
                              <Button
                                size="small"
                                disabled={mode !== "edit"}
                                onClick={() => setIsEditingPhotoComment(true)}
                              >
                                {t("bbox.photoComment.edit")}
                              </Button>
                            )}

                            {showPhotoComment && isEditingPhotoComment && (
                              <>
                                <Button
                                  size="small"
                                  type="primary"
                                  loading={photoCommentSaving}
                                  onClick={handleSavePhotoComment}
                                >
                                  {t("bbox.photoComment.save")}
                                </Button>

                                <Button
                                  size="small"
                                  disabled={photoCommentSaving}
                                  onClick={() => {
                                    setPhotoCommentDraft(getSelectedPhotoComment());
                                    setIsEditingPhotoComment(false);
                                  }}
                                >
                                  {t("bbox.photoComment.cancel")}
                                </Button>
                              </>
                            )}

                            <Button
                              size="small"
                              onClick={() => setShowPhotoComment((prev) => !prev)}
                            >
                              {showPhotoComment
                                ? t("bbox.photoComment.hide")
                                : t("bbox.photoComment.show")}
                            </Button>
                          </div>
                        </div>

                        {showPhotoComment && (
                          <>
                            {isEditingPhotoComment ? (
                              <Input.TextArea
                                value={photoCommentDraft}
                                onChange={(e) => setPhotoCommentDraft(e.target.value)}
                                placeholder={t("bbox.photoComment.placeholder")}
                                autoSize={{ minRows: 2, maxRows: 5 }}
                                maxLength={1000}
                                showCount
                              />
                            ) : (
                              <div
                                style={{
                                  whiteSpace: "pre-wrap",
                                  color: getSelectedPhotoComment() ? "#222" : "#888",
                                }}
                              >
                                {getSelectedPhotoComment() || t("bbox.photoComment.empty")}
                              </div>
                            )}
                          </>
                        )}
                      </FloatingPhotoComment>
                    )}
                    <Button
                      disabled={!selectedPhoto}
                      type="primary"
                      style={{ marginLeft: "10px" }}
                      onClick={downloadWithBoundingBox}>
                      {t("bbox.exportImage")}
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      style={{ marginLeft: 10 }}
                      disabled={!selectedPhoto || !imageLoaded || polygonSavingCount > 0 || savingBoxKey != null}
                      loading={imageJExporting}
                      onClick={exportForImageJ}
                      title={t("bbox.imageJ.hint")}
                    >{t("bbox.imageJ.export")}</Button>
                    <Button
                      style={{ marginLeft: "10px" }}
                      onClick={exportCroppedBoxesAsOneImage}>
                      {t("bbox.exportCrops")}
                    </Button>
                    <Button
                      danger
                      style={{ marginLeft: "10px" }}
                      disabled={!selectedPhoto}
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteCurrentPhoto}
                    >
                      {t("bbox.deletePhoto.delete")}
                    </Button>
                  </div>
                  <div>
                    <CanvasHelpPanel
                      t={t}
                      mode={mode}
                      aPressed={aPressed}
                      ctrlPressed={ctrlPressed}
                      shiftPressed={shiftPressed}
                      showCanvasHelp={showCanvasHelp}
                      setShowCanvasHelp={setShowCanvasHelp}
                    />
                  </div>
                  {mode === "view" && hoveredBoxKey != null && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 100,
                        right: 40,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        color: "black",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        zIndex: 999,
                        pointerEvents: "none", // マウス無視
                      }}
                    >
                      {(() => {
                        const box = boundingBoxes.find(b => b.key === hoveredBoxKey);
                        if (!box) return null;
                        return (
                          <>
                            <div><strong>ID:</strong> {box.id ?? box.key}</div>
                            <div><strong>title:</strong> {box.title}</div>
                            <div><strong>aperture:</strong> {(box.aperture ?? 0).toFixed(2)} μm</div>
                            <div><strong>axisShort:</strong> {(box.short ?? 0).toFixed(2)} px</div>
                            <div><strong>axisLong:</strong> {(box.long ?? 0).toFixed(2)} px</div>
                            <div><strong>area:</strong> {(box.area ?? 0).toFixed(2)} µm²</div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {selectedPhoto && (
                    <div
                      className="canvas-container"
                      style={{
                        position: "static",
                        width: `${displayWidth}px`,
                        height: `${displayHeight}px`,
                        transform: `translate(${currentPosition.x}px, ${currentPosition.y}px) scale(${zoomLevel})`,

                        // 👇 カーソル条件分岐
                        cursor:
                          mode === "edit"
                            ? ctrlPressed
                              ? "crosshair"
                              : aPressed
                                ? "crosshair"
                                : shiftPressed
                                  ? "default"
                                  : isDragging
                                    ? "grabbing"
                                    : "grab"
                            : isDragging
                              ? "grabbing"
                              : "grab",
                      }}

                    >
                      <img
                        key={imgReloadKey}
                        src={`https://firebasestorage.googleapis.com/v0/b/stomatascope-fdf90.appspot.com/o/${encodeURIComponent(
                          selectedPhoto.userName
                        )}%2F${encodeURIComponent(selectedPhoto.cultiverName)}%2F${encodeURIComponent(
                          selectedPhoto.photopath
                        )}?alt=media&token=bb06e309-ca5b-4b6b-9bdd-7ea80efa4ced`}
                        alt="Selected"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain", // アスペクト比を保って収める
                          visibility: imageLoaded ? "visible" : "hidden", // チラ見え防止
                          filter: `brightness(${brightness}) contrast(${contrast})`,
                        }}
                        className={sharpness > 0 ? "apply-svg-sharpen" : ""}  // ← クラスで切替
                        onLoad={() => setImageLoaded(true)}
                        onError={() => { setImageError(true); setImageLoaded(true); }}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                      />
                      {/* 画面どこでもOK（重ならないように） */}
                      <svg width="0" height="0" style={{ position: "absolute" }}>
                        <defs>
                          {/* sharpness を 0〜1 で使って、「ふつうの強調」と「強めの強調」を補間 */}
                          {(() => {
                            // 基本シャープ化カーネル
                            // mild:     0 -1  0 / -1  5 -1 / 0 -1  0
                            // strong:  -1 -1 -1 / -1  9 -1 / -1 -1 -1
                            const s = Math.max(0, Math.min(1, sharpness));
                            const mild = [0, -1, 0, -1, 5, -1, 0, -1, 0];
                            const strong = [-1, -1, -1, -1, 9, -1, -1, -1, -1];
                            const kernel = mild.map((m, i) => m * (1 - s) + strong[i] * s);
                            const divisor = kernel.reduce((a, b) => a + b, 0) || 1;

                            return (
                              <filter id="sharp-kernel">
                                <feConvolveMatrix
                                  order="3"
                                  kernelMatrix={kernel.join(" ")}
                                  divisor={divisor}
                                  edgeMode="duplicate"
                                  preserveAlpha="true"
                                />
                              </filter>
                            );
                          })()}
                        </defs>
                      </svg>

                      <svg
                        width="100%"
                        height="100%"
                        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 20 }}
                      >
                        {imageError && (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(0,0,0,0.5)",
                              zIndex: 30,
                            }}
                          >
                            <Result
                              status="404"
                              title={t("bbox.imageNotFound")}
                              subTitle={t("bbox.imageNotFoundDesc")}
                              extra={
                                <Button
                                  onClick={() => {
                                    // 再試行：状態をリセットして <img> を再マウント
                                    setImageError(false);
                                    setImageLoaded(false);
                                    setImgReloadKey((k) => k + 1);
                                  }}
                                >
                                  {t("common.reload")}
                                </Button>
                              }
                            />
                          </div>
                        )}

                        {/* 描画中のライン */}
                        {drawScaleBar && newLineStart && newLineEnd && (
                          <>
                            <line
                              x1={newLineStart.x / scaleX}
                              y1={newLineStart.y / scaleY}
                              x2={newLineEnd.x / scaleX}
                              y2={newLineEnd.y / scaleY}
                              stroke={lineColor}
                              strokeWidth={lineWidth}
                            />
                            <text
                              x={(newLineStart.x) / scaleX}
                              y={(newLineStart.y) / scaleY - 1}
                              fill={lineColor}
                              fontSize="2"
                            >
                              {(() => {
                                const dx = newLineEnd.x - newLineStart.x;
                                const dy = newLineEnd.y - newLineStart.y;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                const micron = dist * MICRON_PER_PIXEL;
                                return `${dist.toFixed(2)} px / ${micron.toFixed(2)} µm`;
                              })()}
                            </text>
                          </>
                        )}
                        {/* 保存済みのライン一覧 */}
                        {drawScaleBar && imageLoaded && lines.map((line) => (
                          <g key={line.id}>
                            <line
                              x1={line.start.x / scaleX}
                              y1={line.start.y / scaleY}
                              x2={line.end.x / scaleX}
                              y2={line.end.y / scaleY}
                              stroke={lineColor}
                              strokeWidth={lineWidth}
                            />
                            <text
                              x={(line.start.x) / scaleX}
                              y={(line.start.y) / scaleY - 1}
                              fill={lineColor}
                              fontSize="2"
                            >
                              {line.distance} px / {(line.distance * MICRON_PER_PIXEL).toFixed(2)} µm
                            </text>
                          </g>
                        ))}

                        {/* 描画中の四角形 */}
                        {drawScaleBar && newRectStart && newRectEnd && (() => {
                          const rect = buildMeasureRectFromPoints(newRectStart, newRectEnd);
                          if (!rect) return null;

                          const displayX = rect.x / scaleX;
                          const displayY = rect.y / scaleY;
                          const displayW = rect.width / scaleX;
                          const displayH = rect.height / scaleY;

                          return (
                            <g>
                              <rect
                                x={displayX}
                                y={displayY}
                                width={displayW}
                                height={displayH}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                                fill="rgba(255,255,255,0.08)"
                                strokeDasharray="3 2"
                              />
                              <text
                                x={displayX + displayW / 2}
                                y={displayY - 1}
                                fill={lineColor}
                                fontSize="2"
                                textAnchor="middle"
                              >
                                {t("bbox.horizontal")} {rect.width.toFixed(2)} px / {(rect.width * MICRON_PER_PIXEL).toFixed(2)} µm
                              </text>
                              <text
                                x={displayX + 1}
                                y={displayY + displayH / 2}
                                fill={lineColor}
                                fontSize="2"
                              >
                                {t("bbox.vertical")} {rect.height.toFixed(2)} px / {(rect.height * MICRON_PER_PIXEL).toFixed(2)} µm
                              </text>
                            </g>
                          );
                        })()}

                        {/* 保存済みの四角形一覧 */}
                        {drawScaleBar && imageLoaded && measureRects.map((rect) => {
                          const displayX = rect.x / scaleX;
                          const displayY = rect.y / scaleY;
                          const displayW = rect.width / scaleX;
                          const displayH = rect.height / scaleY;

                          return (
                            <g key={rect.id}>
                              <rect
                                x={displayX}
                                y={displayY}
                                width={displayW}
                                height={displayH}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                                fill="rgba(255,255,255,0.08)"
                              />
                              <text
                                x={displayX + displayW / 2}
                                y={displayY - 1}
                                fill={lineColor}
                                fontSize="2"
                                textAnchor="middle"
                              >
                                {t("bbox.horizontal")} {rect.width.toFixed(2)} px / {(rect.width * MICRON_PER_PIXEL).toFixed(2)} µm
                              </text>
                              <text
                                x={displayX + 1}
                                y={displayY + displayH / 2}
                                fill={lineColor}
                                fontSize="2"
                              >
                                {t("bbox.vertical")} {rect.height.toFixed(2)} px / {(rect.height * MICRON_PER_PIXEL).toFixed(2)} µm
                              </text>
                            </g>
                          );
                        })}

                        {/* Android AI / Web手動修正の開口領域。Openのみ描画。 */}
                        {drawPoreMask && imageLoaded && !imageError && poreMaskPolygons.map((mask) => (
                          <polygon
                            key={`pore-mask-${mask.key}`}
                            points={mask.points}
                            fill={mask.source === "manual" ? "rgba(255, 140, 0, 0.28)" : "rgba(255, 0, 0, 0.24)"}
                            stroke={mask.source === "manual" ? "#ff8c00" : "#ff0000"}
                            strokeWidth="0.8"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </svg>
                      {imageLoaded && !imageError && mode === "edit" && newBoxStart && newBoxEnd && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                            zIndex: 15,
                          }}
                        >
                          <svg
                            width="100%"
                            height="100%"
                            style={{ position: "absolute", top: 0, left: 0 }}
                          >
                            {/* 👇🔴 矩形表示 */}
                            <rect
                              x={Math.min(newBoxStart.x, newBoxEnd.x) / scaleX}
                              y={Math.min(newBoxStart.y, newBoxEnd.y) / scaleY}
                              width={Math.abs(newBoxEnd.x - newBoxStart.x) / scaleX}
                              height={Math.abs(newBoxEnd.y - newBoxStart.y) / scaleY}
                              stroke={newBoxStart.type === "close" ? closeColor : openColor}
                              fill={newBoxStart.type === "close"
                                ? "rgba(0, 0, 255, 0.2)"
                                : "rgba(255, 0, 0, 0.2)"}
                              strokeWidth={1}
                            />

                          </svg>
                        </div>
                      )}

                      {imageLoaded && !imageError && drawBoundingBox &&
                        boundingBoxes.map((box) => (
                          <div
                            key={box.key}
                            onMouseEnter={() => {
                              if (shiftPressed || ctrlPressed || aPressed || newBoxStart || newLineStart || newRectStart) return;
                              setHoveredBoxKey(box.key);
                              // 高速化: hoverでは画像cropを生成しない。BBクリックでEditorを開く時だけ生成する。
                            }} // ホバー開始
                            onMouseLeave={() => {
                              if (shiftPressed || ctrlPressed || aPressed || newBoxStart || newLineStart || newRectStart) return;
                              setHoveredBoxKey(null)
                            }} // ホバー終了}
                            style={{
                              position: "absolute",
                              top: box.y / scaleY,
                              left: box.x / scaleX,
                              width: (box.width / scaleX),
                              height: (box.height / scaleY),
                              border: "none", // ボーダーを無効化
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              caretColor: "transparent",
                              boxShadow: `0 0 0 ${boxShadowWidth}px ${box.title === "open" ? openColor : closeColor}`,
                            }}

                            onClick={() => {
                              if (shiftPressed || aPressed || newRectStart) return;
                              if (mode === "edit") {
                                void openPolygonEditorForBox(box);
                              }
                            }}

                          >
                            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                              {/* 💡 半透明の色付きオーバーレイ */}
                              {mode !== "edit" && hoveredBoxKey === box.key && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: box.title === "open"
                                      ? "rgba(255, 0, 0, 0.2)"
                                      : "rgba(0, 0, 255, 0.2)",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                  }}
                                />
                              )}


                              {/* 👇 既存の番号とかの表示コンポーネント */}
                              {/* 👇 ID表示 */}
                              {drawId && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: 0,
                                    right: 2,
                                    fontSize: "4px",
                                    lineHeight: 1.2,
                                    userSelect: "none",
                                    WebkitUserSelect: "none",
                                    pointerEvents: "none",
                                    caretColor: "transparent",
                                    color: box.title === "open" ? openColor : closeColor,
                                  }}
                                >
                                  {box.id && box.id !== "" ? box.id : box.key + 1}
                                </div>
                              )}
                            </div>

                            {drawText && box.title === "open" && (
                              <div
                                style={{
                                  position: "absolute",
                                  lineHeight: 1.2,
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  pointerEvents: "none",
                                  caretColor: "transparent",
                                  marginTop: "1px",
                                  color: "black",
                                  whiteSpace: "nowrap",
                                  fontSize: "4px",
                                  color: box.title === "open" ? openColor : closeColor,
                                  top: -5,
                                  left: 0,
                                }}
                              >
                                {(box.aperture ?? 0).toFixed(2)} μm
                              </div>
                            )}
                            {hoveredBoxKey === box.key && mode === "edit" && (
                              <>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    const sorted = [...boundingBoxes].sort((a, b) => a.key - b.key);
                                    const currentIndex = sorted.findIndex(b => b.key === box.key);
                                    const nextBox = sorted[currentIndex - 1] || sorted[0];

                                    setSelectedBoxKey(nextBox.key);
                                    centerOnBox(nextBox);
                                  }}
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    left: -4,
                                    zIndex: 20,
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.72)",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    fontSize: 7,
                                    lineHeight: 1,
                                    color: "#111",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 0,
                                      height: 0,
                                      borderTop: "4px solid transparent",
                                      borderBottom: "4px solid transparent",
                                      borderRight: "6px solid #111",
                                      transform: "translateX(-1px)",
                                    }}
                                  />
                                </div>

                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    const sorted = [...boundingBoxes].sort((a, b) => a.key - b.key);
                                    const currentIndex = sorted.findIndex(b => b.key === box.key);
                                    const prevBox = sorted[currentIndex + 1] || sorted[sorted.length - 1];

                                    setSelectedBoxKey(prevBox.key);
                                    centerOnBox(prevBox);
                                  }}
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    zIndex: 20,
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.72)",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    fontSize: 7,
                                    lineHeight: 1,
                                    color: "#111",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 0,
                                      height: 0,
                                      borderTop: "4px solid transparent",
                                      borderBottom: "4px solid transparent",
                                      borderLeft: "6px solid #111",
                                      transform: "translateX(1px)",
                                    }}
                                  />
                                </div>
                              </>
                            )}

                            {mode === "edit" && hoveredBoxKey === box.key && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  backgroundColor: box.title === "open"
                                    ? "rgba(255, 0, 0, 0.2)"
                                    : "rgba(0, 0, 255, 0.2)",
                                  pointerEvents: "none", // ←マウス操作に影響しない
                                  zIndex: 1,
                                }}
                              />
                            )}

                          </div>

                        ))}
                    </div>

                  )}
                </div>
                <CanvasPointerToolBadge
                  t={t}
                  mode={mode}
                  aPressed={aPressed}
                  ctrlPressed={ctrlPressed}
                  shiftPressed={shiftPressed}
                  mouseScreenPos={mouseScreenPos}
                  visible={showPointerToolBadge && !!selectedPhoto}
                  newBoxStart={newBoxStart}
                  openColor={openColor}
                  closeColor={closeColor}
                />

                {/* 右側: Editable テーブルによる BB 編集 */}
                {showBBoxTable && (
                  <div style={{
                    flex: "1 0 310px",
                    padding: "10px",
                    backgroundColor: "#f8f8f8",
                    borderLeft: "1px solid #ccc",
                    overflowY: "auto", // スクロールできるように
                  }}>
                    <h3>{t("bbox.bboxTableTitle")}</h3>

                    <Table
                      dataSource={boundingBoxes}
                      className="small-font-table"
                      rowKey="key"
                      size="small"
                      bordered
                      pagination={false}
                      scroll={{ y: 500 }}
                      columns={[
                        {
                          title: "ID",
                          dataIndex: "id",
                          width: 30,
                          fixed: "left",
                          render: (text, record) => (
                            <span
                              style={{ cursor: "pointer", color: "#1890ff", textDecoration: "underline" }}
                              onClick={() => centerOnBox(record)}
                            >
                              {text}
                            </span>
                          )
                        },
                        {
                          title: "title",
                          dataIndex: "title",
                          width: 90,
                          render: (_, record) => (
                            <Select
                              className={record.title === "open" ? "open-select" : "close-select"}
                              size="small"
                              value={record.title}
                              disabled={mode !== "edit" || savingBoxKey === record.key} // 🔥 保存中は無効に！
                              onChange={async (val) => {
                                setSavingBoxKey(record.key);

                                const updatedBox = {
                                  ...record,
                                  title: val,
                                  aperture: val === "close" ? 0 : record.aperture,
                                  long: val === "close" ? 0 : record.long,
                                  short: val === "close" ? 0 : record.short,
                                  area: val === "close" ? 0 : record.area,
                                  idClass: record.title !== val ? (record.idClass ?? 0) + 1 : record.idClass,
                                };

                                const updatedBoxes = boundingBoxes.map(box =>
                                  box.key === record.key ? updatedBox : box
                                );
                                setBoundingBoxes(updatedBoxes);

                                try {
                                  await savePhotoResults(updatedBoxes);

                                  // ✅ 成功メッセージ
                                  message.success(t("bbox.saveId", { id: updatedBox.id }), 1.5);

                                  await logBoundingBoxAction({
                                    email: user.email,
                                    photopath: selectedPhoto.photopath,
                                    cultiverName: selectedPhoto.cultiverName,
                                    boxId: updatedBox.id,
                                    action: "changed(" + val + ")",
                                  });
                                                            } catch (error) {
                                  message.error(t("bbox.saveFailed"), 2); // ❌ 失敗通知
                                } finally {
                                  setSavingBoxKey(null);
                                }
                              }}

                              options={[
                                { label: "open", value: "open" },
                                { label: "close", value: "close" },
                              ]}
                              style={{ width: "100%" }}
                            />
                          ),
                        },
                        {
                          title: "Aperture (μm)",
                          dataIndex: "aperture",
                          width: 60,
                          render: (_, record) => (
                            <div style={{ textAlign: "right" }}>
                              {(record.aperture ?? 0).toFixed(2)}
                            </div>
                          ),
                        },
                        {
                          title: "Area (µ㎡)",
                          dataIndex: "area",
                          width: 60,
                          render: (_, record) => (
                            <div style={{ textAlign: "right" }}>
                              {Math.round(record.area ?? 0)}
                            </div>
                          )
                        }
                        ,
                        {
                          title: "Long (px)",
                          dataIndex: "long",
                          width: 60,

                          render: (_, record) => (
                            <div style={{ textAlign: "right" }}>
                              {(record.long ?? 0).toFixed(2)}
                            </div>
                          ),
                        },
                        {
                          title: "Short (px)",
                          dataIndex: "short",
                          width: 60,
                          render: (_, record) => (
                            <div style={{ textAlign: "right" }}>
                              {(record.short ?? 0).toFixed(2)}
                            </div>
                          ),
                        },

                        {
                          title: t("common.delete"),
                          width: 30,
                          render: (_, record) => (
                            <Button
                              danger
                              size="small"
                              disabled={mode !== "edit"}
                              icon={<DeleteOutlined />}
                              type='text'
                              onClick={async () => {
                                const updated = boundingBoxes.filter((box) => box.key !== record.key);
                                setBoundingBoxes(updated);
                                setOriginalBoundingBoxes(updated); // 保存後の状態を「元データ」とする

                                try {
                                  await savePhotoResults(updated);

                                  message.success(t("bbox.deletedAndSavedId", { id: record.id }));
                                  refetch();
                                  // 🔥 ここ！
                                  await logBoundingBoxAction({
                                    email: user.email,
                                    photopath: selectedPhoto.photopath,
                                    cultiverName: selectedPhoto.cultiverName,
                                    boxId: record.id,
                                    action: "deleted",
                                  });
                                                            } catch (err) {
                                  message.error(t("bbox.deleteAfterSaveFailed"));
                                }
                              }}
                            >
                            </Button>

                          ),
                        },
                      ]}
                    />
                    {lines.length > 0 && (
                      <>
                        <h3 style={{ marginTop: 20 }}>{t("bbox.linesTitle")}</h3>

                        <Table
                          dataSource={lines}
                          rowKey="id"
                          size="small"
                          bordered
                          pagination={false}
                          scroll={{ y: 120 }}
                          columns={[
                            {
                              title: "ID",
                              key: "index",
                              width: 80,
                              render: (_, __, index) => index, // ← 0からの連番を表示！
                            },

                            {
                              title: "Start (x,y)",
                              render: (line, _, index) => (
                                <span>
                                  ({Math.round(line.start.x)}, {Math.round(line.start.y)})
                                </span>
                              ),
                            },
                            {
                              title: "End (x,y)",
                              render: (line) => (
                                <span>
                                  ({Math.round(line.end.x)}, {Math.round(line.end.y)})
                                </span>
                              ),
                            },
                            {
                              title: t("bbox.distance"),
                              render: (line) => {
                                return `${Number(line.distance).toFixed(1)} px / ${(line.distance * MICRON_PER_PIXEL).toFixed(2)} µm`; // ← ここ！
                              },
                            }
                            ,
                            {
                              title: t("common.delete"),
                              render: (_, record) => (
                                <Button
                                  danger
                                  size="small"
                                  disabled={mode !== "edit"}
                                  icon={<DeleteOutlined />}
                                  type='text'
                                  onClick={async () => {
                                    // 1. 削除後のlines配列を作成
                                    const updated = lines.filter((line) => line.id !== record.id);

                                    // 2. React側の状態を更新
                                    setLines(updated);
                                    setUndoStack((prev) => [...prev, { boxes: boundingBoxes, lines, measureRects }]);

                                    try {
                                      // 3. Firestoreにアップデート
                                      await savePhotoResults(boundingBoxes, { lines: updated, measureRects });

                                      // 6. 成功メッセージ表示
                                      message.success(t("bbox.deletedAndSavedId", { id: record.id }));

                                      // 7. Firestore読み直し
                                      refetch();

                                      // 8. ログ記録
                                      await logBoundingBoxAction({
                                        email: user.email,
                                        photopath: selectedPhoto.photopath,
                                        cultiverName: selectedPhoto.cultiverName,
                                        boxId: record.id,
                                        action: "deleted",
                                      });

                                                                    } catch (err) {
                                      message.error(t("bbox.deleteAfterSaveFailed"));
                                    }
                                  }}
                                />
                              ),
                              width: 40,
                            },
                          ]}

                        />
                      </>
                    )}
                    {measureRects.length > 0 && (
                      <>
                        <h3 style={{ marginTop: 20 }}>{t("bbox.rectanglesTitle")}</h3>

                        <Table
                          dataSource={measureRects}
                          rowKey="id"
                          size="small"
                          bordered
                          pagination={false}
                          scroll={{ y: 120 }}
                          columns={[
                            {
                              title: "ID",
                              key: "index",
                              width: 60,
                              render: (_, __, index) => index,
                            },
                            {
                              title: t("bbox.position"),
                              render: (rect) => (
                                <span>
                                  ({Math.round(rect.x)}, {Math.round(rect.y)})
                                </span>
                              ),
                            },
                            {
                              title: t("bbox.horizontal"),
                              render: (rect) =>
                                `${rect.width.toFixed(1)} px / ${(rect.width * MICRON_PER_PIXEL).toFixed(2)} µm`,
                            },
                            {
                              title: t("bbox.vertical"),
                              render: (rect) =>
                                `${rect.height.toFixed(1)} px / ${(rect.height * MICRON_PER_PIXEL).toFixed(2)} µm`,
                            },
                            {
                              title: t("common.delete"),
                              width: 40,
                              render: (_, record) => (
                                <Button
                                  danger
                                  size="small"
                                  disabled={mode !== "edit"}
                                  icon={<DeleteOutlined />}
                                  type="text"
                                  onClick={async () => {
                                    const updated = measureRects.filter((rect) => rect.id !== record.id);

                                    setMeasureRects(updated);
                                    setUndoStack((prev) => [
                                      ...prev,
                                      { boxes: boundingBoxes, lines, measureRects },
                                    ]);

                                    try {
                                      await savePhotoResults(boundingBoxes, {
                                        lines,
                                        measureRects: updated,
                                      });

                                      message.success(t("bbox.rectangleDeleted"));
                                      refetch();
                                    } catch (err) {
                                      message.error(t("bbox.rectangleDeleteFailed"));
                                    }
                                  }}
                                />
                              ),
                            },
                          ]}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default BoundingBox;
